// @ts-nocheck
import { CH2_FRAMES } from "./data/ch2Frames";
import { CH1_FRAMES } from "./data/ch1Frames";
import { CH3_FRAMES } from "./data/ch3Frames";
import { CH4_FRAMES } from "./data/ch4Frames";
import { CH4_SLIDES, CH4_MAP_SLIDE } from "./data/ch4Slides";
import LiveSlides from "./components/LiveSlides";
import { CH5_FRAMES } from "./data/ch5Frames";
import { CH6_FRAMES } from "./data/ch6Frames";
import { CH7_FRAMES } from "./data/ch7Frames";
import { CH8_FRAMES } from "./data/ch8Frames";
import { CH9_FRAMES } from "./data/ch9Frames";
import { CH10_FRAMES } from "./data/ch10Frames";
import { CH11_FRAMES } from "./data/ch11Frames";
import { CH12_FRAMES } from "./data/ch12Frames";
import type { Frame } from "./data/frames";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { FIELDS_DATA } from "./content/he/intro-narration";
import { AuthBadge } from "./components/AuthBadge";
import { PaywallGate } from "./components/PaywallGate";
import { LeadCaptureCard } from "./components/LeadCaptureCard";
import { DecisionPage } from "./components/DecisionPage";
import { SalesPage } from "./components/SalesPage";
import { PickPage } from "./components/PickPage";
import { AboutPage } from "./components/AboutPage";
import { ReviewerInvitePage } from "./components/ReviewerInvitePage";
import { ChatWorkshopPage } from "./components/ChatWorkshopPage";
import { DailyLionPage } from "./components/DailyLionPage";
import { AdminPage } from "./components/admin/AdminPage";
import { PaymentSuccess } from "./components/PaymentSuccess";
import { AudioPlayer } from "./components/AudioPlayer";
import { DrivingNotice, isDrivingNoticeDismissed } from "./components/DrivingNotice";
import { NotFoundPage } from "./components/NotFoundPage";
import { ContactPage } from "./components/ContactPage";
import { WelcomePage, WELCOME_DISMISSED_KEY } from "./components/WelcomePage";
import { Watermark } from "./components/Watermark";
import { ChapterContent } from "./components/ChapterContent";
import { TasteVideo } from "./components/TasteVideo";
import { useAuth } from "./lib/useAuth";
import { useAdmin } from "./lib/useAdmin";
import { useAccess } from "./lib/useAccess";
import { usePicks } from "./lib/usePicks";
import { trackChapterView } from "./lib/fbTrack";
import { captureAttribution, flushAttribution } from "./lib/attribution";
import { useCloudProgress } from "./lib/useCloudProgress";
import { useBookmarks } from "./lib/useBookmarks";
import { supabase } from "./lib/supabase";
import { logSiteEvent } from "./lib/siteEvents";

const REVIEWER_TOKEN_PATH = /^\/r\/([\w-]+)\/?$/;
const ADMIN_PATH = /^\/admin\/?$/;
const PAYMENT_SUCCESS_PATH = /^\/payment-success\/?$/;
const CONTACT_PATH = /^\/contact\/?$/;
const WELCOME_PATH = /^\/welcome\/?$/;
// Variant B of the landing experiment (29.8): quotes -> promise -> offer.
const DECISION_PATH = /^\/b\/?$/;
// Sales page v2 (21.10): "עמוד מתוך הספר" - hidden until Shlomo approves.
const SALES2_PATH = /^\/b2\/?$/;
// Gift landing (10.9): the offer card opened straight in gift mode — the
// link for the holiday post and the alumni email.
const GIFT_PATH = /^\/gift\/?$/;
// Chat workshop (22.9): personal token link, no login.
const CHAT_PATH = /^\/c\/([a-f0-9]{24})\/?$/;
// Daily lion question game (29.9): free, no login.
const DAILY_PATH = /^\/day\/?$/;
// Chapter pick (8.10): the reader chooses chapters from the ToC; two open
// for free (chapter_grants), the rest behind the offer.
const PICK_PATH = /^\/pick\/?$/;
// About the author (10.10): Shlomo's "מי אני".
const ABOUT_PATH = /^\/about\/?$/;
const HOME_PATH = /^\/?$/;
const PENDING_TOKEN_KEY = "pending_invitation_token";

// Recognised SPA routes. Anything else renders NotFoundPage instead of
// silently falling through to the home page (which made unknown URLs
// indistinguishable from the landing screen).
function isKnownSpaPath(pathname: string): boolean {
  return (
    HOME_PATH.test(pathname) ||
    REVIEWER_TOKEN_PATH.test(pathname) ||
    ADMIN_PATH.test(pathname) ||
    PAYMENT_SUCCESS_PATH.test(pathname) ||
    CONTACT_PATH.test(pathname) ||
    WELCOME_PATH.test(pathname) ||
    DECISION_PATH.test(pathname) ||
    SALES2_PATH.test(pathname) ||
    GIFT_PATH.test(pathname) ||
    CHAT_PATH.test(pathname) ||
    DAILY_PATH.test(pathname) ||
    PICK_PATH.test(pathname) ||
    ABOUT_PATH.test(pathname)
  );
}

const IMG1 = "/breicha.png";
const IMG2_RAINBOW = "/ch2/old-rainbow-brain.png";
const IMG2_CASTLE = "/ch2/old-castle.png";
const IMG3_SCALE = "/ch3/3.3.webp"; // the old external scale image was never shipped; dead constant
const IMG2_EYE = "/ch2/old-cosmic-eye.png";
const IMG4_EVENT = "/event.png";
const IMG4_INTERPRET = "/interpretation.png";
const IMG4_UNCONSC = "/unconsc_intrp.png";
const IMG4_STOP = "/stop.png";
const IMG4_CONSC = "/consc_interp.png";
const IMG4_POSITIVE = "/positive.png";
const IMG4_NEWHABIT = "/newhabit.png";
const IMG4_DIAGRAM = "/allStages.png";
const IMG5_RAMZOR = "/ch5/traffic-light.svg";

const AUDIO1 = "/hakdama.mp3";

const styles = `
@import url('https://fonts.googleapis.com/css2?family=Frank+Ruhl+Libre:wght@400;500;700;900&family=Heebo:wght@400;500;600;700&display=swap');
*{box-sizing:border-box;margin:0;padding:0}
.slim-header-locate{position:absolute;top:8px;right:56px;z-index:56;background:var(--bg2);color:var(--deep);border:1px solid var(--sand);border-radius:50%;width:30px;height:30px;font-size:15px;line-height:1;cursor:pointer;display:flex;align-items:center;justify-content:center;font-family:'Heebo',sans-serif;transition:background .15s, transform .15s}
.slim-header-locate:hover{background:var(--sand);transform:translateY(-1px)}
.locator-pop{position:fixed;top:52px;right:12px;z-index:58;background:#fff;border:1px solid #E5D9C8;border-radius:10px;box-shadow:0 6px 18px rgba(0,0,0,.18);padding:12px 14px;max-width:288px;font-family:'Heebo',sans-serif;direction:rtl}
.locator-ref{font-weight:700;color:var(--deep,#5C3D2E);font-size:15px;margin-bottom:10px;text-align:right;line-height:1.4}
.locator-btn{display:block;width:100%;box-sizing:border-box;border:1px solid #D9C7B0;background:#FAF6F0;color:var(--ink,#3a3a3a);border-radius:7px;padding:7px 10px;font-size:13px;cursor:pointer;font-family:'Heebo',sans-serif;text-align:center}
.locator-btn.primary{background:var(--deep,#5C3D2E);color:#fff;border-color:var(--deep,#5C3D2E)}
.locator-btn:hover{filter:brightness(1.05)}
.locator-sep{margin:12px 0 6px;padding-top:10px;border-top:1px solid #EEE3D3;font-size:12px;font-weight:600;color:var(--muted,#8B7355);text-align:right}
.locator-goto{display:flex;gap:6px;align-items:stretch}
.locator-goto .locator-input{flex:1;min-width:0;border:1px solid #D9C7B0;border-radius:7px;padding:6px 8px;font-size:13px;font-family:'Heebo',sans-serif}
.locator-goto .locator-btn{width:auto;flex:0 0 auto;padding:7px 14px}
.locator-close{margin-top:10px}
.locator-saved{margin-top:8px;padding:8px 10px;background:#EAF5E8;border:1px solid #CBE5C2;border-radius:7px;font-size:12.5px;color:#3B6B2E;text-align:center}
.locator-hint{margin-top:8px;font-size:12px;color:var(--muted,#8B7355);line-height:1.5;text-align:right}
.locator-savebox{display:flex;gap:6px;align-items:stretch;margin-top:8px}
.locator-savebox .locator-input{flex:1;min-width:0;text-align:right;direction:rtl}
.locator-savebox .locator-btn{width:auto;flex:0 0 auto;padding:7px 14px}
.bm-list{margin:6px 0 22px;background:var(--white,#FFFDF9);border:1px solid var(--sand,#E8DCC8);border-radius:14px;padding:14px 16px}
.bm-list-h{font-family:'Frank Ruhl Libre',serif;font-weight:700;font-size:19px;color:var(--deep,#5C3D2E);margin-bottom:10px;text-align:right}
.bm-empty{font-size:13.5px;color:var(--muted,#8B7355);line-height:1.6;text-align:right}
.bm-row{display:flex;align-items:center;gap:8px;padding:9px 4px;border-top:1px solid #F0E7D8}
.bm-row:first-of-type{border-top:none}
.bm-main{flex:1;min-width:0;cursor:pointer;text-align:right}
.bm-title{font-size:14.5px;font-weight:600;color:var(--text,#2D1B10);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.bm-meta{font-size:12px;color:var(--muted,#8B7355);margin-top:1px}
.bm-actions{display:flex;gap:3px;flex:0 0 auto}
.bm-act{border:1px solid #E2D4BE;background:#FAF6F0;border-radius:7px;width:32px;height:32px;font-size:14px;cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0}
.bm-act:hover{filter:brightness(1.04)}
.bm-del:hover{background:#FBEAE7;border-color:#E8B8AE}
@keyframes paraFlash{0%{background:rgba(255,193,7,.38)}100%{background:transparent}}
.para-flash{animation:paraFlash 2.2s ease-out;border-radius:4px}
:root{--bg:#FAF7F2;--bg2:#F3EDE3;--sand:#E8DCC8;--warm:#C4A882;--terra:#A0704A;--deep:#5C3D2E;--text:#2D1B10;--muted:#8B7355;--accent:#D4875A;--white:#FFFDF9;--radius:16px;--shadow:0 4px 24px rgba(92,61,46,0.10);--shadow-lg:0 12px 48px rgba(92,61,46,0.18)}
body{background:var(--bg);direction:rtl;padding-top:54px}
.slim-header{position:fixed;top:0;left:0;right:0;background:var(--white);border-bottom:1px solid var(--sand);z-index:55;box-shadow:0 1px 6px rgba(92,61,46,0.08)}
.slim-header-auth{position:absolute;top:8px;left:12px;z-index:56}
/* Help button moved to the right side, next to the hamburger. Earlier
   position at left:62px collided with the AuthBadge's horizontal layout
   (email + admin/feedback/sign-out buttons) which extends beyond left:62.
   Right side has only the hamburger, so placing help there avoids overlap. */
.slim-header-help{position:absolute;top:8px;right:96px;z-index:56;background:var(--bg2);color:var(--deep);border:1px solid var(--sand);border-radius:50%;width:30px;height:30px;font-size:14px;font-weight:700;cursor:pointer;display:flex;align-items:center;justify-content:center;font-family:'Heebo',sans-serif;text-decoration:none;transition:background .15s, transform .15s}
.slim-header-help:hover{background:var(--sand);transform:translateY(-1px)}
.slim-header-help:focus-visible{outline:2px solid var(--accent);outline-offset:2px}
/* WhatsApp in the header — a reachable human is the cheapest scam-disqualifier
   for the 55+ audience (trust research, 11.8). Same footprint as the help dot. */
.slim-header-wa{position:absolute;top:8px;right:136px;z-index:56;background:#fff;color:#1B8755;border:1px solid rgba(27,135,85,0.5);border-radius:50%;width:30px;height:30px;font-size:15px;line-height:1;cursor:pointer;display:flex;align-items:center;justify-content:center;text-decoration:none;transition:background .15s, transform .15s}
.slim-header-wa:hover{background:rgba(27,135,85,0.08);transform:translateY(-1px)}
/* Chapter title in fixed header. Long titles wrap up to 2 lines; 3+ lines clip.
   Replaces previous ellipsis-only truncation which lost context on mobile —
   "פרק 7 — הורמונים, ארבעת הערוצים, ערכים..." was unreadable.
   Right padding bumped to 100px to fit help (right:58, width:30) +
   hamburger (right:12, width:38) on the right edge without overlap. */
/* min-height guarantees the white strip encloses the absolutely-positioned
   buttons (hamburger is 38px tall at top:8) instead of letting them hang
   over the hero card below — Shlomo's "the icons float outside the bar"
   note, 1.8. Top padding centers a one-line title within the strip. */
.slim-header-row{font-family:'Heebo',sans-serif;font-size:13px;color:var(--deep);font-weight:500;text-align:center;line-height:1.35;padding:13px 134px 6px 64px;min-height:44px;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;word-break:break-word}
.slim-progress{height:3px;background:var(--bg2);position:relative;overflow:hidden}
.slim-progress-bar{height:100%;background:#A0704A;transition:width 80ms linear;will-change:width}
/* .offer-strip — the always-visible gentle offer ribbon (funnel leak #1 fix,
   Shlomo approved 4.8). Fixed to the BOTTOM on purpose: the top slim-header
   is load-bearing for every sticky offset (.mb top:58, .ap2-sticky top:118),
   and growing it would re-run the #228 regression saga. Bottom placement
   touches nothing and sits in the mobile thumb zone. Shown only on the free
   chapter for visitors without access; ✕ hides it for the session. */
.offer-strip{position:fixed;bottom:12px;left:50%;transform:translateX(-50%);z-index:54;display:flex;align-items:center;gap:8px;background:linear-gradient(135deg,var(--deep),#3a2419);color:#fff;border:none;border-radius:50px;padding:10px 18px;font-family:'Heebo',sans-serif;font-size:14px;font-weight:500;box-shadow:0 8px 24px rgba(92,61,46,0.38);cursor:pointer;direction:rtl;white-space:nowrap;max-width:calc(100vw - 24px)}
.offer-strip:hover{transform:translateX(-50%) translateY(-2px);box-shadow:0 12px 28px rgba(92,61,46,0.45)}
.offer-strip b{font-weight:700}
.offer-strip-x{position:relative;margin-right:6px;background:rgba(255,255,255,0.15);border:none;border-radius:50%;width:22px;height:22px;color:#fff;font-size:12px;line-height:1;cursor:pointer;display:flex;align-items:center;justify-content:center;flex-shrink:0}
.offer-strip-x:hover{background:rgba(255,255,255,0.3)}
@media (max-width:600px){.offer-strip{font-size:13px;padding:9px 14px}}
/* .ch1-cta — end-of-free-chapter invitation to continue to the paid
   workshop (unpaid readers only; Shlomo, 6.8). */
.ch1-cta{background:linear-gradient(135deg,var(--deep),#3a2419);color:#fff;border-radius:20px;padding:28px 24px;margin:36px 0 10px;text-align:center;font-family:'Heebo',sans-serif;box-shadow:0 10px 32px rgba(92,61,46,0.28)}
.ch1-cta-title{font-family:'Frank Ruhl Libre',serif;font-size:22px;font-weight:700;margin-bottom:8px;line-height:1.4}
.ch1-cta-body{font-size:15px;line-height:1.7;opacity:.92;max-width:440px;margin:0 auto 18px}
.ch1-cta-btn{background:linear-gradient(135deg,#A0704A,#D4875A);color:#fff;border:none;padding:14px 30px;border-radius:16px;font-family:'Heebo',sans-serif;font-size:16px;font-weight:700;cursor:pointer;box-shadow:0 6px 20px rgba(0,0,0,0.25);transition:transform .15s}
.ch1-cta-btn:hover{transform:translateY(-2px)}
.ch1-cta-fine{font-size:12.5px;opacity:.8;margin-top:12px;line-height:1.6}
/* .decide — the early decision moment in chapter 1 (23.8) */
.decide{background:linear-gradient(135deg,#FFF8F0,#FFFDF9);border:1px solid #E8DCC8;border-radius:16px;padding:18px 20px;margin:22px 0 10px;direction:rtl;font-family:'Heebo',sans-serif;box-shadow:0 6px 20px rgba(92,61,46,0.08)}
.decide-t{font-family:'Frank Ruhl Libre',serif;font-size:20px;font-weight:700;color:var(--deep);margin-bottom:4px}
.decide-b{font-size:14px;line-height:1.7;color:var(--deep);margin-bottom:12px}
.decide-row{display:flex;align-items:center;gap:14px;flex-wrap:wrap}
.decide-btn{background:linear-gradient(135deg,#A0704A,#D4875A);color:#fff;border:none;border-radius:12px;padding:11px 22px;font-family:'Heebo',sans-serif;font-size:15px;font-weight:700;cursor:pointer;box-shadow:0 4px 14px rgba(160,112,74,0.3)}
.decide-alt{font-size:13px;color:#8B7355}
.ch1-cta-quote{font-size:14px;line-height:1.7;font-style:italic;background:rgba(255,255,255,0.09);border-radius:12px;padding:12px 18px;max-width:440px;margin:0 auto 18px}
.ch1-cta-quote span{font-style:normal;font-size:12.5px;opacity:.8;display:block;margin-top:4px}
.bc{max-width:860px;margin:0 auto;padding:0 16px 80px;font-family:'Heebo',sans-serif;color:var(--text);direction:rtl}
.bh{background:linear-gradient(135deg,var(--deep) 0%,var(--terra) 60%,var(--accent) 100%);border-radius:0 0 40px 40px;padding:40px 32px 36px;color:white;margin-bottom:32px;position:relative;overflow:hidden}
.bh-slim{padding:14px 24px 14px;margin-bottom:10px;border-radius:0 0 24px 24px}
.bh-slim .bt{margin-bottom:2px;font-size:12px}
.bh-slim .ct{font-size:26px;margin-bottom:2px}
.bh-slim .cb{margin-top:6px;padding:3px 12px}
.bh::before{content:'';position:absolute;top:-60px;left:-60px;width:220px;height:220px;border-radius:50%;background:rgba(255,255,255,0.06)}
.bh::after{content:'';position:absolute;bottom:-40px;right:-40px;width:160px;height:160px;border-radius:50%;background:rgba(255,255,255,0.05)}
.bt{font-family:'Frank Ruhl Libre',serif;font-size:13px;opacity:.75;margin-bottom:8px;letter-spacing:2px}
.ct{font-family:'Frank Ruhl Libre',serif;font-size:32px;font-weight:700;line-height:1.25;margin-bottom:10px}
.ct-sub{font-family:'Frank Ruhl Libre',serif;font-size:19px;font-weight:500;opacity:.92;margin-bottom:12px}
.cs{font-size:15px;opacity:.85;line-height:1.6;font-weight:400;max-width:540px}
/* "The page is the ad" hero additions (20.8) */
.bh-promise{font-size:15.5px;line-height:1.9;font-weight:400;max-width:560px;margin:14px 0 4px}
.bh-promise em{font-style:normal;font-weight:700}
.bh-open{display:inline-block;background:rgba(255,255,255,0.16);border:1px solid rgba(255,255,255,0.28);border-radius:14px;padding:9px 18px;font-size:16px;font-weight:700;margin:12px 0 10px}
.bh-sub{font-size:15px;opacity:.75;margin-bottom:4px}
/* Holiday gift ribbon above the hero (10.9) - one thin line, not a banner */
.gift-ribbon{display:block;text-decoration:none;background:linear-gradient(90deg,#1E2A44,#34507A);color:#fff;border-bottom:3px solid #F0C27B;text-align:center;padding:16px 14px 14px;line-height:1.3;margin:-40px -32px 24px}
.gift-ribbon b{display:block;color:#F0C27B;font-family:'Frank Ruhl Libre',serif;font-size:44px;font-weight:700;line-height:1.15}
.gift-ribbon span{display:block;font-size:19px;font-weight:600;margin-top:6px}
.gift-ribbon:hover{filter:brightness(1.06)}
@media(max-width:640px){.gift-ribbon{padding:12px 10px 12px;margin:-28px -20px 20px}.gift-ribbon b{font-size:34px}.gift-ribbon span{font-size:15px}}
.taste-video{margin:0 0 26px;background:#2A1218;border:1px solid #F0C27B;border-radius:14px;padding:14px 14px 12px;text-align:center}
.taste-video-title{color:#F0C27B;font-family:'Frank Ruhl Libre',serif;font-size:26px;font-weight:700;line-height:1.2}
.taste-video-sub{color:#F5EFE6;font-size:15px;margin:4px 0 10px}
.taste-video video{width:100%;max-width:820px;aspect-ratio:16/9;border-radius:10px;background:#000;display:block;margin:0 auto}
@media(max-width:640px){.taste-video{padding:10px 8px 8px;margin:0 0 20px}.taste-video-title{font-size:21px}.taste-video-sub{font-size:13px}}
/* Testimonials strip below the hero */
.testi-strip{display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin:-14px 0 26px}
.testi{background:#FFFDF9;border:1px solid #E8DCC8;border-radius:14px;padding:14px 16px;box-shadow:0 3px 12px rgba(92,61,46,0.06)}
.testi-q{font-size:14px;line-height:1.65;color:var(--deep);font-weight:500}
.testi-n{font-size:12.5px;color:#8B6F5C;margin-top:8px}
@media(max-width:640px){.testi-strip{grid-template-columns:1fr;gap:10px}}
.cb{display:inline-flex;align-items:center;gap:6px;background:rgba(255,255,255,0.15);border:1px solid rgba(255,255,255,0.2);border-radius:20px;padding:5px 14px;font-size:12px;margin-top:16px}
/* .mb — the reading/listening mode bar. Sticky (author decision, 2026-07-13):
   the reader must be able to switch mode from anywhere in the chapter, so the
   bar stays pinned just below the slim-header for the whole scroll. It lives
   inside .bc like .ap2-sticky, so it never un-sticks mid-chapter. z-index
   sits between the slim-header (55) and the listen player (50), and the
   player's top offset clears this bar — see .ap2-sticky below. */
.mb{display:flex;align-items:center;gap:12px;background:var(--white);border:1px solid var(--sand);border-radius:50px;padding:6px 14px;margin-bottom:6px;box-shadow:var(--shadow);position:sticky;top:58px;z-index:52}
/* Page-colored backdrop behind the pinned pill. The pill sticks at top:58px
   but the slim-header above it is only ~30-46px tall (title-dependent), so
   chapter text scrolled through the gap and peeked around the capsule's
   rounded corners (Noam's screenshots, 1.8). The backdrop matches --bg so it
   is invisible against the page yet hides the scrolling text. -16px sides
   match the .bc gutter; -30px top always tucks under the slim-header. */
.mb::before{content:"";position:absolute;left:-16px;right:-16px;top:-8px;bottom:-6px;background:var(--bg);z-index:-1;pointer-events:none}
/* 12.10: the backdrop used to reach 30px up and painted over the bottom of the slim chapter banner (title cut). 8px is enough to cover the seam under the sticky header. */
/* CSS paint-order gotcha: because .mb is its own stacking context, the
   negative-z ::before paints ABOVE the element's own background — hiding
   the white capsule. ::after (later in tree order) repaints the capsule
   surface on top of the backdrop, below the content. */
.mb::after{content:"";position:absolute;inset:-1px;background:var(--white);border:1px solid var(--sand);border-radius:50px;box-shadow:var(--shadow);z-index:-1;pointer-events:none}
/* Chapters 2+: buttons-only mode bar (no explanatory text) — slimmer pill,
   centered, so the pinned stack costs minimal phone-screen height. */
.mb-compact{justify-content:center;padding:2px 8px;gap:10px}
.ml{font-size:14px;color:var(--muted);flex:1}
.mbtn{padding:5px 14px;border-radius:40px;border:none;cursor:pointer;font-family:'Heebo',sans-serif;font-size:14px;font-weight:500;transition:all .2s}
.mbtn.active{background:var(--deep);color:white;box-shadow:0 2px 12px rgba(92,61,46,0.25)}
.mbtn:not(.active){background:var(--bg2);color:var(--muted)}
/* Before the reader chose (Shlomo, 12.10): reading and listening wear the same colour. */
.mbtn.neutral{background:var(--bg2);color:var(--deep);border:1.5px solid var(--sand);box-shadow:none}
.ap{background:linear-gradient(135deg,var(--deep),#3a2419);border-radius:var(--radius);padding:20px 24px;color:white;margin-bottom:24px;display:flex;align-items:center;gap:16px;flex-wrap:wrap}
.pb{width:52px;height:52px;border-radius:50%;background:var(--accent);border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:22px;flex-shrink:0}
/* .ap2-sticky — in-flow listen-player bar (AudioPlayer component, built on
   react-h5-audio-player). position:sticky (NOT fixed): on iOS WebKit — which
   Chrome on iPhone uses too — a position:fixed *interactive* element grows a
   STALE touch hit-region. Its painted position updates but the tappable area
   lags until a scroll event forces a recompute, so the reader had to nudge
   the page a millimetre or two before "pause" would respond (and it appeared
   to bob against the text). A sticky element rides in the scroll layer, so
   its hit-region never goes stale. It lives inside .bc (which spans the whole
   chapter), so once it sticks (top:118px — below the slim-header AND the
   sticky .mb mode bar) it stays pinned and visible for the entire chapter —
   including when the reader enters listen mode mid-scroll,
   since by then they're already scrolled past its in-flow position. No more
   .bc padding-top hack: the bar now occupies real flow space. (An earlier
   position:fixed + transform:translateX centering was worse — the transform
   made iOS desync the hit-region AND overflow narrow viewports.) */
.ap2-sticky{position:sticky;top:118px;z-index:50;margin:0 0 18px;padding:14px 16px 10px;background:linear-gradient(135deg,var(--deep),#3a2419);color:white;box-shadow:var(--shadow);border-radius:16px}
/* Same page-colored backdrop trick as .mb::before — covers the gap between
   the pinned mode bar (bottom ≈112px) and this player (top:118px) and the
   player's rounded corners, so chapter text can't scroll through it. */
.ap2-sticky::before{content:"";position:absolute;left:-16px;right:-16px;top:-16px;bottom:-6px;background:var(--bg);z-index:-1;pointer-events:none}
/* Same paint-order fix as .mb::after: the cream backdrop hid the player's
   dark gradient (white-on-white controls — Shlomo's report, 2.8). Repaint
   the gradient surface above the backdrop. */
.ap2-sticky::after{content:"";position:absolute;inset:0;background:linear-gradient(135deg,var(--deep),#3a2419);box-shadow:var(--shadow);border-radius:16px;z-index:-1;pointer-events:none}
.ap2-title{font-size:14px;font-weight:500;color:white;line-height:1.3;margin-bottom:8px;display:flex;align-items:center;justify-content:center;gap:12px;flex-wrap:wrap}
.ap2-title-text{flex:0 1 auto}
.ap2-title-slot{flex:0 0 auto}
.auto-scroll-toggle{display:inline-flex;align-items:center;gap:4px;padding:4px 12px;border-radius:999px;border:1px solid rgba(255,255,255,.25);background:rgba(255,255,255,.08);color:#fff;font-family:'Heebo',sans-serif;font-size:12px;font-weight:500;cursor:pointer;transition:background .12s,border-color .12s}
.auto-scroll-toggle:hover{background:rgba(255,255,255,.16);border-color:rgba(255,255,255,.4)}
.auto-scroll-toggle.is-off{background:rgba(0,0,0,.18);border-color:rgba(255,255,255,.18);opacity:.85}
.auto-scroll-toggle:focus-visible{outline:2px solid #fff;outline-offset:2px}
@media (max-width:480px){.auto-scroll-toggle{font-size:11px;padding:3px 10px}}
/* Driving safety notice — one-shot modal shown on first entry to
   listen mode per device. See src/components/DrivingNotice.tsx and the
   listening-experience spec. Warm overlay, centered card, single
   acknowledgment button. Auto-dismisses on overlay click as well. */
.driving-notice-overlay{position:fixed;inset:0;background:rgba(36,22,15,0.55);backdrop-filter:blur(2px);z-index:100;display:flex;align-items:center;justify-content:center;padding:24px;animation:driving-notice-fade-in .18s ease}
.driving-notice-card{background:#FFFAF2;border-radius:18px;box-shadow:0 12px 40px rgba(0,0,0,0.28);max-width:420px;width:100%;padding:32px 28px;text-align:center;border:1px solid #E8DCC8}
.driving-notice-icon{font-size:48px;margin-bottom:8px;line-height:1}
.driving-notice-title{font-family:'Frank Ruhl Libre',serif;font-size:24px;color:#5C3D2E;margin:0 0 14px 0;font-weight:600}
.driving-notice-body{font-family:'Heebo',sans-serif;font-size:15px;color:#3a2419;line-height:1.65;margin:0 0 22px 0}
.driving-notice-body strong{color:#A85A3D}
.driving-notice-btn{background:linear-gradient(135deg,#C5704A,#A85A3D);color:#fff;border:none;border-radius:999px;font-family:'Heebo',sans-serif;font-size:15px;font-weight:600;padding:12px 28px;cursor:pointer;box-shadow:0 3px 10px rgba(165,90,61,.3);transition:transform .12s,box-shadow .12s,filter .12s}
.driving-notice-btn:hover{transform:translateY(-1px);box-shadow:0 5px 14px rgba(165,90,61,.4);filter:brightness(1.05)}
.driving-notice-btn:active{transform:translateY(0)}
.driving-notice-btn:focus-visible{outline:2px solid #5C3D2E;outline-offset:2px}
@keyframes driving-notice-fade-in{from{opacity:0}to{opacity:1}}
@media (max-width:480px){.driving-notice-card{padding:24px 20px}.driving-notice-title{font-size:20px}.driving-notice-body{font-size:14px}}
/* Exercise overlay — listening redesign Approach D pilot. Surfaces when
   the AudioPlayer crosses a "structured" exercise marker (currently
   only chapter 6's תרגיל section). Less intrusive than the driving
   notice because it appears mid-flow — the audio has paused, the
   reader needs to decide whether to fill the exercise now or continue
   listening. Both buttons close the overlay; only "המשך האזנה"
   resumes audio. */
.exercise-overlay{position:fixed;inset:0;background:rgba(36,22,15,0.5);backdrop-filter:blur(2px);z-index:80;display:flex;align-items:center;justify-content:center;padding:24px;animation:driving-notice-fade-in .18s ease}
.exercise-overlay-card{background:#FFFAF2;border-radius:18px;box-shadow:0 12px 40px rgba(0,0,0,0.28);max-width:440px;width:100%;padding:28px 24px;text-align:center;border:1px solid #E8DCC8}
.exercise-overlay-icon{font-size:42px;margin-bottom:6px;line-height:1}
.exercise-overlay-title{font-family:'Frank Ruhl Libre',serif;font-size:20px;color:#5C3D2E;margin:0 0 10px 0;font-weight:600;line-height:1.3}
.exercise-overlay-body{font-family:'Heebo',sans-serif;font-size:14px;color:#3a2419;line-height:1.6;margin:0 0 22px 0}
.exercise-overlay-actions{display:flex;flex-direction:column;gap:10px}
.exercise-overlay-btn{border-radius:999px;font-family:'Heebo',sans-serif;font-size:14px;font-weight:600;padding:10px 22px;cursor:pointer;transition:transform .12s,box-shadow .12s,filter .12s,background .12s,border-color .12s}
.exercise-overlay-btn.primary{background:linear-gradient(135deg,#C5704A,#A85A3D);color:#fff;border:none;box-shadow:0 3px 10px rgba(165,90,61,.3)}
.exercise-overlay-btn.primary:hover{transform:translateY(-1px);box-shadow:0 5px 14px rgba(165,90,61,.4);filter:brightness(1.05)}
.exercise-overlay-btn.secondary{background:transparent;color:#5C3D2E;border:1px solid #C9B594}
.exercise-overlay-btn.secondary:hover{background:#F3EDE3;border-color:#A85A3D}
.exercise-overlay-btn:active{transform:translateY(0)}
.exercise-overlay-btn:focus-visible{outline:2px solid #5C3D2E;outline-offset:2px}
@media (max-width:480px){.exercise-overlay-card{padding:22px 18px}.exercise-overlay-title{font-size:18px}.exercise-overlay-body{font-size:13px}}
/* --- overrides for the library's default .rhap_* classes to match
   the book's warm-brown palette. The library renders LTR by default;
   for Hebrew we keep the controls left→right (play/skip controls read
   the same way globally) but the progress bar fills from left to right
   which matches universal media-player conventions. */
.rhap_container{background:transparent !important;box-shadow:none !important;padding:0 !important;font-family:'Heebo',sans-serif !important;direction:ltr}
.rhap_progress-section{margin-bottom:6px}
.rhap_progress-container{height:18px}
.rhap_progress-bar{background-color:rgba(255,255,255,0.18) !important;height:5px}
.rhap_progress-filled,.rhap_progress-indicator{background-color:var(--accent) !important}
.rhap_progress-indicator{width:14px !important;height:14px !important;top:-5px !important;margin-left:-7px !important;box-shadow:0 1px 4px rgba(0,0,0,0.4)}
.rhap_download-progress{background-color:rgba(255,255,255,0.08) !important}
.rhap_time{color:rgba(255,255,255,0.85) !important;font-size:12px !important;font-variant-numeric:tabular-nums}
.rhap_main-controls-button,.rhap_button-clear{color:white !important;opacity:.9;transition:transform .12s, opacity .12s}
.rhap_main-controls-button:hover,.rhap_button-clear:hover{opacity:1;transform:scale(1.07)}
.rhap_play-pause-button{font-size:42px !important;width:46px !important;height:46px !important;color:var(--accent) !important}
.rhap_play-pause-button:hover{color:#fff !important}
.rhap_rewind-button,.rhap_forward-button{font-size:26px !important;width:26px !important;height:26px !important}
@media (max-width:480px){
  .ap2-sticky{padding:10px 12px 8px}
  .ap2-title{font-size:13px;margin-bottom:6px}
}
.ai{flex:1;min-width:120px}.at{font-size:14px;font-weight:500;margin-bottom:4px}.as{font-size:12px;opacity:.6}
.vs{background:rgba(255,255,255,0.12);border:1px solid rgba(255,255,255,0.2);border-radius:8px;padding:6px 10px;color:white;font-size:12px;font-family:'Heebo',sans-serif;cursor:pointer;outline:none}
.vs option{background:#3a2419;color:white}
.spb{background:rgba(255,255,255,0.12);border:1px solid rgba(255,255,255,0.25);color:white;border-radius:20px;padding:5px 14px;font-size:12px;font-family:'Heebo',sans-serif;cursor:pointer;white-space:nowrap}
.spb.pl{background:var(--accent);border-color:var(--accent)}
.sc{background:var(--white);border-radius:var(--radius);padding:14px 24px 18px;margin-bottom:14px;box-shadow:var(--shadow);border:1px solid rgba(196,168,130,0.2)}
.sh{display:flex;align-items:flex-start;gap:12px;margin-bottom:6px}
.sh .section-listen-anchor{display:flex;align-items:center;height:44px}
.sh .section-listen-btn{margin:0}
.si{width:44px;height:44px;border-radius:12px;display:flex;align-items:center;justify-content:center;font-size:22px;flex-shrink:0}
.si-i{background:#FEF3E2}.si-r{background:#EEF2FF}.si-p{background:#E8F4EC}.si-f{background:#FFF0F0}
.stitle{font-family:'Frank Ruhl Libre',serif;font-size:20px;font-weight:700;color:var(--deep);line-height:1.3}
.sdesc{font-size:14px;color:var(--muted);margin-top:3px}
.sha{margin-right:auto;display:flex;gap:8px;align-items:center}
.rt{font-size:17px;line-height:1.7;color:var(--text);font-weight:400}
.rt strong{color:var(--terra);font-weight:600}
.rt em{font-style:normal;background:rgba(212,135,90,0.15);padding:1px 4px;border-radius:4px}
.rt p{margin-bottom:16px}.rt p:last-child{margin-bottom:0}
.poem{background:linear-gradient(135deg,#FFF8F0,#FFF3E8);border-right:4px solid var(--accent);border-radius:0 12px 12px 0;padding:20px 24px;margin:20px 0;font-size:15px;line-height:2.1;color:var(--deep);font-family:'Frank Ruhl Libre',serif}
.tip{background:#FFFBF5;border-right:4px solid var(--warm);border-radius:0 12px 12px 0;padding:14px 18px;font-size:14px;color:var(--deep);line-height:1.7;margin-bottom:20px}
.tip strong{color:var(--terra)}
.hbox{background:#F0F7F3;border:1px solid #B2DFDB;border-radius:12px;padding:16px 20px;margin:16px 0 20px}
.ht{font-weight:600;color:#2E7D32;font-size:14px;margin-bottom:8px}
.hbox ul{padding-right:20px}.hbox li{font-size:14px;line-height:2;color:var(--deep)}
.warn-box{background:#FFF8E1;border-right:4px solid #FFB300;border-radius:0 12px 12px 0;padding:16px 20px;margin:16px 0;font-size:15px;line-height:1.8;color:var(--deep)}
.warn-box strong{color:#E65100}
/* Inline emphasis system (chapters 6-12; ch1-5 use boxes). Three tiers, each a
   consistent meaning so the variety holds attention instead of becoming noise:
   .em-key = the section's headline idea (bold + soft highlight, ~1/chapter),
   .em-imp = an important point/definition (warm bold), .em-ref = a reflective
   aphorism (warm italic, occasional). Applied in place by apply_emphasis.py —
   the sentence stays in the flow, it's only wrapped, never duplicated/removed. */
.em-key{font-weight:700;background:#FAE7C2;border-radius:4px;padding:1px 5px;box-decoration-break:clone;-webkit-box-decoration-break:clone}
.em-imp{font-weight:700;color:#A0522D}
.em-ref{font-style:italic;color:var(--deep)}
.dv{height:1px;background:linear-gradient(90deg,transparent,var(--sand),transparent);margin:4px 0 10px}
.rg{display:flex;flex-direction:column;gap:16px}
.fl{font-size:14px;font-weight:600;color:var(--deep);margin-bottom:6px;display:block}
.fs{font-size:12px;color:var(--muted);margin-bottom:8px;display:block}
.ri{width:100%;padding:12px 16px;border:2px solid var(--sand);border-radius:12px;font-family:'Heebo',sans-serif;font-size:15px;line-height:1.6;color:var(--text);background:var(--bg);resize:vertical;outline:none;transition:border-color .2s;direction:rtl;min-height:60px}
.ch2-fig{margin:22px 0 6px;text-align:center}.ch2-fig img{max-width:100%;border-radius:12px;display:block;margin:0 auto}.ch2-fig figcaption{font-size:13px;color:var(--muted);margin-top:6px;font-style:italic}
.exbox{background:#E8EEF7;border:1px solid #C9D6EA;border-radius:12px;padding:14px 18px;margin:14px 0 12px}.exlabel{font-size:12px;color:#5B6B85;font-weight:600;margin-bottom:4px}.extitle{font-weight:700;font-size:17px;color:var(--deep);margin-bottom:6px}.exbox p{margin:6px 0;font-size:15px;line-height:1.75}
.frbox{background:#FBF4DC;border:1px solid #E8D9A8;border-radius:12px;padding:14px 18px;margin:0 0 10px}.frlabel{font-size:12px;color:#8A7A45;font-weight:600;margin-bottom:4px}.frtitle{font-weight:700;font-size:17px;color:var(--deep);margin-bottom:6px}.frintro{font-size:14px;color:var(--muted);font-style:italic;margin-bottom:8px}
.fr2col{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:8px}@media(max-width:520px){.fr2col{grid-template-columns:1fr}}
.frscale{display:flex;gap:4px;flex-wrap:wrap;margin:4px 0 12px}.frscale button{width:34px;height:34px;border-radius:50%;border:1.5px solid var(--sand);background:var(--bg);font-family:'Heebo',sans-serif;font-size:14px;cursor:pointer;color:var(--text)}.frscale button.on{background:var(--deep);color:#fff;border-color:var(--deep)}
.bridge{font-style:italic;font-weight:600;color:var(--deep);margin:4px 0 22px;font-size:15px}
.frchips{display:flex;gap:6px;flex-wrap:wrap;margin:4px 0 12px}.frchips button{padding:6px 12px;border-radius:999px;border:1.5px solid var(--sand);background:var(--bg);font-family:'Heebo',sans-serif;font-size:14px;cursor:pointer;color:var(--text)}.frchips button.on{background:var(--deep);color:#fff;border-color:var(--deep)}
.lsdeck{background:#FBF4DC;border:1px solid #E8D9A8;border-radius:14px;padding:14px 16px 12px;margin:14px 0 10px;direction:rtl}.lshead{display:flex;justify-content:space-between;align-items:center;font-size:12px;color:#8A7A45;font-weight:600;margin-bottom:6px}.lsart{max-width:330px;margin:0 auto}.lssvg{width:100%;height:auto;display:block}.lstitle{font-weight:700;font-size:19px;color:var(--deep);margin:10px 0 4px;text-align:center;line-height:1.35;text-wrap:balance}.lslead{font-size:15px;color:var(--text);line-height:1.7;margin:0 0 10px;text-align:center}.lsq{font-size:14px;font-weight:600;color:var(--deep);margin:6px 0 4px}.lshint{font-size:14px;color:var(--muted);line-height:1.6;background:var(--bg);border-radius:10px;padding:8px 12px;margin:2px 0 10px;min-height:1.6em}.lsmore{margin:6px 0 10px;font-size:14px;color:var(--text);line-height:1.7}.lsmore summary{cursor:pointer;color:#8A7A45;font-weight:600;font-size:13px}.lsmore p{margin:6px 0}.lsnav{display:flex;align-items:center;justify-content:space-between;gap:8px;border-top:1px solid #E8D9A8;padding-top:10px;margin-top:4px}.lsnav button{padding:7px 12px;border-radius:999px;border:1.5px solid var(--deep);background:var(--deep);color:#fff;font-family:'Heebo',sans-serif;font-size:13px;cursor:pointer;white-space:nowrap;flex-shrink:0}.lsnav button.ghost{background:var(--bg);color:var(--deep)}.lsnav button:disabled{opacity:.35;cursor:default}.lsdots{display:flex;gap:5px;flex-wrap:wrap;justify-content:center}.lsdots button{width:10px;height:10px;padding:0;border-radius:50%;border:1px solid var(--deep);background:transparent;cursor:pointer}.lsdots button.on{background:var(--deep)}.lsdots button.done{background:#C9A44C;border-color:#C9A44C}.lsbreath{text-align:center;margin:4px 0 10px}.lsbreath .big{font-size:48px;font-weight:700;color:var(--deep);line-height:1.1}.lsbreath>button{padding:10px 20px;border-radius:999px;border:none;background:var(--deep);color:#fff;font-family:'Heebo',sans-serif;font-size:15px;font-weight:600;cursor:pointer}.lsrecap{list-style:none;padding:0;margin:0 0 10px;display:grid;gap:6px}.lsrecap li{display:flex;gap:8px;flex-wrap:wrap;font-size:14px;background:var(--bg);border-radius:8px;padding:6px 10px}.lsrecap b{color:var(--deep);min-width:8em}.lsrecap .none{color:var(--muted)}
.ri:focus{border-color:var(--accent);background:var(--white)}
/* .ta — short-form textarea for inline exercise prompts (ex1-ex7,
   chapter 4/5/7 standalone reflections). Full-width like .ri but a
   touch lighter on border + padding so it doesn't dominate when used
   inline between paragraphs. Inline width:100% styles on table-cell
   textareas (with className="ta") still override these. */
.ta{width:100%;padding:10px 14px;border:1.5px solid var(--sand);border-radius:10px;font-family:'Heebo',sans-serif;font-size:14px;line-height:1.55;color:var(--text);background:var(--white);resize:vertical;outline:none;transition:border-color .2s;direction:rtl;text-align:right;box-sizing:border-box}
.ta:focus{border-color:var(--terra);background:var(--white)}
.cc{text-align:left;font-size:12px;color:var(--muted);margin-top:4px}
.pc{background:linear-gradient(135deg,var(--deep),#3a2419);border-radius:16px;padding:28px;color:white;margin-bottom:20px}
.pt{font-family:'Frank Ruhl Libre',serif;font-size:22px;font-weight:700;margin-bottom:6px}
.pd{font-size:14px;opacity:.85;line-height:1.7;margin-bottom:20px}.pd strong{color:#FFD180}
.ps{display:flex;flex-direction:column;gap:14px}
.pst{display:flex;gap:14px;align-items:flex-start}
.sn{width:32px;height:32px;border-radius:50%;background:var(--accent);display:flex;align-items:center;justify-content:center;font-family:'Frank Ruhl Libre',serif;font-size:15px;font-weight:700;flex-shrink:0;color:white}
.stx{font-size:14px;line-height:1.7;opacity:.92;padding-top:4px}.stx strong{color:#FFD180}
.cp{background:var(--white);border:1px solid var(--sand);border-radius:50px;padding:10px 20px;display:flex;align-items:center;gap:12px;margin-bottom:24px;box-shadow:var(--shadow)}
.cpl{font-size:13px;color:var(--muted)}
.cpt{flex:1;height:6px;background:var(--bg2);border-radius:3px;overflow:hidden}
.cpi{height:100%;background:linear-gradient(90deg,var(--terra),var(--accent));border-radius:3px;transition:width .5s}
.cpp{font-size:13px;font-weight:600;color:var(--terra);min-width:32px}
.acts{display:flex;gap:12px;margin-top:20px;flex-wrap:wrap}
.bpr{flex:1;padding:14px 24px;background:linear-gradient(135deg,var(--terra),var(--accent));color:white;border:none;border-radius:12px;font-family:'Heebo',sans-serif;font-size:15px;font-weight:600;cursor:pointer}
.bsc{padding:14px 24px;background:var(--bg2);color:var(--deep);border:1.5px solid var(--sand);border-radius:12px;font-family:'Heebo',sans-serif;font-size:15px;cursor:pointer}
.svb{position:fixed;bottom:24px;left:50%;transform:translateX(-50%);background:var(--deep);color:white;padding:12px 28px;border-radius:40px;font-size:14px;font-weight:500;opacity:0;transition:opacity .3s;pointer-events:none;z-index:100;white-space:nowrap}
.svb.vis{opacity:1}
figure{margin:20px 0;text-align:center}
figure img{width:100%;border-radius:16px;box-shadow:0 8px 32px rgba(92,61,46,0.2)}
figcaption{margin-top:10px;font-size:14px;color:#8B7355;font-style:italic}
.imgph{margin:20px 0;padding:32px;background:#FFF8F0;border:2px dashed #C4A882;border-radius:12px;text-align:center}
.imgph .ico{font-size:32px;margin-bottom:8px}
.imgph .lbl{font-weight:600;color:#A0704A;margin-bottom:4px}
.imgph .hint{font-size:13px;color:#8B7355}
.ch-numbered{counter-reset:section}
.ch-numbered .sc{counter-increment:section;position:relative}
.ch-numbered .sc::before{position:absolute;top:14px;right:18px;background:var(--bg2);color:var(--muted);padding:3px 11px;border-radius:12px;font-size:11px;font-weight:700;font-family:'Heebo',sans-serif;border:1px solid var(--sand);letter-spacing:.5px}
.ch-numbered-6 .sc::before{content:"6." counter(section)}
.ch-numbered-7 .sc::before{content:"7." counter(section)}
.ch-numbered-8 .sc::before{content:"8." counter(section)}
.ch-numbered-9 .sc::before{content:"9." counter(section)}
.ch-numbered-10 .sc::before{content:"10." counter(section)}
.ch-numbered-11 .sc::before{content:"11." counter(section)}
.ch-numbered-12 .sc::before{content:"12." counter(section)}
/* Per-section listen button. Portalled by ChapterContent into a
   placeholder div at the top of every .sc block of chapters 6–12 when
   in read mode. Icon-only on every viewport per the listening-redesign
   spec: 🎧 is recognisable enough on its own; adding text would clutter
   the section heading area. Tooltip + aria-label still expose
   "האזן לסעיף N.M" for accessibility. */
.section-listen-anchor{display:inline-block}
.section-listen-btn{display:inline-flex;align-items:center;justify-content:center;margin:0 0 12px 0;padding:0;width:40px;height:40px;background:linear-gradient(135deg,#C5704A,#A85A3D);color:#fff;border:none;border-radius:50%;font-size:18px;cursor:pointer;box-shadow:0 2px 6px rgba(165,90,61,.25);transition:transform .12s ease,box-shadow .12s ease,filter .12s ease}
.section-listen-btn:hover{transform:translateY(-1px);box-shadow:0 4px 10px rgba(165,90,61,.32);filter:brightness(1.05)}
.section-listen-btn:active{transform:translateY(0);box-shadow:0 1px 3px rgba(165,90,61,.3)}
.section-listen-btn:focus-visible{outline:2px solid #5C3D2E;outline-offset:2px}
@media (max-width:480px){.section-listen-btn{width:36px;height:36px;font-size:16px}}
/* TOC (table of contents) styles */
/* TOC trigger lives inside the slim header at top-right (RTL start side).
   Replaces a former .toc-fab floating button that overlapped chapter content
   on narrow viewports. Compact icon-only — matches the auth badge on the left. */
.slim-header-toc{position:absolute;top:8px;right:12px;z-index:56;background:var(--deep);color:white;border:none;border-radius:8px;width:38px;height:38px;font-size:16px;font-weight:600;cursor:pointer;display:flex;align-items:center;justify-content:center;font-family:'Heebo',sans-serif;box-shadow:0 2px 8px rgba(92,61,46,0.18);transition:transform .15s,box-shadow .15s}
.slim-header-toc:hover{transform:translateY(-1px);box-shadow:0 4px 12px rgba(92,61,46,0.28)}
.slim-header-toc:focus-visible{outline:2px solid var(--accent);outline-offset:2px}
.toc-page{max-width:860px;margin:0 auto;padding:0 16px 80px}
.toc-back{display:inline-flex;align-items:center;gap:8px;background:var(--bg2);border:1.5px solid var(--sand);border-radius:50px;padding:9px 18px;font-family:'Heebo',sans-serif;font-size:14px;font-weight:500;color:var(--deep);cursor:pointer;margin-bottom:24px;transition:background .15s}
.toc-back:hover{background:var(--sand)}
.toc-h{font-family:'Frank Ruhl Libre',serif;font-size:34px;font-weight:700;color:var(--deep);text-align:center;margin-bottom:8px}
.toc-sub{text-align:center;color:var(--muted);font-size:14px;margin-bottom:32px}
.toc-card{background:var(--white);border:1px solid rgba(196,168,130,0.25);border-radius:var(--radius);padding:20px 24px;margin-bottom:14px;box-shadow:var(--shadow);transition:all .2s}
.toc-card:hover{box-shadow:var(--shadow-lg);border-color:var(--accent)}
.toc-chh{display:flex;align-items:center;gap:14px;cursor:pointer;padding:4px 0}
.toc-cicon{width:46px;height:46px;border-radius:12px;background:linear-gradient(135deg,var(--terra),var(--accent));color:white;display:flex;align-items:center;justify-content:center;font-size:22px;flex-shrink:0;font-weight:700;font-family:'Frank Ruhl Libre',serif}
.toc-cmeta{flex:1}
.toc-cnum{font-size:11px;color:var(--muted);font-weight:600;letter-spacing:1px;text-transform:uppercase}
.toc-ctit{font-family:'Frank Ruhl Libre',serif;font-size:19px;font-weight:700;color:var(--deep);line-height:1.3}
.toc-cbook{font-size:12px;color:var(--terra);margin-top:3px}
.toc-arrow{color:var(--muted);font-size:20px;transition:transform .15s}
.toc-chh:hover .toc-arrow{color:var(--terra);transform:translateX(-4px)}
.toc-subs{margin-top:14px;padding-top:14px;border-top:1px dashed var(--sand);display:flex;flex-direction:column;gap:6px}
.toc-sub-link{display:flex;align-items:center;gap:10px;padding:8px 12px;border-radius:8px;background:var(--bg);border:1px solid transparent;cursor:pointer;color:var(--ink);font-size:14px;transition:all .15s;text-align:right}
.toc-sub-link:hover{background:var(--bg2);border-color:var(--sand);transform:translateX(-2px)}
.toc-sub-num{font-size:11px;color:var(--muted);font-weight:700;min-width:32px;font-family:'Heebo',sans-serif}
.toc-sub-tit{flex:1;line-height:1.5}
/* Mirror Law rulers: horizontal on desktop, stacked on mobile */
.ruler-row{display:flex;align-items:center;gap:16px;margin-bottom:22px;font-size:13px}
.ruler-side{width:130px;padding:8px 10px;border:1px solid var(--sand);border-radius:6px;font-size:13px;text-align:center;direction:rtl;flex-shrink:0;font-family:inherit;background:var(--white)}
.ruler-scale-wrap{flex:1;min-width:220px;display:flex;align-items:center;gap:8px;justify-content:center}
.ruler-num{color:var(--muted);font-size:11px;flex-shrink:0}
.ruler-scale{flex:1;display:flex;flex-direction:column;align-items:center;gap:6px}
.ruler-count{width:60px;padding:4px 8px;border:1px solid var(--sand);border-radius:6px;font-size:13px;text-align:center;background:white;font-family:inherit}
.ruler-line{width:100%;height:2px;background:var(--sand)}
/* Mirror Law: card-per-person properties table on mobile */
.props-table{width:100%;border-collapse:collapse;font-size:13px}
.props-person-card{display:none;background:var(--white);border:1px solid var(--sand);border-radius:10px;padding:14px;margin-bottom:14px}
.props-person-head{font-weight:700;font-size:15px;color:var(--deep);padding-bottom:8px;margin-bottom:10px;border-bottom:1px solid var(--sand);display:flex;align-items:center;gap:8px}
.props-person-sub{font-size:12px;color:var(--muted);font-weight:500;margin-right:auto}
.props-trait-row{display:flex;align-items:center;gap:8px;margin-bottom:8px}
.props-trait-label{font-size:12px;color:var(--muted);width:64px;flex-shrink:0;font-weight:500}
.props-trait-input{flex:1;padding:8px 10px;border:1px solid var(--sand);border-radius:6px;font-size:13px;font-family:inherit;text-align:right;direction:rtl;background:var(--bg2)}
/* Chapter 7 values grid: positive list (96 values, 5 cols on desktop /
   3 cols on mobile, column-major alphabetical) + a visually-separated
   "values to avoid" column (8 items, lightly red-tinted to signal intent).
   Two blocks divided by a thin vertical rule on desktop; on mobile they
   stack with a horizontal divider and the negatives become 3 cols too. */
.values-wrap{display:flex;gap:18px;align-items:stretch;margin:8px 0 4px}
.values-positive{flex:1;min-width:0}
.values-negative{flex:0 0 auto;width:170px}
.values-separator{width:1px;background:var(--sand);align-self:stretch;flex-shrink:0}
.values-section-label{font-size:12px;color:var(--muted);font-weight:600;margin-bottom:8px;text-align:center;letter-spacing:.2px}
.values-grid-pos{display:grid;grid-template-rows:repeat(21,auto);grid-auto-flow:column;grid-auto-columns:1fr;gap:6px}
.values-grid-neg{display:grid;grid-template-columns:1fr;gap:6px}
.values-cell{padding:6px 8px;background:var(--bg2);border-radius:5px;border:1px solid var(--sand);text-align:center;font-size:13px;line-height:1.4;color:var(--deep)}
.values-cell-neg{background:#FFF1F1;border-color:#F1C9C9;color:#8B3535}
@media(max-width:600px){
  .values-wrap{flex-direction:column;gap:14px}
  .values-positive,.values-negative{width:100%;flex:0 0 auto}
  .values-separator{width:100%;height:1px;align-self:auto}
  .values-grid-pos{grid-template-rows:repeat(32,auto)}
  .values-grid-neg{grid-template-columns:repeat(3,1fr)}
}
/* Wide-table scroll affordance: pure-CSS scroll shadows (Roma Komarov pattern).
   Shadows appear at scrollable edges and auto-hide when fully scrolled to that side. */
.wide-scroll{
  overflow-x:auto;
  -webkit-overflow-scrolling:touch;
  background:
    /* cover fades (match container bg) — hide shadow at the edge when nothing to scroll there */
    linear-gradient(to right, var(--bg) 30%, rgba(255,247,239,0)) left center / 28px 100% no-repeat,
    linear-gradient(to left, var(--bg) 30%, rgba(255,247,239,0)) right center / 28px 100% no-repeat,
    /* shadows (revealed when scrolled away from that edge) */
    radial-gradient(ellipse at left, rgba(0,0,0,0.16), transparent 70%) left center / 14px 100% no-repeat,
    radial-gradient(ellipse at right, rgba(0,0,0,0.16), transparent 70%) right center / 14px 100% no-repeat;
  background-attachment: local, local, scroll, scroll;
}
@media(max-width:600px){
  .bh{padding:28px 20px 24px}.ct{font-size:24px}.sc{padding:12px 14px 14px}
  .bh-slim{padding:12px 16px 10px}.bh-slim .ct{font-size:22px}
  .ch-numbered .sc::before{top:10px;right:12px;font-size:10px;padding:2px 8px}
  .slim-header-toc{top:6px;right:8px;width:34px;height:34px;font-size:15px}
  .slim-header-locate{top:8px;right:46px;width:26px;height:26px;font-size:13px}
  .slim-header-help{top:8px;right:80px;left:auto;width:26px;height:26px;font-size:12px}
  .slim-header-wa{top:8px;right:112px;width:26px;height:26px;font-size:13px}
  .toc-h{font-size:26px}.toc-card{padding:16px}.toc-cicon{width:40px;height:40px;font-size:18px}
  /* Mobile header padding leaves room for both auth (left) and toc (right) buttons.
     Left was 48px, but even the icon-collapsed AuthBadge (✎ + ⏻, admins also ⚙)
     reaches ~90px from the left edge — the title used to run under the buttons
     and truncate mid-word (Noam's screenshots, 1.8). */
  .slim-header-row{padding:12px 112px 6px 92px;font-size:12px;min-height:40px}
  /* Rulers stack vertically */
  .ruler-row{flex-direction:column;align-items:stretch;gap:8px;padding:12px;background:var(--white);border:1px solid var(--sand);border-radius:10px;margin-bottom:14px}
  .ruler-side{width:100%;padding:10px 12px;font-size:14px}
  .ruler-scale-wrap{min-width:0}
  /* Hide grid table, show card-per-person */
  .props-table-wrap{display:none}
  .props-person-card{display:block}
}
`;

// localStorage key holding {chapter, started, scrollY} so a refresh
// drops the reader back where they were instead of bouncing them to chapter 1.
const POS_KEY = "ei_book_pos";
const loadSavedPos = () => {
  try {
    return JSON.parse(localStorage.getItem(POS_KEY) || "{}");
  } catch {
    return {};
  }
};

// Chapter 6 "Mirror Law" — colored dot next to each trait. Lets the reader
// tag traits that belong to the same internal "ruler" (סרגל) with a shared
// colour. Index 0 = hollow/unset, 1-9 = filled colours. Clicking the dot
// cycles 0 → 1 → ... → 9 → 0 → ... — the cycle through "0" gives the
// reader an explicit way to clear a colour. Values persist via the same
// `fields` mechanism the trait inputs use: key
// `ch6_t1_<rowKey>_c<colIdx>_color`. Stored as the stringified index 0-9.
const CH6_PEOPLE = [
  { label: "אדם יקר", sub: "אוהבים ומעריכים", color: "#C62828", bg: "#FFEBEE" },
  { label: "מכר ניטרלי", sub: "לא טוב ולא רע", color: "#0277BD", bg: "#E1F5FE" },
  { label: "דמות מפורסמת", sub: "מהתקשורת בלבד", color: "#6A1B9A", bg: "#F3E5F5" },
  { label: "פוליטיקאי", sub: "מהתקשורת בלבד", color: "#E65100", bg: "#FFF3E0" },
  { label: "אדם מאתגר", sub: "לעיתים מעצבן אותך", color: "#2E7D32", bg: "#E8F5E9" },
] as const;

// Chapter 7 "ארבעת הערוצים" fill-table column headers — shared by the desktop
// table (sticky header) and the mobile card-per-row view so both stay in sync.
const CH7_CHANNEL_COLS = [
  "המרכיב שבחרתי",
  "מידת מעורבות המרכיב ביום יום שלי",
  "הוא חיובי או שלילי עבורי",
  "להעצים או לצמצם אותו",
  "זקוקה לסיוע לשם כך?",
  "מי יכול לסייע לך",
] as const;

// Chapter 7 "ערכים" exercise — the three value/belief categories become the
// three cards (headers), each holding the current-state + plans fields. Tints
// carried over from the old 4-column table (green / amber / red).
const CH7_VALUE_COLS = [
  { label: "ערכים ואמונות בהם אני מאמינה וחיה על פיהם", bg: "#E8F5E9", border: "#B7DFC0" },
  { label: "ערכים ואמונות בהם אני מאמינה ולא חיה על פיהם (עדיין)", bg: "#FFF8E1", border: "#EAD9A3" },
  { label: "ערכים ואמונות בהם אני לא מאמינה וחיה על פיהם", bg: "#FFEBEE", border: "#EBC4CA" },
] as const;

const CH6_TRAIT_COLORS = [
  "transparent", // 0 = hollow
  "#E53935", // 1 red
  "#FB8C00", // 2 orange
  "#FDD835", // 3 yellow
  "#43A047", // 4 green
  "#00ACC1", // 5 teal
  "#1E88E5", // 6 blue
  "#8E24AA", // 7 purple
  "#D81B60", // 8 pink
  "#607D8B", // 9 grey
];

function ch6ReadColorIdx(value: any): number {
  const n = parseInt(String(value || ""), 10);
  if (!Number.isFinite(n) || n < 0 || n > 9) return 0;
  return n;
}

type TraitColorDotProps = {
  fk: string;
  fields: Record<string, any>;
  setFields: (next: Record<string, any>) => void;
  size?: number;
};

function TraitColorDot({ fk, fields, setFields, size = 22 }: TraitColorDotProps) {
  const idx = ch6ReadColorIdx(fields[fk]);
  const fill = CH6_TRAIT_COLORS[idx];
  const isHollow = idx === 0;
  return (
    <button
      type="button"
      onClick={() => {
        const next = (idx + 1) % CH6_TRAIT_COLORS.length;
        setFields({ ...fields, [fk]: String(next) });
      }}
      title={isHollow ? "לחץ לסימון בצבע" : `צבע ${idx} (לחץ להחלפה)`}
      aria-label={isHollow ? "סמן בצבע" : `צבע ${idx}, לחץ להחלפה`}
      style={{
        width: size,
        height: size,
        minWidth: size,
        borderRadius: "50%",
        border: isHollow
          ? "1.5px dashed var(--warm)"
          : `1.5px solid rgba(0,0,0,0.18)`,
        background: fill,
        cursor: "pointer",
        padding: 0,
        flexShrink: 0,
        boxShadow: isHollow ? "none" : "0 1px 3px rgba(0,0,0,0.18)",
        transition: "background 0.12s, border-color 0.12s",
      }}
    />
  );
}

// Hebrew names for the 9 colors (index 0 unused). Used in the rulers list.
const CH6_COLOR_NAMES = [
  "", "אדום", "כתום", "צהוב", "ירוק", "טורקיז", "כחול", "סגול", "ורוד", "אפור",
];

// Tally how many of the 25 traits carry each color. Returns an array of
// { idx, count } for every color actually used, sorted by count (desc) then
// color index (asc) so the most-used ruler appears first.
function ch6TallyColors(fields: Record<string, any>): { idx: number; count: number }[] {
  const counts: Record<number, number> = {};
  for (const rk of ["trait1", "trait2", "trait3", "trait4", "trait5"]) {
    for (let c = 0; c < 5; c++) {
      const idx = ch6ReadColorIdx(fields[`ch6_t1_${rk}_c${c}_color`]);
      if (idx > 0) counts[idx] = (counts[idx] || 0) + 1;
    }
  }
  return Object.keys(counts)
    .map((k) => ({ idx: parseInt(k, 10), count: counts[parseInt(k, 10)] }))
    .sort((a, b) => b.count - a.count || a.idx - b.idx);
}

// Count how many of the 25 trait cells have text (filled) and how many carry
// a color (colored). Drives the adaptive guidance box above the rulers.
function ch6Stats(fields: Record<string, any>): { filled: number; colored: number } {
  let filled = 0;
  let colored = 0;
  for (const rk of ["trait1", "trait2", "trait3", "trait4", "trait5"]) {
    for (let c = 0; c < 5; c++) {
      const text = String(fields[`ch6_t1_${rk}_c${c}`] || "").trim();
      const idx = ch6ReadColorIdx(fields[`ch6_t1_${rk}_c${c}_color`]);
      if (text) filled++;
      if (idx > 0) colored++;
    }
  }
  return { filled, colored };
}

// A count number inside a hand-drawn-looking circle. Two slightly-offset
// rough ellipse strokes give the doubled-line look of a pen circle, so it
// reads as "scribbled by hand" rather than a perfect computer circle.
function ScribbledCount({ n, color }: { n: number; color: string }) {
  return (
    <span
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 40,
        height: 40,
        flexShrink: 0,
      }}
    >
      <svg
        width="40"
        height="40"
        viewBox="0 0 40 40"
        style={{ position: "absolute", inset: 0 }}
        aria-hidden="true"
      >
        <path
          d="M20,5 C29,4 35,12 34,20 C35,29 28,35 20,34 C11,35 5,28 6,20 C5,11 12,5 20,5"
          fill="none"
          stroke={color}
          strokeWidth="1.7"
          strokeLinecap="round"
          opacity="0.9"
        />
        <path
          d="M21,6.5 C12,5.5 6.5,13 6.5,20.5 C5.5,29 14,34 21,33 C30,34 34,26.5 33,18.5 C34,11.5 28,6.5 21,6.5"
          fill="none"
          stroke={color}
          strokeWidth="1.1"
          strokeLinecap="round"
          opacity="0.5"
        />
      </svg>
      <span style={{ position: "relative", fontWeight: 700, fontSize: 15, color: "var(--deep)" }}>
        {n}
      </span>
    </span>
  );
}

// N short vertical tick marks in the ruler's colour. Wraps to multiple rows
// for high counts. The number is shown separately in the ScribbledCount, so
// plain lines (no tally-grouping) are enough.
function RulerTicks({ n, color }: { n: number; color: string }) {
  return (
    <div style={{ display: "flex", gap: 3, alignItems: "center", flexWrap: "wrap", maxWidth: 160 }}>
      {Array.from({ length: n }).map((_, i) => (
        <div key={i} style={{ width: 2.5, height: 20, background: color, borderRadius: 1.5 }} />
      ))}
    </div>
  );
}

// Worked example for chapter 6, rendered in the SAME visual language the
// reader just used (coloured dots + ruler ticks + scribbled count). Shows
// how several different-sounding traits collapse into ONE ruler when they
// share a colour — the core "aha" of the Mirror Law exercise. Static
// illustration (hard-coded sample data), framed as a post-work answer key.
function Ch6WorkedExample() {
  const GREEN = CH6_TRAIT_COLORS[4];
  const sampleTraits = ["ישר", "אשת מצפון", "מדבר בגובה העיניים", "הוגן"];
  return (
    <div style={{ marginTop: 22, padding: "16px 18px", background: "#FBF7F0", border: "1px dashed var(--warm)", borderRadius: 12 }}>
      <div style={{ fontSize: 12, color: "var(--muted)", fontStyle: "italic", marginBottom: 4 }}>
        דוגמה — הציצו רק אחרי שמילאתם את שלכם
      </div>
      <div style={{ fontWeight: 600, color: "var(--deep)", marginBottom: 14, fontSize: 14 }}>
        איך כמה תכונות הופכות לסרגל אחד
      </div>

      {/* sample traits, each tagged with the same colour */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 6 }}>
        {sampleTraits.map((t) => (
          <span
            key={t}
            style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "7px 12px", background: "var(--white)", border: "1px solid var(--sand)", borderRadius: 8, fontSize: 13 }}
          >
            <span>{t}</span>
            <span style={{ width: 15, height: 15, borderRadius: "50%", background: GREEN, border: "1.5px solid rgba(0,0,0,0.18)", flexShrink: 0 }} />
          </span>
        ))}
      </div>

      <div style={{ textAlign: "center", color: "var(--muted)", fontSize: 12, margin: "10px 0" }}>
        ↓ אותו צבע = אותו סרגל ↓
      </div>

      {/* the single ruler they collapse into */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 8px", background: "var(--white)", borderRadius: 8, border: "1px solid var(--sand)", flexWrap: "wrap" }}>
        <span style={{ flex: 1, minWidth: 80, fontWeight: 600, color: "#2E7D32", textAlign: "right" }}>ישר</span>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          <span style={{ width: 16, height: 16, borderRadius: "50%", background: GREEN, border: "1.5px solid rgba(0,0,0,0.18)", flexShrink: 0 }} />
          <RulerTicks n={4} color={GREEN} />
          <ScribbledCount n={4} color={GREEN} />
        </div>
        <span style={{ flex: 1, minWidth: 80, fontWeight: 600, color: "#C62828", textAlign: "left" }}>שקרן</span>
      </div>

      <p style={{ fontSize: 13, color: "var(--deep)", lineHeight: 1.75, marginTop: 14, marginBottom: 0 }}>
        ארבע תכונות שונות שכתבתם — "ישר", "אשת מצפון", "מדבר בגובה העיניים", "הוגן" — כולן ביטאו בעצם אותו דבר. נתתם להן אותו צבע, והן התכווצו לסרגל אחד: הסרגל שדרכו אתם מודדים יושר. זו, בקטן, תמונת המשקפיים שלכם.
      </p>
    </div>
  );
}


// Workshop writing frame ("מסגרת") for chapters 1-3 (and the ones that
// follow). Shapes are documented in src/data/frames.ts. All answers go
// through the same progress.fields map as the other chapters, so they
// sync across devices like everything else.
function FrameBox({ f, fields, setFields, save }: { f: Frame; fields: any; setFields: any; save: React.ReactNode }) {
  const val = (k: string) => fields[k] || "";
  const set = (k: string, v: string) => setFields((r: any) => ({ ...r, [k]: v }));
  const key = (i: number, c?: number) => `${f.id}_${i}${c !== undefined ? "_c" + c : ""}`;
  const ta = (k: string, rows = 2) => (
    <textarea className="ri" rows={rows} placeholder="כתבי כאן..." value={val(k)} onChange={(e) => set(k, e.target.value)} />
  );
  const line = (lab: string, i: number, rows = 2) => (
    <div key={i}>
      <label className="fl">{lab}</label>
      {ta(key(i), rows)}
    </div>
  );
  const scaleRow = (lab: string, i: number) => (
    <div key={i}>
      <label className="fl">{lab}</label>
      <div className="frscale">
        {Array.from({ length: 10 }, (_, n) => n + 1).map((n) => (
          <button type="button" key={n} className={val(key(i)) === String(n) ? "on" : ""} onClick={() => set(key(i), String(n))}>{n}</button>
        ))}
      </div>
    </div>
  );
  const twoColRow = (row: string, i: number) => {
    const [l, r] = row.split("|");
    return (
      <div key={i} className="fr2col">
        <div><label className="fl">{l}</label>{ta(key(i, 1))}</div>
        <div><label className="fl">{r}</label>{ta(key(i, 2))}</div>
      </div>
    );
  };
  let body: React.ReactNode;
  if (f.type === "scale") {
    body = f.fields.map(scaleRow);
  } else if (f.type === "scale2" || f.type === "twocol_named") {
    // Mixed frames: the row kind is read off the label, not the position.
    // "a|b" = two named columns; a label ending in ":" (or empty) = text
    // line; anything else ("כמה...", "איפה...") = 1-10 ruler. Chapters 1-3
    // were written positionally (field 0 = text) and satisfy the same
    // rule; chapters 4-12 mix the kinds freely (quality round, 2.11.2026).
    body = f.fields.map((lab, i) =>
      lab.includes("|") ? twoColRow(lab, i) : !lab || /:\s*$/.test(lab) ? line(lab, i) : scaleRow(lab, i),
    );
  } else if (f.type === "twocol") {
    body = f.fields.map(twoColRow);
  } else if (f.type === "choice5") {
    // first label = "א קהל · ב כביש · ..." → single-choice chips
    body = f.fields.map((lab, i) =>
      i === 0 ? (
        <div key={i} className="frchips">
          {lab.split(" · ").map((opt) => (
            <button type="button" key={opt} className={val(key(0)) === opt ? "on" : ""} onClick={() => set(key(0), val(key(0)) === opt ? "" : opt)}>{opt}</button>
          ))}
        </div>
      ) : (
        line(lab, i)
      ),
    );
  } else {
    body = f.fields.map((lab, i) =>
      lab ? line(lab, i, f.type === "oneline" || /שלוש שורות/.test(lab) ? 3 : 2) : null,
    );
  }
  return (
    <div className="frbox">
      <div className="frlabel">{f.type === "existing" ? `תוספת למסגרת ${f.n}` : `מסגרת ${f.n}`}</div>
      <div className="frtitle">{f.title}</div>
      {f.intro ? <div className="frintro">{f.intro}</div> : null}
      <div className="rg">{body}</div>
      {save}
    </div>
  );
}

export default function EIBook() {
  const [chapter, setChapter] = useState(() => {
    // /pick hands over the chosen chapter in the URL (?ch=N): the saved
    // position is unreliable at that moment because the pick page's own
    // scroll-persist timer can overwrite it 200ms after the click (8.10).
    try {
      const q = Number(new URLSearchParams(window.location.search).get("ch"));
      if (q >= 1 && q <= 12) return q;
    } catch {}
    const saved = loadSavedPos();
    return typeof saved.chapter === "number" &&
      saved.chapter >= 1 &&
      saved.chapter <= 12
      ? saved.chapter
      : 1;
  });
  const [mode, setMode] = useState("read");
  const [modeChosen, setModeChosen] = useState(false); // first click colours the buttons
  const { user, loading: authLoading } = useAuth();

  // Acquisition attribution (20.8): remember which ad brought this visitor
  // (utm_* / fbclid in the landing URL), and once they sign in — attach it
  // to their profile so the morning report can count signups per source.
  useEffect(() => {
    captureAttribution();
  }, []);
  useEffect(() => {
    if (user?.id) flushAttribution();
  }, [user?.id]);
  const { hasAccess, loading: accessLoading } = useAccess();
  // Dubbing-QA: the 🚩 flag button in listen mode renders for admins
  // only (MVP — the audio_flags table itself already supports any
  // authenticated user if we ever open it to readers).
  const { isAdmin } = useAdmin();
  // Paywall: chapter 1 free; 2–12 require an authenticated user with an
  // active license (purchase or reviewer invitation). While auth/access are
  // resolving we don't show the paywall — avoids a flash for paid users.
  // Chapter pick (8.10): a reader who picked this chapter on /pick reads it
  // without a license (server-side gate in get_chapter_content, 0048).
  const { picks, loading: picksLoading, canRead } = usePicks();
  const chapterGranted = !hasAccess && picks.chapters.includes(chapter);
  const showPaywall =
    chapter > 1 && !authLoading && !accessLoading && !picksLoading && !hasAccess && !canRead(chapter);

  // Offer strip (funnel leak #1): on the free chapter nothing tells the
  // visitor there is a full paid workshop. A dismissible floating ribbon
  // links to the offer screen (= chapter 2's PaywallGate). Session-scoped
  // dismissal: returning visitors see it again, mid-session it stays away.
  const [offerStripHidden, setOfferStripHidden] = useState(() => {
    try {
      return sessionStorage.getItem("ei_offer_strip_hidden") === "1";
    } catch {
      return false;
    }
  });
  const dismissOfferStrip = () => {
    setOfferStripHidden(true);
    try {
      sessionStorage.setItem("ei_offer_strip_hidden", "1");
    } catch {}
  };
  const showOfferStrip =
    chapter === 1 &&
    !authLoading &&
    !accessLoading &&
    !hasAccess &&
    !offerStripHidden;

  // Reviewer invitation: when the URL is /r/<token>, show the invitation
  // landing page instead of the book. After the user logs in via the magic
  // link, claim_invitation links the reviewer license to their account.
  const reviewerToken =
    typeof window !== "undefined"
      ? window.location.pathname.match(REVIEWER_TOKEN_PATH)?.[1] ?? null
      : null;

  // Admin route — completely separate UI; the AdminPage handles its own
  // gating (is_admin check) so we don't need to duplicate that logic here.
  const isAdminPath =
    typeof window !== "undefined" &&
    ADMIN_PATH.test(window.location.pathname);

  const isPaymentSuccessPath =
    typeof window !== "undefined" &&
    PAYMENT_SUCCESS_PATH.test(window.location.pathname);

  const isContactPath =
    typeof window !== "undefined" &&
    CONTACT_PATH.test(window.location.pathname);

  const isWelcomePath =
    typeof window !== "undefined" &&
    WELCOME_PATH.test(window.location.pathname);

  const isDecisionPath =
    typeof window !== "undefined" &&
    DECISION_PATH.test(window.location.pathname);

  const isGiftPath =
    typeof window !== "undefined" &&
    GIFT_PATH.test(window.location.pathname);

  const isPickPath =
    typeof window !== "undefined" &&
    PICK_PATH.test(window.location.pathname);

  const isAboutPath =
    typeof window !== "undefined" &&
    ABOUT_PATH.test(window.location.pathname);

  const chatToken =
    typeof window !== "undefined"
      ? window.location.pathname.match(CHAT_PATH)?.[1] ?? null
      : null;

  const isUnknownPath =
    typeof window !== "undefined" &&
    !isKnownSpaPath(window.location.pathname);

  useEffect(() => {
    if (authLoading || !user) return;
    let stored: string | null = null;
    try {
      stored = window.localStorage.getItem(PENDING_TOKEN_KEY);
    } catch {}
    const tokenToClaim = reviewerToken || stored;
    if (!tokenToClaim) return;
    let cancelled = false;
    (async () => {
      await supabase.rpc("claim_invitation", { _token: tokenToClaim });
      if (cancelled) return;
      try {
        window.localStorage.removeItem(PENDING_TOKEN_KEY);
      } catch {}
      if (reviewerToken) {
        window.history.replaceState({}, "", "/");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, authLoading, reviewerToken]);

  // First-time-sign-in welcome redirect.
  //
  // When an authenticated reader lands on the home route for the first
  // time (no `ei_welcome_dismissed_v1` flag in localStorage), bounce them
  // to /welcome so they get the onboarding before opening the book.
  // The page sets the flag itself when they click its CTA, so the
  // redirect only fires once per device. The "?" button in the slim
  // header keeps the page reachable anytime.
  //
  // Guarded against:
  //   - SSR (window check)
  //   - Non-home routes (admin, payment-success, contact, /r/<token>, /welcome itself)
  //   - Anonymous visitors (they don't sign in, so no localStorage entry
  //     could exist for them anyway — they hit the public preview as before)
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (authLoading || !user) return;
    if (!HOME_PATH.test(window.location.pathname)) return;
    try {
      if (window.localStorage.getItem(WELCOME_DISMISSED_KEY)) return;
      // Set the flag NOW so we can't get into a redirect loop if the user
      // immediately hits Back after landing on /welcome.
      window.localStorage.setItem(WELCOME_DISMISSED_KEY, "1");
    } catch {
      // private mode etc. — fall through, redirect anyway; the worst case
      // is that /welcome shows again next session, which isn't broken.
    }
    window.location.replace("/welcome");
  }, [user, authLoading]);
  const [fields, setFields] = useState({});
  const [freeText, setFreeText] = useState("");
  const [pracLog, setPracLog] = useState("");
  const [saveBanner, setSaveBanner] = useState(false);
  // Cross-device sync: pulls cloud progress on mount (merging with the
  // localStorage-cached values) and auto-uploads edits 1.5 s after the
  // last keystroke. Pre-auth or free-preview readers stay local-only.
  // Also tracks per-chapter audio playback positions for Spotify-style
  // cross-device resume — see audioPositions / setAudioPosition usage
  // on the AudioPlayer below.
  const {
    pushNow: pushProgressToCloud,
    audioPositions,
    setAudioPosition,
    touchChapter,
  } = useCloudProgress({
    user,
    hasAccess,
    authLoading,
    accessLoading,
    fields,
    setFields,
    freeText,
    setFreeText,
    pracLog,
    setPracLog,
  });

  // Mark each chapter the reader actually lands on. Without this, the
  // progress table only logs writers and audio listeners — a reader
  // who scrolls through a chapter without writing notes or pressing
  // play never gets a row, and the admin Users table understates
  // engagement. The hook itself throttles to one bump per chapter per
  // session, so even rapid chapter-switching won't spam Supabase.
  //
  // 3 s settle delay filters out fly-through clicks (e.g. opening the
  // ToC and tapping chapter 7 → chapter 8 → chapter 9 in quick
  // succession should NOT log all three as "read" — only the one the
  // reader actually stops on).
  useEffect(() => {
    if (!user || !(hasAccess || chapterGranted)) return;
    if (typeof chapter !== "number" || chapter < 1 || chapter > 12) return;
    const id = window.setTimeout(() => {
      touchChapter(chapter);
    }, 3000);
    return () => window.clearTimeout(id);
  }, [chapter, user, hasAccess, chapterGranted, touchChapter]);

  // Meta Pixel funnel step: which chapters does a visitor actually open?
  // Anonymous visitors included — this is the "did the ad clicker read
  // anything" signal that the campaign analysis was missing.
  useEffect(() => {
    trackChapterView(chapter);
  }, [chapter]);

  // ---- Listen-experience state ----
  // Declared as a single block BEFORE any of the listen-mode effects
  // below so the effects can close over these values without hitting a
  // Temporal Dead Zone error at module init. Previously these lived
  // further down in the function and the effects were hoisted above
  // their declarations — JS lets that parse cleanly but throws
  // "Cannot access 'currentAudioTime' before initialization" the
  // moment EIBook actually renders.

  // When the reader picks a TOC subsection (e.g. "6.4") AND later flips
  // to listen mode, the audio should seek to that section's start, not
  // restart from chapter 0. The timestamps live in
  // public/chapter-N-sections.json (built by scripts/align_chapter_sections.py),
  // and AudioPlayer reads this prop to override its localStorage/cloud
  // resume on first play. Cleared automatically when chapter changes
  // away from the target, or after the player consumes it.
  const [pendingAudioSection, setPendingAudioSection] = useState<{
    chapter: number;
    idx: number;
  } | null>(null);
  // Live playback time from AudioPlayer (in seconds). Drives the
  // auto-scroll effect below — when the time crosses into a new
  // section, the page scrolls smoothly to that section's heading.
  const [currentAudioTime, setCurrentAudioTime] = useState(0);

  // ---- Dubbing-QA flag button (🚩) ----
  // One tap while listening files a row in audio_flags with the chapter
  // and the exact playback second; the offline tooling maps that second
  // to the transcript words, so the tap alone identifies the word. An
  // optional note field opens after the tap for "sounds like X" hints.
  const [lastAudioFlag, setLastAudioFlag] = useState(null); // {id, sec}
  const [audioFlagNote, setAudioFlagNote] = useState("");
  const [audioFlagBusy, setAudioFlagBusy] = useState(false);
  const [audioFlagError, setAudioFlagError] = useState(null);

  const fileAudioFlag = useCallback(async () => {
    if (!user || audioFlagBusy) return;
    setAudioFlagBusy(true);
    setAudioFlagError(null);
    const sec = Math.round(currentAudioTime * 100) / 100;
    const { data, error } = await supabase
      .from("audio_flags")
      .insert({ user_id: user.id, chapter, audio_sec: sec })
      .select("id")
      .single();
    setAudioFlagBusy(false);
    if (error) {
      setAudioFlagError("שגיאה בשמירת הדגל - נסה שוב");
      return;
    }
    setAudioFlagNote("");
    setLastAudioFlag({ id: data.id, sec });
  }, [user, audioFlagBusy, currentAudioTime, chapter]);

  const saveAudioFlagNote = useCallback(async () => {
    if (!lastAudioFlag) return;
    if (audioFlagNote.trim()) {
      await supabase
        .from("audio_flags")
        .update({ note: audioFlagNote.trim() })
        .eq("id", lastAudioFlag.id);
    }
    setLastAudioFlag(null);
    setAudioFlagNote("");
  }, [lastAudioFlag, audioFlagNote]);

  // The confirmation panel folds away on its own so it never blocks
  // continued listening (the flag itself is already saved).
  useEffect(() => {
    if (!lastAudioFlag) return;
    const id = window.setTimeout(() => setLastAudioFlag(null), 20000);
    return () => window.clearTimeout(id);
  }, [lastAudioFlag]);
  // Auto-scroll preference. Default ON per the listening spec.
  // Reader can toggle off via the player's "🔒 השאר במקום" pill.
  const [autoScrollEnabled, setAutoScrollEnabledState] = useState(() => {
    try {
      const raw = window.localStorage.getItem("ei_audio_auto_scroll");
      return raw === null ? true : raw === "1";
    } catch {
      return true;
    }
  });
  const setAutoScrollEnabled = (v: boolean) => {
    setAutoScrollEnabledState(v);
    try {
      window.localStorage.setItem("ei_audio_auto_scroll", v ? "1" : "0");
    } catch {}
  };
  // First-listen safety modal — shown once per device on the reader's
  // very first entry into listen mode. Initial state checks
  // localStorage so we don't even bother mounting the modal for
  // returning listeners. setShowDrivingNotice(true) fires once at the
  // moment mode flips from "read" → "listen" if the flag is unset.
  const [showDrivingNotice, setShowDrivingNotice] = useState(false);
  // Per-chapter section-timestamp JSON. Cached so we don't re-fetch on
  // every render. Keyed by chapter number. Mutated directly (refs don't
  // trigger renders, and we don't need a render — the auto-scroll
  // effect reads it directly).
  const sectionsByChapterRef = useRef<Map<number, any>>(new Map());
  // Per-chapter exercise-timestamp JSON (Approach D pilot — chapter 6
  // only for now). Loaded on demand, mirrors the sections-JSON pattern.
  const exercisesByChapterRef = useRef<Map<number, any>>(new Map());
  // The exercise the AudioPlayer just paused at, if any. Drives the
  // "🎯 כאן יש תרגיל למילוי" overlay. Cleared when the reader hits
  // "המשך האזנה" or navigates away.
  const [activeExercise, setActiveExercise] = useState<{
    chapter: number;
    startSec: number;
    label: string;
  } | null>(null);
  // External play/pause/restart command for AudioPlayer. Token bumps on
  // every send so duplicate values don't fire twice. "restart" seeks the
  // audio to 0:00 keeping the play/pause state — used by the bottom
  // "חזרה להתחלה" button in listen mode (Shlomo, round 7: scrolling to
  // the top without moving the voice just made the auto-scroll snap the
  // page right back).
  const [audioCommand, setAudioCommand] = useState<
    { action: "play" | "pause" | "restart"; token: number } | null
  >(null);
  const audioCommandTokenRef = useRef(0);
  const sendAudioCommand = (action: "play" | "pause" | "restart") => {
    audioCommandTokenRef.current += 1;
    setAudioCommand({ action, token: audioCommandTokenRef.current });
  };
  // The startSec of the last exercise we paused at. Used to avoid
  // re-pausing immediately when the reader resumes playback at the
  // same timestamp.
  const lastExercisePauseRef = useRef<number | null>(null);
  // ── Smooth pace-following auto-scroll engine (#2 option A) ──────────
  // Instead of jumping to the active section, we ease the page toward
  // where the narration *will* be when the tween finishes, at speech
  // pace. The anchor model is a sorted list of { t, el }: section
  // start-times (the accurate backbone, from chapter-N-sections.json)
  // subdivided across each section's paragraphs by text length, so the
  // scroll slows for dense passages. Built lazily per chapter from the
  // live DOM; element refs (not Y values) are stored so layout shifts
  // and resizes don't invalidate it.
  const scrollAnchorsRef = useRef<Array<{ t: number; el: HTMLElement }>>([]);
  const scrollAnchorChapterRef = useRef<number | null>(null);
  // Active rAF tween id, so a new tick cancels the previous animation.
  const scrollRafRef = useRef<number | null>(null);
  // performance.now() until which auto-scroll yields to the reader after
  // they scroll by hand (wheel / touch / arrows). Prevents tug-of-war.
  const scrollSuppressUntilRef = useRef<number>(0);
  // Set when the reader manually scrolls away while listening (desktop
  // wheel / nav keys). While held, auto-scroll stays off until the
  // narration's target position catches up to wherever the reader is —
  // so reading ahead is respected instead of being yanked back after 2s
  // (Shlomo, round-15: "אני מקדם לאזור הקריאה והוא חוזר"). Cleared when
  // the target reaches the reader or after a 60s cap.
  const manualHoldSinceRef = useRef<number | null>(null);

  // First-listen driving notice trigger. Fires exactly when mode flips
  // to "listen" and the reader hasn't dismissed the notice before.
  // The DrivingNotice component itself marks the localStorage flag on
  // mount, so closing the tab without clicking "אישור" still counts as
  // "seen" — we don't want to nag readers.
  useEffect(() => {
    if (mode !== "listen") return;
    if (isDrivingNoticeDismissed()) return;
    setShowDrivingNotice(true);
  }, [mode]);

  // Fetch chapter-N-sections.json once per chapter when entering listen
  // mode. The JSON is small (~5 KB) and cached on disk by the SW; this
  // effect just hydrates the in-memory map so the auto-scroll effect
  // below can read it synchronously.
  useEffect(() => {
    if (mode !== "listen") return;
    if (typeof chapter !== "number" || chapter < 1 || chapter > 12) return;
    if (sectionsByChapterRef.current.has(chapter)) return;
    let cancelled = false;
    fetch(`/chapter-${chapter}-sections.json`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        sectionsByChapterRef.current.set(chapter, data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [mode, chapter]);

  // Fetch chapter-N-exercises.json (Approach D pilot — currently only
  // chapter 6 ships one). When the audio crosses a structured-exercise
  // startSec, App.tsx pauses the player and shows the "כאן יש תרגיל"
  // overlay. Other chapters get no exercises file → effect short
  // circuits and listen mode plays straight through.
  useEffect(() => {
    if (mode !== "listen") return;
    if (typeof chapter !== "number" || chapter < 1 || chapter > 12) return;
    if (exercisesByChapterRef.current.has(chapter)) return;
    let cancelled = false;
    fetch(`/chapter-${chapter}-exercises.json`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled) return;
        // Store the result even when null so we don't re-fetch a
        // missing file every render.
        exercisesByChapterRef.current.set(chapter, data || null);
      })
      .catch(() => {
        if (!cancelled) exercisesByChapterRef.current.set(chapter, null);
      });
    return () => {
      cancelled = true;
    };
  }, [mode, chapter]);

  // Detect when the audio crosses a structured-exercise marker. The
  // AudioPlayer fires onTimeUpdate every ~2 s during play; on each
  // tick we check the cached exercises list and, if the latest tick
  // pushed us past a marker we haven't paused for, pause the audio
  // and surface the overlay.
  useEffect(() => {
    if (mode !== "listen") return;
    if (activeExercise) return; // already paused on an exercise
    const data = exercisesByChapterRef.current.get(chapter);
    if (!data || !Array.isArray(data.exercises)) return;
    for (const ex of data.exercises) {
      if (ex?.type !== "structured") continue;
      const start = ex?.startSec;
      if (typeof start !== "number" || !Number.isFinite(start)) continue;
      // Only fire on the FIRST tick where currentAudioTime crosses the
      // marker. The pause-then-resume cycle records the pause time in
      // lastExercisePauseRef so we don't re-pause on every tick after.
      if (
        currentAudioTime >= start &&
        currentAudioTime < start + 8 && // window so we don't fire on far-future seeks
        lastExercisePauseRef.current !== start
      ) {
        lastExercisePauseRef.current = start;
        setActiveExercise({
          chapter,
          startSec: start,
          label: ex.label || "תרגיל",
        });
        sendAudioCommand("pause");
        return;
      }
    }
  }, [currentAudioTime, chapter, mode, activeExercise]);

  // When the reader leaves listen mode OR switches chapters, drop
  // the overlay AND reset the "we paused here" memory so they can
  // re-encounter the same exercise next time around.
  useEffect(() => {
    setActiveExercise(null);
    lastExercisePauseRef.current = null;
  }, [mode, chapter]);

  // Build (or rebuild) the paragraph-level scroll anchor model for a
  // chapter from the live DOM + cached section timestamps. Returns true
  // on success. We store element refs (not Y values) so the model
  // survives image loads, widget mounts and window resizes — Y is read
  // fresh at tween time.
  const buildScrollAnchors = useCallback((ch: number): boolean => {
    const data = sectionsByChapterRef.current.get(ch);
    if (!data || !Array.isArray(data.sections)) return false;
    const wrapper =
      document.querySelector(`.ch-numbered-${ch}`) || document.body;
    const scs = Array.from(wrapper.querySelectorAll<HTMLElement>(".sc"));
    if (!scs.length) return false;
    // Sections with a real startSec, in document order. null / low-
    // confidence ones are skipped as time anchors; their paragraphs are
    // still covered by the span between the surrounding known sections.
    const known = data.sections
      .filter(
        (s: any) =>
          typeof s?.startSec === "number" && Number.isFinite(s.startSec),
      )
      .map((s: any) => ({ idx: s.idx as number, t: s.startSec as number }))
      .sort((a: any, b: any) => a.idx - b.idx);
    if (!known.length) return false;

    // Collect narrated elements across the WHOLE wrapper, not only
    // inside .sc sections. Round-7 listen (Shlomo) exposed two blind
    // spots of the per-.sc model: the chapter OPENING before the first
    // section marker (ch7 — the prose scrolled off-screen during the
    // first minute) and wrapper-level closing boxes AFTER the last .sc
    // (ch6 — the summary box + lion bridge line never scrolled into
    // view). Tables and widget anchors participate too (they're
    // narrated as cell text / verbal cues), and styled text boxes —
    // plain divs with no p/li inside — are harvested as single anchors.
    // figure/img included since round 11: images aren't narrated but eat
    // real vertical space — with zero anchor weight the tween sprinted
    // across them and the text vanished behind the player bar (Shlomo:
    // chapter-1 opening, right at the hero image).
    // Round 11b: box titles (.ht), process-card titles (.pt) and
    // descriptions (.pd) added — the "חמש הצצות" section is five styled
    // boxes whose headings had zero anchor weight, so the scroll crept
    // ahead across them.
    const SELECTOR =
      "p, li, h3, .stitle, .sdesc, .ht, .pt, .pd, table, figure, [data-widget-anchor]";
    // Workshop writing frames (chN_fK anchors, 26-29.10) carry
    // data-no-tts in the chapter HTML: no spoken cue exists for them, so
    // they must not get the 250-char "narrated widget" weight below —
    // that parked the scroll on the frame while the voice was already
    // into the next section.
    const matched = Array.from(
      wrapper.querySelectorAll<HTMLElement>(SELECTOR),
    );
    const extras: HTMLElement[] = [];
    const harvest = (el: HTMLElement) => {
      for (const child of Array.from(el.children) as HTMLElement[]) {
        if (child.closest(".ap2-sticky, .rhap_container, button")) continue;
        // Not prose: the fixed site header (its inline <style> text was
        // harvested as a ~2000-char "paragraph" and swallowed the first
        // ~37s of every chapter — the opening section's real paragraphs
        // were squeezed into the last second before pin 1; found 30.10
        // on the ch4 listen check), plus style/script/noscript nodes.
        if (child.matches("style, script, noscript, .slim-header")) continue;
        if (child.matches(SELECTOR)) continue;
        if (child.querySelector(SELECTOR)) {
          harvest(child);
          continue;
        }
        if ((child.textContent || "").trim().length >= 40) extras.push(child);
      }
    };
    harvest(wrapper as HTMLElement);
    const all = matched
      .concat(extras)
      .filter(
        (el) =>
          !el.closest(".ap2-sticky, .rhap_container") &&
          // Workshop blocks (exercise boxes, bridges, images added since
          // 26.10) are not narrated: keep them out of the scroll model.
          !el.closest("[data-no-tts]") &&
          (el.hasAttribute("data-widget-anchor") ||
            (el.textContent || "").trim().length > 0),
      );
    // Nested matches (a <p> inside a table cell) would double-count —
    // keep only outermost, then restore document order.
    const outer = all.filter(
      (el) => !all.some((other) => other !== el && other.contains(el)),
    );
    outer.sort((a, b) =>
      a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING
        ? -1
        : 1,
    );
    if (!outer.length) return false;

    // Widget anchors: 250 ≈ the spoken cue of a real widget (ch1's
    // positive_actions / reflection_form / practice_log are announced in
    // the recording). Anchors that render nothing for this reader
    // (audio_s1-5 retired, save_for_later / decision_moment only for
    // visitors, and anything else that is empty) have no cue and no
    // height: weight 0, or the scroll parked on thin air for ~15s at
    // the end of every span (ch1 opening, 29.10).
    const lenOf = (el: HTMLElement) =>
      el.hasAttribute("data-widget-anchor")
        ? el.getBoundingClientRect().height > 40
          ? 250
          : 0
        : el.tagName === "FIGURE"
          ? 60 // unspoken image — a few seconds of dwell to glide across
          : Math.max(1, (el.textContent || "").trim().length);

    // TIME PINS: elements with authoritative timestamps. Two sources:
    // (1) known-section headers (scs[idx] ↔ startSec), and — since
    // round 13 — (2) SUBSECTION headings (h3) aligned by the sections
    // script into data.subs. Long chapters (7/9/12) had half-hour spans
    // between .sc sections, so char-interpolation drift accumulated and
    // the scroll ran ahead or lagged (Shlomo's desktop report); h3 pins
    // cap the error at subsection length.
    const normTxt = (s: string) => (s || "").replace(/\s+/g, " ").trim();
    const pins: Array<{ el: HTMLElement; t: number }> = [];
    for (const kn of known)
      if (scs[kn.idx]) pins.push({ el: scs[kn.idx], t: kn.t });
    const subsData: any[] = Array.isArray(data.subs) ? data.subs : [];
    for (const sub of subsData) {
      if (typeof sub?.startSec !== "number" || !Number.isFinite(sub.startSec))
        continue;
      const probe = normTxt(sub.text).slice(0, 25);
      if (probe.length < 8) continue;
      const el = outer.find((e) => normTxt(e.textContent || "").startsWith(probe));
      if (el) pins.push({ el, t: sub.startSec });
    }
    pins.sort((a, b) =>
      a.el === b.el
        ? a.t - b.t
        : a.el.compareDocumentPosition(b.el) & Node.DOCUMENT_POSITION_FOLLOWING
          ? -1
          : 1,
    );

    // Assign each element to the span of the last pin at-or-before it;
    // elements before the first pin form the chapter-opening span
    // [0 → pins[0].t].
    const spanOf = (el: HTMLElement): number => {
      let s = -1;
      for (let k = 0; k < pins.length; k++) {
        const b = pins[k].el;
        if (
          b === el ||
          b.contains(el) ||
          b.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING
        )
          s = k;
        else break;
      }
      return s;
    };
    const spans: HTMLElement[][] = pins.map(() => []);
    const opening: HTMLElement[] = [];
    for (const el of outer) {
      const s = spanOf(el);
      if (s < 0) opening.push(el);
      else spans[s].push(el);
    }

    // Reading pace (chars/sec) across the pinned range — only used to
    // extrapolate the tail after the last pin. Floor avoids an
    // absurdly long tail if the span is tiny.
    let spanChars = 0;
    for (const seg of spans)
      spanChars += seg.reduce((n, el) => n + lenOf(el), 0);
    const spanSecs = Math.max(1, pins[pins.length - 1].t - pins[0].t);
    const charsPerSec = Math.max(6, spanChars / spanSecs);

    const anchors: Array<{ t: number; el: HTMLElement }> = [];
    const distribute = (
      els: HTMLElement[],
      tStart: number,
      tEnd: number,
    ) => {
      const lens = els.map(lenOf);
      const total = Math.max(1, lens.reduce((x, y) => x + y, 0));
      let cum = 0;
      for (let p = 0; p < els.length; p++) {
        anchors.push({
          t: tStart + (tEnd - tStart) * (cum / total),
          el: els[p],
        });
        cum += lens[p];
      }
    };
    if (opening.length) {
      // Narration before the first pin (chapter title, intro story).
      // If the first pin sits at ~0s there's nothing to spread.
      if (pins[0].t > 2) distribute(opening, 0, pins[0].t);
      else spans[0] = opening.concat(spans[0]);
    }
    for (let k = 0; k < pins.length; k++) {
      const a = pins[k];
      const b = pins[k + 1];
      const els = spans[k];
      if (!els.length) {
        anchors.push({ t: a.t, el: a.el });
        continue;
      }
      const total = els.reduce((n, el) => n + lenOf(el), 0);
      // Distribute this span's time across its elements by text length,
      // so the scroll lingers on dense passages.
      const tEnd = b ? b.t : a.t + total / charsPerSec;
      distribute(els, a.t, tEnd);
    }
    anchors.sort((x, y) => x.t - y.t);
    // Dev-only peek for listen-mode QA (window.__scrollAnchors in the
    // local server; stripped from production builds).
    if (process.env.NODE_ENV === "development")
      (window as any).__scrollAnchors = { pins, anchors };
    scrollAnchorsRef.current = anchors;
    scrollAnchorChapterRef.current = ch;
    return anchors.length > 0;
  }, []);

  // Absolute page-Y the viewport should scroll to so the text narrated at
  // time `t` sits ~4-5 text lines BELOW the pinned stack (header + mode
  // bar + player). Shlomo, 2026-07-13: readers follow the text while
  // listening; the narrated line must stay comfortably visible — text
  // lagging below is fine, text vanishing above the player is not.
  // Measured live because the pinned stack's height differs between
  // mobile/desktop and changed when the bars became sticky (PR #181).
  const scrollTargetForTime = useCallback((t: number): number | null => {
    const A = scrollAnchorsRef.current;
    if (!A.length) return null;
    const LINES_BELOW = 170; // ≈5-6 lines of chapter text
    const player = document.querySelector(".ap2-sticky");
    const pinnedBottom = player
      ? Math.max(0, player.getBoundingClientRect().bottom)
      : 230;
    const OFFSET = pinnedBottom + LINES_BELOW;
    const absTop = (el: HTMLElement) =>
      el.getBoundingClientRect().top + window.scrollY;
    if (t <= A[0].t) return absTop(A[0].el) - OFFSET;
    const last = A[A.length - 1];
    if (t >= last.t) return absTop(last.el) - OFFSET;
    let i = 0;
    while (i < A.length - 1 && A[i + 1].t <= t) i++; // few-hundred entries, runs ~/2s
    const a = A[i];
    const b = A[i + 1] || a;
    const span = b.t - a.t || 1;
    const frac = Math.min(1, Math.max(0, (t - a.t) / span));
    // Glide only through the element being narrated, never across
    // whatever sits between it and the next narrated element. Since the
    // workshop layer (26-29.10: image + exercise box + writing frame +
    // bridge, all data-no-tts, ~800-1500px) the gap after a section's
    // last paragraph is huge; interpolating straight to the next anchor
    // dragged the spoken paragraph off the top of the screen while the
    // voice was still reading it (Shlomo, 29.10: "אין הלימה בין הקול
    // לטקסט"). The jump to the next element happens when its anchor time
    // arrives (tweenScrollTo eases far jumps).
    // +24 ≈ one paragraph gap, so ordinary prose flows exactly as before.
    const aTop = absTop(a.el);
    const aEnd = aTop + a.el.getBoundingClientRect().height + 24;
    const end = Math.min(absTop(b.el), aEnd);
    return aTop + Math.max(0, end - aTop) * frac - OFFSET;
  }, []);

  // Ease the window scroll to targetY. dur=2000ms (linear) keeps pace
  // with playback between the ~2s ticks; a far jump (after a seek) uses a
  // quick ease-out instead of a slow 2s crawl.
  const tweenScrollTo = useCallback((targetY: number) => {
    if (scrollRafRef.current != null) {
      cancelAnimationFrame(scrollRafRef.current);
      scrollRafRef.current = null;
    }
    const maxY = Math.max(
      0,
      document.documentElement.scrollHeight - window.innerHeight,
    );
    const endY = Math.min(maxY, Math.max(0, targetY));
    const startY = window.scrollY;
    const dist = endY - startY;
    if (Math.abs(dist) < 4) return; // already there
    const far = Math.abs(dist) > window.innerHeight * 1.6;
    // 1200ms (was 2000): a full-tick tween finished only as the next
    // tick arrived, so the page ran a beat behind the voice throughout.
    const dur = far ? 450 : 1200;
    const t0 = performance.now();
    const step = (now: number) => {
      // Reader grabbed the page mid-tween → abort and yield.
      if (performance.now() < scrollSuppressUntilRef.current) {
        scrollRafRef.current = null;
        return;
      }
      const e = Math.min(1, (now - t0) / dur);
      const k = far ? 1 - (1 - e) * (1 - e) : e; // ease-out jump, linear pace
      window.scrollTo(0, startY + dist * k);
      if (e < 1) scrollRafRef.current = requestAnimationFrame(step);
      else scrollRafRef.current = null;
    };
    scrollRafRef.current = requestAnimationFrame(step);
  }, []);

  // Drive the smooth scroll on each ~2s playback tick. We deliberately
  // TRAIL the voice by a few seconds (Shlomo, 2026-07-15: the scroll
  // kept outrunning the narration and the spoken line vanished behind
  // the pinned bars; per his rule — text lagging below is fine, text
  // escaping above is not). The char-based anchor interpolation is an
  // estimate that can run ahead of the actual voice; the trailing bias
  // absorbs that error in the harmless direction. Reader can stop the
  // scroll any time via the player's "🔒 השאר במקום" toggle.
  useEffect(() => {
    if (mode !== "listen" || !autoScrollEnabled) return;
    if (typeof chapter !== "number") return;
    if (performance.now() < scrollSuppressUntilRef.current) return;
    if (
      scrollAnchorChapterRef.current !== chapter ||
      !scrollAnchorsRef.current.length
    ) {
      if (!buildScrollAnchors(chapter)) return; // DOM / sections not ready yet
    }
    // Trailing bias trimmed 4s → 1.5s in round 15c (Shlomo: "the page
    // ends, the narration continues, the text vanishes at the bottom" —
    // the 4s trail plus the 2s tick plus the 2s tween added up to ~8s
    // of visible lag). 1.5s still absorbs small model error in the
    // harmless direction (text a touch below, never escaping above).
    const target = scrollTargetForTime(currentAudioTime - 1.5);
    if (target == null) return;
    // Catch-up hold: after a deliberate manual scroll, resume only when
    // the narration target arrives near the reader's position (or after
    // 60s — the 🔒 toggle is the way to stop for good).
    if (manualHoldSinceRef.current != null) {
      const near =
        Math.abs(target - window.scrollY) < window.innerHeight * 0.6;
      const expired = performance.now() - manualHoldSinceRef.current > 60000;
      if (!near && !expired) return;
      manualHoldSinceRef.current = null;
    }
    tweenScrollTo(target);
  }, [
    currentAudioTime,
    chapter,
    mode,
    autoScrollEnabled,
    buildScrollAnchors,
    scrollTargetForTime,
    tweenScrollTo,
  ]);

  // Yield to the reader: any manual scroll gesture suppresses auto-scroll
  // for a few seconds and cancels the in-flight tween. wheel / touchmove /
  // nav-keys are user-initiated; our own window.scrollTo does NOT fire
  // them, so this never trips on the animation itself.
  useEffect(() => {
    const yld = () => {
      // Short yield window: pause auto-scroll briefly after a manual
      // gesture, then resume on the next playback tick. 4s felt like the
      // scroll "stalled" (#8); 2s respects a quick adjustment without a
      // long dead zone. The 🔒 toggle remains the way to stop it for good.
      scrollSuppressUntilRef.current = performance.now() + 2000;
      // Deliberate scroll gesture → enter catch-up hold (see the tween
      // effect): auto-scroll waits for the voice to reach the reader.
      manualHoldSinceRef.current = performance.now();
      if (scrollRafRef.current != null) {
        cancelAnimationFrame(scrollRafRef.current);
        scrollRafRef.current = null;
      }
    };
    const keyYld = (e: KeyboardEvent) => {
      if (
        ["ArrowUp", "ArrowDown", "PageUp", "PageDown", "Home", "End", " "].includes(
          e.key,
        )
      )
        yld();
    };
    // Freeze the tween the instant a finger (or mouse button) lands
    // anywhere. During listen mode the page is animating almost
    // continuously, and on iOS a tap on a moving page mis-lands — the
    // pause button flashed but didn't pause until the reader nudged the
    // page (Shlomo, round-7: "3-4 clicks, moving the bar a bit lets it
    // stop"). Stopping the animation at touchstart makes the very first
    // tap land on a static page; the scroll resumes on the next
    // playback tick (~2s), so reading flow is unaffected.
    const freeze = () => {
      if (scrollRafRef.current != null) {
        cancelAnimationFrame(scrollRafRef.current);
        scrollRafRef.current = null;
      }
      scrollSuppressUntilRef.current = Math.max(
        scrollSuppressUntilRef.current,
        performance.now() + 700, // covers the iOS tap→click delay
      );
    };
    window.addEventListener("wheel", yld, { passive: true });
    window.addEventListener("touchmove", yld, { passive: true });
    window.addEventListener("keydown", keyYld);
    window.addEventListener("touchstart", freeze, {
      passive: true,
      capture: true,
    });
    window.addEventListener("pointerdown", freeze, {
      passive: true,
      capture: true,
    });
    return () => {
      window.removeEventListener("wheel", yld);
      window.removeEventListener("touchmove", yld);
      window.removeEventListener("keydown", keyYld);
      window.removeEventListener("touchstart", freeze, {
        capture: true,
      } as any);
      window.removeEventListener("pointerdown", freeze, {
        capture: true,
      } as any);
    };
  }, []);

  // Drop the anchor model + cancel any tween when chapter or mode
  // changes, so a stale model from the previous chapter is never used.
  useEffect(() => {
    scrollAnchorsRef.current = [];
    scrollAnchorChapterRef.current = null;
    manualHoldSinceRef.current = null;
    if (scrollRafRef.current != null) {
      cancelAnimationFrame(scrollRafRef.current);
      scrollRafRef.current = null;
    }
  }, [chapter, mode]);

  // Body class for the fixed-position AudioPlayer container — adds the
  // padding-top to .bc that keeps chapter prose from sliding under the
  // floating audio bar. Driven by `mode === "listen"` because that's
  // exactly when the AudioPlayer is mounted (see chapter prose JSX
  // below).
  useEffect(() => {
    const cls = "has-audio-player";
    if (mode === "listen" && chapter >= 1 && chapter <= 12) {
      document.body.classList.add(cls);
    } else {
      document.body.classList.remove(cls);
    }
    return () => {
      document.body.classList.remove(cls);
    };
  }, [mode, chapter]);

  // One-shot welcome banner shown after a successful purchase. The flag is
  // set by PaymentSuccess and consumed (cleared) on first render here.
  const [justPurchased, setJustPurchased] = useState(false);
  useEffect(() => {
    try {
      if (window.sessionStorage.getItem("just_purchased") === "1") {
        window.sessionStorage.removeItem("just_purchased");
        setJustPurchased(true);
      }
    } catch {}
  }, []);
  // Table-of-contents view: when true we replace the chapter content with the TOC.
  // prevChapter remembers where to return when the user presses "Back".
  // pendingScroll holds an instruction to scroll to a specific .sc within a chapter
  // after navigation (consumed by a useEffect below).
  const [tocOpen, setTocOpen] = useState(false);
  const [prevChapter, setPrevChapter] = useState(null);
  const [pendingScroll, setPendingScroll] = useState(null);
  // Slim sticky header — shows current chapter + a progress bar that follows
  // either the scroll position (in read mode) or the TTS playback (in listen mode).
  // `started` is false on chapter open so the big Hero is visible; flips true
  // the moment the reader picks "קריאה" or "האזנה".
  const [started, setStarted] = useState(() => !!loadSavedPos().started);
  const [scrollProgress, setScrollProgress] = useState(0);
  // The chapter-change reset must only fire on *actual* chapter changes —
  // not on the first mount, and not on React strict-mode's double-invocation
  // of effects in dev. We compare against the previously-seen chapter via
  // a ref (refs persist across strict-mode reinvocations).
  const prevChapterForResetRef = useRef(chapter);

  useEffect(() => {
    try {
      const s = JSON.parse(localStorage.getItem("ei_book_ch1") || "{}");
      if (s.fields) setFields(s.fields);
      if (s.freeText) setFreeText(s.freeText);
      if (s.pracLog) setPracLog(s.pracLog);
    } catch {}
  }, []);

  // Reset the per-chapter UI state whenever the reader switches chapters:
  // hide the slim-header progress and bring back the big Hero so the
  // next chapter has its own intro moment. Compares against
  // prevChapterForResetRef so it skips the first mount (and any
  // strict-mode re-invocation) — only real chapter changes reset.
  useEffect(() => {
    if (prevChapterForResetRef.current === chapter) return;
    prevChapterForResetRef.current = chapter;
    setStarted(false);
    setScrollProgress(0);
    // Snap to top of the new chapter. rAF waits for the post-render commit
    // (setStarted(false) above triggers a second render); instant scroll —
    // not smooth — because chapter change is a discrete jump and smooth
    // animation racing with layout shifts left readers landing mid-page.
    requestAnimationFrame(() =>
      window.scrollTo({ top: 0, left: 0, behavior: "auto" })
    );
  }, [chapter]);

  // One-time scroll restore on mount: if the previous session ended mid-page
  // we drop the reader back at that scroll position once layout has settled.
  useEffect(() => {
    // When a deep-link (#loc=) drives positioning, the pendingPara effect owns
    // the scroll — don't fight it.
    if (window.location.hash.includes("loc=")) return;
    const saved = loadSavedPos();
    if (typeof saved.scrollY !== "number" || saved.scrollY <= 0) return;
    // Same async-prose race: the chapter is fetched from book_content, so a
    // fixed 120ms delay restored against a still-short page and landed the
    // reader in the wrong place. Poll until the page is tall enough to reach
    // the saved offset (i.e. the prose has mounted), then restore.
    let cancelled = false;
    let timer: number | null = null;
    let attempts = 0;
    const MAX_ATTEMPTS = 50;
    const tryRestore = () => {
      if (cancelled) return;
      const reachable = document.documentElement.scrollHeight - window.innerHeight;
      if (reachable >= saved.scrollY - 4 || attempts >= MAX_ATTEMPTS) {
        window.scrollTo(0, Math.min(saved.scrollY, Math.max(0, reachable)));
        return;
      }
      attempts += 1;
      timer = window.setTimeout(tryRestore, 80);
    };
    timer = window.setTimeout(tryRestore, 100);
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, []);

  // Persist {chapter, started, scrollY} so a refresh keeps the reader's
  // place. Throttled on scroll (200ms) to avoid hammering localStorage.
  useEffect(() => {
    const persist = () => {
      try {
        localStorage.setItem(
          POS_KEY,
          JSON.stringify({ chapter, started, scrollY: window.scrollY })
        );
      } catch {}
    };
    persist();
    let t = null;
    const onScroll = () => {
      if (t) clearTimeout(t);
      t = setTimeout(persist, 200);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      if (t) clearTimeout(t);
      window.removeEventListener("scroll", onScroll);
    };
  }, [chapter, started]);

  // Track scroll position to drive the slim-header progress bar in read mode.
  useEffect(() => {
    const onScroll = () => {
      const max =
        document.documentElement.scrollHeight - window.innerHeight;
      const p = max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0;
      setScrollProgress(p);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [chapter, started]);
  const scrollToTop = () => {
    if (mode === "listen") {
      // Bring the VOICE back to the beginning too — otherwise the
      // auto-scroll tweens the page straight back to the narrated spot
      // and the button appears to "not work" (Shlomo, ch7). Play state
      // is preserved: playing keeps playing from 0:00, paused stays
      // paused at 0:00.
      sendAudioCommand("restart");
      lastExercisePauseRef.current = null; // exercises re-arm from the top
      // Kill any in-flight tween and hold auto-scroll briefly — the next
      // playback tick (t≈0) will already target the top on its own.
      if (scrollRafRef.current != null) {
        cancelAnimationFrame(scrollRafRef.current);
        scrollRafRef.current = null;
      }
      scrollSuppressUntilRef.current = performance.now() + 2500;
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ---- TOC helpers ----
  const openTOC = () => {
    setPrevChapter(chapter);
    setTocOpen(true);
    scrollToTop();
  };
  const closeTOC = () => {
    if (prevChapter !== null) setChapter(prevChapter);
    setTocOpen(false);
    // small delay so layout settles before we scroll
    setTimeout(scrollToTop, 50);
  };
  // Switch to listen mode and seek the audio to a specific section.
  // Called by the per-section "האזן מסעיף זה" buttons that are injected
  // into every .sc block (see useEffect below). The pendingAudioSection
  // state is what AudioPlayer reads to decide WHERE to start.
  //
  // Deliberately does NOT scroll: the AudioPlayer's container is now
  // position:fixed at the top of the viewport (see .ap2-sticky CSS),
  // so the reader stays exactly where they are in the section text
  // while the floating bar shows up above their current scroll position.
  // The body.has-audio-player class (managed by the effect below) bumps
  // .bc's padding-top to make room for the bar.
  //
  // Wrapped in useCallback so the injection effect below has a stable
  // identity and only re-runs when `chapter` actually changes.
  const listenFromSection = useCallback((idx) => {
    setPendingAudioSection({ chapter, idx });
    setMode("listen");
    setStarted(true);
  }, [chapter]);

  // Stable props for the (memoized) AudioPlayer — see the memo() in
  // AudioPlayer.tsx. These must keep a constant identity across the ~2s
  // currentAudioTime ticks, otherwise the memo can't skip the re-render
  // and the pause-button race (#7) returns.
  const handleCloudPositionChange = useCallback(
    (pos: number) => setAudioPosition(chapter, pos),
    [chapter, setAudioPosition],
  );
  // Freeze the cloud resume position per chapter. The player only needs it
  // on mount/chapter-change (to pick max(local, cloud)); but audioPositions
  // also updates from our OWN ~5s position saves during playback, and that
  // changing prop was the last thing re-rendering the (memoized) player
  // mid-play — the churn that made the pause button drop clicks. We capture
  // the chapter's cloud value once (and once more if it arrives async from
  // null), then ignore the self-writes that follow, so the player stays
  // perfectly still while playing.
  const cloudPosRef = useRef<{ ch: number; pos: number | null }>({
    ch: -1,
    pos: null,
  });
  {
    const live = audioPositions[chapter] ?? null;
    if (cloudPosRef.current.ch !== chapter) {
      cloudPosRef.current = { ch: chapter, pos: live };
    } else if (cloudPosRef.current.pos == null && live != null) {
      cloudPosRef.current = { ch: chapter, pos: live };
    }
  }
  const frozenCloudPos = cloudPosRef.current.pos;
  const handleSectionSeekConsumed = useCallback(
    () => setPendingAudioSection(null),
    [],
  );
  const audioTitleSlot = useMemo(
    () => (
      <button
        type="button"
        className={"auto-scroll-toggle " + (autoScrollEnabled ? "is-on" : "is-off")}
        onClick={() => setAutoScrollEnabled(!autoScrollEnabled)}
        aria-label={autoScrollEnabled ? "כיבוי גלילה אוטומטית" : "הפעלת גלילה אוטומטית"}
        title={autoScrollEnabled ? "כיבוי גלילה אוטומטית" : "הפעלת גלילה אוטומטית"}
      >
        {autoScrollEnabled ? "📜 גלילה אוטומטית" : "🔒 השאר במקום"}
      </button>
    ),
    [autoScrollEnabled],
  );

  // Navigate to chapter N, optionally scrolling to a specific .sc (by 0-based index)
  const goToChapter = (n, subIdx = null) => {
    setChapter(n);
    setTocOpen(false);
    if (subIdx === null) {
      setTimeout(scrollToTop, 50);
      // Picking a chapter (not a subsection) clears any pending audio
      // seek — the reader didn't ask to jump into the middle.
      setPendingAudioSection(null);
    } else {
      setPendingScroll({ chapter: n, idx: subIdx });
      // Remember the section the reader picked so that if they flip to
      // listen mode (now or later) we seek the audio there. AudioPlayer
      // ignores this when null and reverts to its normal resume logic.
      setPendingAudioSection({ chapter: n, idx: subIdx });
    }
  };

  // After chapter renders, scroll to the requested sub-section if any.
  // Chapter prose is fetched async from book_content, so the .sc blocks are
  // NOT in the DOM at a fixed short delay — a single 80ms shot lost the race
  // and fell back to the chapter top (bug #79). Instead POLL until the target
  // section actually mounts (up to ~4s), then scroll to it.
  useEffect(() => {
    if (!pendingScroll) return;
    if (tocOpen) return;
    if (pendingScroll.chapter !== chapter) return;
    let cancelled = false;
    let timer: number | null = null;
    let attempts = 0;
    const MAX_ATTEMPTS = 50; // 50 × 80ms ≈ 4s ceiling for a slow fetch
    const tryScroll = () => {
      if (cancelled) return;
      const wrapper = document.querySelector(`.ch-numbered-${chapter}`);
      const target = wrapper
        ? wrapper.querySelectorAll(".sc")[pendingScroll.idx]
        : document.querySelectorAll(".sc")[pendingScroll.idx];
      if (target) {
        const y = target.getBoundingClientRect().top + window.scrollY - 16;
        window.scrollTo({ top: y, behavior: "smooth" });
        setPendingScroll(null);
        return;
      }
      attempts += 1;
      if (attempts >= MAX_ATTEMPTS) {
        // Section never appeared (out-of-range index, or a very slow load) —
        // give up quietly. The chapter-change reset already left us at the
        // top, so no forced scroll needed.
        setPendingScroll(null);
        return;
      }
      timer = window.setTimeout(tryScroll, 80);
    };
    timer = window.setTimeout(tryScroll, 60);
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [chapter, tocOpen, pendingScroll]);

  // ── 📍 Paragraph locator ─────────────────────────────────────────────
  // A web book has no fixed pages (text reflows per device/font), so we
  // give each prose block a stable, device-independent reference. The 📍
  // button shows the reader-friendly section they're in and copies a
  // deep-link that re-opens the book at that exact paragraph — so readers
  // can save/share a precise spot the way page numbers used to.
  //
  // The link encodes the paragraph as a *content hash* of its text
  // (#loc=N.a<hash>), NOT an ordinal. This survives book edits: inserting
  // or deleting other paragraphs no longer shifts every saved link to the
  // wrong place. Only re-wording THAT paragraph changes its hash (and then
  // just that one link gracefully fails to resolve, instead of silently
  // pointing somewhere wrong). Legacy ordinal links (#loc=N.M, digits
  // only) still resolve via a fallback so nothing already shared breaks.
  const [locator, setLocator] = useState<
    { ch: number; p: number; anchor: string; section: string; snippet: string; copied: boolean } | null
  >(null);
  const [locPaste, setLocPaste] = useState("");
  const [pendingPara, setPendingPara] = useState<
    { chapter: number; anchor?: string; p?: number } | null
  >(null);

  // ── ⭐ Bookmarks ("המקומות שלי") ──────────────────────────────────────
  // Saved spots, synced per-reader. The save action lives inside the 📍
  // popover; the list lives at the top of the ☰ table-of-contents page.
  const { bookmarks, addBookmark, removeBookmark } = useBookmarks(user);
  // Inline save UI inside the 📍 popover: null = not saving, "prompt" =
  // editing the title, "done" = just-saved confirmation.
  const [saveState, setSaveState] = useState<"idle" | "prompt" | "done">("idle");
  const [saveTitle, setSaveTitle] = useState("");
  // When the reader returns to a bookmark in a chapter they no longer have
  // access to, hold it here to show a gentle "this chapter needs a purchase"
  // confirm instead of silently dropping them on the paywall.
  const [lockedReturn, setLockedReturn] = useState<{ chapter: number } | null>(null);
  // Per-bookmark "link copied" flash, keyed by bookmark id.
  const [copiedBookmarkId, setCopiedBookmarkId] = useState<string | null>(null);

  // Ordered, addressable prose blocks in the currently-rendered chapter.
  // Same content order on every device → a stable numbering.
  const getProseBlocks = (): HTMLElement[] => {
    const out: HTMLElement[] = [];
    for (const sc of Array.from(document.querySelectorAll<HTMLElement>(".sc"))) {
      const blocks = Array.from(
        sc.querySelectorAll<HTMLElement>("p, li"),
      ).filter((el) => (el.textContent || "").trim().length > 0);
      out.push(...(blocks.length ? blocks : [sc]));
    }
    return out;
  };

  // Small, fast, stable string hash (cyrb53) → base36. Same text always
  // yields the same token on every device, so a link minted on a phone
  // resolves on a laptop. Not cryptographic — collisions are astronomically
  // unlikely for prose-length strings, and the per-chapter occurrence
  // suffix below handles the only realistic case (two identical short
  // list items in one chapter).
  const hashText = (str: string): string => {
    let h1 = 0xdeadbeef;
    let h2 = 0x41c6ce57;
    for (let i = 0; i < str.length; i++) {
      const ch = str.charCodeAt(i);
      h1 = Math.imul(h1 ^ ch, 2654435761);
      h2 = Math.imul(h2 ^ ch, 1597334677);
    }
    h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
    h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
    const n = 4294967296 * (2097151 & h2) + (h1 >>> 0);
    return n.toString(36);
  };
  const normalizeProseText = (s: string): string =>
    s.replace(/\s+/g, " ").trim().slice(0, 400);

  // Prose blocks paired with a stable, text-derived anchor. Duplicate text
  // within a chapter gets an occurrence suffix ("-2") so each block is
  // still uniquely addressable; that suffix is order-among-identical-text
  // only, so inserting a *different* paragraph between them doesn't move it.
  const getProseAnchors = (): { el: HTMLElement; anchor: string }[] => {
    const seen = new Map<string, number>();
    return getProseBlocks().map((el) => {
      const base = hashText(normalizeProseText(el.textContent || ""));
      const n = (seen.get(base) || 0) + 1;
      seen.set(base, n);
      return { el, anchor: n > 1 ? `${base}-${n}` : base };
    });
  };

  // Index of the prose block currently at the top of the viewport.
  const blockAtTopIndex = (list: { el: HTMLElement }[]): number => {
    const offset = 170;
    let idx = 0;
    for (let i = 0; i < list.length; i++) {
      if (list[i].el.getBoundingClientRect().top - offset <= 0) idx = i;
      else break;
    }
    return idx;
  };

  // Anchor of the prose block currently at the top of the viewport.
  const currentAnchor = (): string => {
    const list = getProseAnchors();
    return list[blockAtTopIndex(list)]?.anchor || "";
  };

  // Opening words of the paragraph at the top of the viewport — used as the
  // default, recognizable bookmark name (the section heading alone repeats
  // across many spots in a long section). The reader can still rename it.
  const currentParaSnippet = (): string => {
    const list = getProseAnchors();
    const el = list[blockAtTopIndex(list)]?.el;
    const txt = (el?.textContent || "").replace(/\s+/g, " ").trim();
    if (txt.length <= 52) return txt;
    return txt.slice(0, 52).replace(/\s+\S*$/, "") + "…";
  };

  // A loc token is either a content anchor ("a<hash>") or a legacy ordinal
  // (digits only). Decode to whichever the navigator should resolve.
  const parseLocToken = (tok: string): { anchor?: string; p?: number } => {
    if (/^\d+$/.test(tok)) return { p: parseInt(tok, 10) };
    return { anchor: tok.replace(/^a/, "") };
  };

  // 1-based number of the prose block currently at the top of the viewport.
  const currentParaNumber = (): number => {
    const blocks = getProseBlocks();
    const offset = 170; // clears slim-header + (when present) audio player
    let idx = 0;
    for (let i = 0; i < blocks.length; i++) {
      if (blocks[i].getBoundingClientRect().top - offset <= 0) idx = i;
      else break;
    }
    return idx + 1;
  };

  // Heading of the section currently at the top of the viewport — the
  // reader-friendly label (recognizable, unlike a raw paragraph number).
  const currentSectionLabel = (): string => {
    const scs = Array.from(document.querySelectorAll<HTMLElement>(".sc"));
    const offset = 170;
    let cur: HTMLElement | null = scs[0] || null;
    for (const sc of scs) {
      if (sc.getBoundingClientRect().top - offset <= 0) cur = sc;
      else break;
    }
    const h = cur?.querySelector(".stitle, h1, h2, h3, h4");
    return (h?.textContent || "").trim();
  };

  const openLocator = () => {
    if (typeof chapter !== "number" || chapter < 1 || chapter > 12) return;
    setLocPaste("");
    setSaveState("idle");
    setSaveTitle("");
    setLocator({
      ch: chapter,
      p: currentParaNumber(),
      anchor: currentAnchor(),
      section: currentSectionLabel(),
      snippet: currentParaSnippet(),
      copied: false,
    });
  };

  // Navigate to a saved location (chapter + anchor, or legacy ordinal).
  // The pendingPara effect then scrolls + flashes it.
  const goToLocation = (ch: number, target: { anchor?: string; p?: number }) => {
    if (!(ch >= 1 && ch <= 12)) return;
    if (!target.anchor && !(target.p && target.p >= 1)) return;
    setStarted(true);
    setChapter(ch);
    setPendingPara({ chapter: ch, anchor: target.anchor, p: target.p });
    setLocator(null);
  };

  // Parse a pasted link/reference → go. Accepts a full URL with a loc
  // token (anchor or ordinal), or a bare "6.13" / "6-13" ordinal.
  const goToPasted = () => {
    const txt = locPaste.trim();
    let ch = 0;
    let tok = "";
    const mUrl = txt.match(/loc=(\d+)\.([A-Za-z0-9_-]+)/);
    const mNum = txt.match(/(\d+)\s*[.\-:]\s*(\d+)/);
    if (mUrl) {
      ch = parseInt(mUrl[1], 10);
      tok = mUrl[2];
    } else if (mNum) {
      ch = parseInt(mNum[1], 10);
      tok = mNum[2];
    } else {
      return;
    }
    if (!(ch >= 1 && ch <= 12)) return;
    try {
      window.location.hash = `loc=${ch}.${tok}`;
    } catch {}
    goToLocation(ch, parseLocToken(tok));
  };

  // Build the shareable deep-link for a spot. Prefers the stable content
  // anchor; falls back to the legacy ordinal so older saved spots still work.
  const buildLocUrl = (ch: number, anchor?: string | null, p?: number | null): string => {
    const tok = anchor ? `a${anchor}` : `${p || 1}`;
    return `${window.location.origin}/#loc=${ch}.${tok}`;
  };

  const copyLocatorLink = () => {
    if (!locator) return;
    const url = buildLocUrl(locator.ch, locator.anchor, locator.p);
    const done = () => setLocator((l) => (l ? { ...l, copied: true } : l));
    try {
      navigator.clipboard?.writeText(url).then(done, done);
    } catch {
      done();
    }
  };

  // ── Bookmark actions ─────────────────────────────────────────────────
  // Save the spot the 📍 popover is currently showing into "המקומות שלי".
  const saveCurrentSpot = async () => {
    if (!locator) return;
    const title =
      saveTitle.trim() || locator.snippet || locator.section || `פרק ${locator.ch}`;
    const res = await addBookmark({
      chapter: locator.ch,
      anchor: locator.anchor || null,
      paraOrdinal: locator.p,
      sectionLabel: locator.section || null,
      title,
    });
    if (res) setSaveState("done");
  };

  // A bookmark in a paid chapter (2-12) is unreachable for a reader without
  // access — returning would just dump them on the paywall. Guard it.
  const bookmarkIsLocked = (ch: number): boolean => ch > 1 && !hasAccess;

  const returnToBookmark = (b: { chapter: number; anchor: string | null; para_ordinal: number | null }) => {
    if (bookmarkIsLocked(b.chapter)) {
      setLockedReturn({ chapter: b.chapter });
      return;
    }
    closeTOC();
    goToLocation(b.chapter, {
      anchor: b.anchor || undefined,
      p: b.para_ordinal || undefined,
    });
  };

  const copyBookmarkLink = (b: { id: string; chapter: number; anchor: string | null; para_ordinal: number | null }) => {
    const url = buildLocUrl(b.chapter, b.anchor, b.para_ordinal);
    const flash = () => {
      setCopiedBookmarkId(b.id);
      window.setTimeout(
        () => setCopiedBookmarkId((id) => (id === b.id ? null : id)),
        1800,
      );
    };
    try {
      navigator.clipboard?.writeText(url).then(flash, flash);
    } catch {
      flash();
    }
  };

  // Deep-link: opening #loc=N.<token> jumps to chapter N and that spot.
  // Token is a content anchor ("a<hash>") or a legacy ordinal (digits).
  useEffect(() => {
    const apply = () => {
      const m = window.location.hash.match(/loc=(\d+)\.([A-Za-z0-9_-]+)/);
      if (!m) return;
      const ch = parseInt(m[1], 10);
      const { anchor, p } = parseLocToken(m[2]);
      if (ch >= 1 && ch <= 12 && (anchor || (p && p >= 1))) {
        setStarted(true);
        setChapter(ch);
        setPendingPara({ chapter: ch, anchor, p });
      }
    };
    apply();
    window.addEventListener("hashchange", apply);
    return () => window.removeEventListener("hashchange", apply);
  }, []);

  // After the deep-linked chapter mounts, scroll to the target paragraph
  // and flash it briefly so the reader sees exactly where they landed.
  // Resolve by content anchor first; fall back to the legacy ordinal.
  useEffect(() => {
    if (!pendingPara || pendingPara.chapter !== chapter) return;
    // Same async-prose race as bug #79: the chapter HTML is fetched from
    // book_content, so a single fixed delay (was 350ms) silently missed the
    // target paragraph on a slow/cold fetch and left the reader at the top.
    // Poll until the paragraph actually mounts (~4s ceiling), then scroll.
    let cancelled = false;
    let timer: number | null = null;
    let attempts = 0;
    const MAX_ATTEMPTS = 50;
    const tryScroll = () => {
      if (cancelled) return;
      let el: HTMLElement | undefined;
      if (pendingPara.anchor) {
        el = getProseAnchors().find((a) => a.anchor === pendingPara.anchor)?.el;
      }
      if (!el && pendingPara.p && pendingPara.p >= 1) {
        el = getProseBlocks()[pendingPara.p - 1];
      }
      if (el) {
        window.scrollTo({
          // Match currentParaNumber()'s detection offset so a copied
          // reference and the landed paragraph stay in sync on round-trips.
          top: el.getBoundingClientRect().top + window.scrollY - 170,
          behavior: "smooth",
        });
        el.classList.add("para-flash");
        const flashEl = el;
        setTimeout(() => flashEl.classList.remove("para-flash"), 2200);
        setPendingPara(null);
        return;
      }
      attempts += 1;
      if (attempts >= MAX_ATTEMPTS) {
        setPendingPara(null);
        return;
      }
      timer = window.setTimeout(tryScroll, 80);
    };
    timer = window.setTimeout(tryScroll, 60);
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [chapter, pendingPara]);

  // Per-section listen buttons used to be injected into the DOM by a
  // 500ms interval. They're now rendered by <ChapterContent> as proper
  // React portals into placeholder divs inside each .sc — see the
  // `onSectionListen` prop wired up below. React owns the lifecycle,
  // so the buttons can't get wiped by widget portal re-mounts or other
  // DOM gymnastics.

  const save = useCallback(() => {
    localStorage.setItem(
      "ei_book_ch1",
      JSON.stringify({ fields, freeText, pracLog })
    );
    // Flush to the cloud immediately so the reader sees the same text
    // the next time they open the book on another device. No-op for
    // free-preview readers without access.
    pushProgressToCloud();
    setSaveBanner(true);
    setTimeout(() => setSaveBanner(false), 2200);
  }, [fields, freeText, pracLog, pushProgressToCloud]);

  // Compact save button shown right under each fillable table/exercise, so
  // readers can persist progress without scrolling to the chapter footer.
  const InlineSave = ({ label = "שמור התקדמות" }: { label?: string }) => (
    <div style={{ display: "flex", justifyContent: "flex-end", margin: "10px 0 6px" }}>
      <button
        type="button"
        onClick={save}
        style={{
          padding: "8px 16px",
          background: "linear-gradient(135deg, var(--terra), var(--accent))",
          color: "white",
          border: "none",
          borderRadius: 10,
          fontFamily: "'Heebo', sans-serif",
          fontSize: 13,
          fontWeight: 600,
          cursor: "pointer",
          boxShadow: "0 2px 8px rgba(160,112,74,0.25)",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        💾 {label}
      </button>
    </div>
  );

  // Save button + jump-to-related-table — for paired exercises (Mirror Law,
  // negative/positive emotions, strengths/weaknesses intelligences, etc.).
  // Lets the reader bounce between the two halves of an exercise once they
  // understand the relationship, instead of scrolling through the explanatory
  // text between them every time.
  const SaveAndJump = ({ jumpTo, jumpLabel }: { jumpTo: string; jumpLabel: string }) => (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, margin: "10px 0 6px" }}>
      <button
        type="button"
        onClick={() => document.getElementById(jumpTo)?.scrollIntoView({ behavior: "smooth", block: "start" })}
        style={{ padding: "8px 14px", background: "var(--bg2)", color: "var(--deep)", border: "1.5px solid var(--sand)", borderRadius: 10, fontFamily: "'Heebo', sans-serif", fontSize: 13, fontWeight: 500, cursor: "pointer" }}
      >
        {jumpLabel}
      </button>
      <button
        type="button"
        onClick={save}
        style={{ padding: "8px 16px", background: "linear-gradient(135deg, var(--terra), var(--accent))", color: "white", border: "none", borderRadius: 10, fontFamily: "'Heebo', sans-serif", fontSize: 13, fontWeight: 600, cursor: "pointer", boxShadow: "0 2px 8px rgba(160,112,74,0.25)" }}
      >
        💾 שמור התקדמות
      </button>
    </div>
  );

  // Chapter-8 exercise tables rendered as a responsive card grid (one card
  // per row, every column a labelled field) instead of a cramped wide table —
  // readable + fillable on mobile. A plain render fn (NOT a component) so the
  // textareas keep focus across re-renders. Same field keys → answers persist.
  const renderCh8Grid = ({
    id,
    headers,
    prefix,
    rowKeys,
    colKeys,
    rowLabel,
    example,
    footer,
  }: {
    id?: string;
    headers: string[];
    prefix: string;
    rowKeys: number[];
    colKeys: number[];
    rowLabel: string;
    example?: string[];
    footer: React.ReactNode;
  }) => (
    <div id={id} style={{ marginTop: 16, marginBottom: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(258px, 1fr))", gap: 14, alignItems: "start" }}>
        {example && (
          <div style={{ background: "var(--bg2)", border: "1px dashed var(--warm)", borderRadius: 12, padding: 14 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: "var(--warm)", borderBottom: "1px dashed var(--sand)", paddingBottom: 8, marginBottom: 12 }}>דוגמה</div>
            {headers.map((h, i) => (
              <div key={i} style={{ marginBottom: i === headers.length - 1 ? 0 : 11 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--muted)", marginBottom: 3, lineHeight: 1.35 }}>{h}</div>
                <div style={{ fontSize: 13, color: "var(--deep)", lineHeight: 1.55 }}>{example[i]}</div>
              </div>
            ))}
          </div>
        )}
        {rowKeys.map((rk, ri) => (
          <div key={rk} style={{ background: "var(--white)", border: "1px solid var(--sand)", borderRadius: 12, padding: 14 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: "var(--terra)", borderBottom: "1px solid var(--sand)", paddingBottom: 8, marginBottom: 12 }}>{rowLabel} {ri + 1}</div>
            {headers.map((h, ci) => {
              const fk = `${prefix}_r${rk}_c${colKeys[ci]}`;
              return (
                <div key={ci} style={{ marginBottom: ci === headers.length - 1 ? 0 : 12 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--deep)", marginBottom: 5, lineHeight: 1.35 }}>{h}</div>
                  <textarea
                    value={fields[fk] || ""}
                    onChange={(e) => setFields({ ...fields, [fk]: e.target.value })}
                    className="ta"
                    rows={2}
                    style={{ width: "100%", padding: "8px 10px", border: "1px solid var(--sand)", borderRadius: 6, background: "var(--bg2)", fontFamily: "inherit", fontSize: 13, direction: "rtl", resize: "vertical" }}
                    placeholder="כתבי כאן..."
                  />
                </div>
              );
            })}
          </div>
        ))}
      </div>
      {footer}
    </div>
  );

  const filled = Object.values(fields).filter(
    (v) => v?.trim().length > 3
  ).length;
  const pct = Math.round(
    ([
      filled >= 2,
      freeText.trim().length > 20,
      pracLog.trim().length > 5,
    ].filter(Boolean).length /
      3) *
      100
  );

  // Whole-book summary. Walks every editable surface across all chapters and
  // emits only the fields the reader actually filled in — so a partial reader
  // gets a short, focused file (no "(לא מולא)" noise).
  const downloadSummary = () => {
    const today = new Date().toLocaleDateString("he-IL");
    const lines: string[] = [];
    const get = (k: string) => (fields[k] || "").trim();

    const section = (title: string, contentLines: string[]) => {
      if (contentLines.length === 0) return;
      lines.push("");
      lines.push("─── " + title + " ───");
      lines.push("");
      lines.push(...contentLines);
    };

    lines.push("══════════════════════════════════════════════");
    lines.push("  שאלת האריה - סדנה בספר — סיכום אישי");
    lines.push("  תאריך: " + today);
    lines.push("══════════════════════════════════════════════");

    // Chapter 1: reflection questions + free text + practice log
    const ch1: string[] = [];
    FIELDS_DATA.forEach((f) => {
      const v = (fields[f.id] || "").trim();
      if (v) ch1.push(`${f.label}\n  ${v}`);
    });
    if (freeText.trim()) ch1.push(`תכונות חופשיות\n  ${freeText.trim()}`);
    if (pracLog.trim()) ch1.push(`יומן תרגול\n  ${pracLog.trim()}`);
    section("פרק 1 — יש כאן אריה, באמת?", ch1);

    // Chapter 4: past vs present interpretations
    const ch4: string[] = [];
    for (let r = 1; r <= 4; r++) {
      const what = get(`ch4_t1_r${r}_c1`);
      const past = get(`ch4_t1_r${r}_c2`);
      const now = get(`ch4_t1_r${r}_c3`);
      if (what || past || now) {
        ch4.push(`• ${what || "—"}`);
        if (past) ch4.push(`    פרשנות בעבר: ${past}`);
        if (now) ch4.push(`    פרשנות היום:  ${now}`);
      }
    }
    section("פרק 4 — אירוע / פרשנות בעבר / פרשנות היום", ch4);

    // Chapter 5: negative and positive emotions tables share the same shape
    const renderEmotionTable = (prefix: string, lastCol: string) => {
      const out: string[] = [];
      for (let r = 1; r <= 5; r++) {
        const emo = get(`${prefix}_r${r}_c1`);
        const beh = get(`${prefix}_r${r}_c2`);
        const imp = get(`${prefix}_r${r}_c3`);
        if (emo || beh || imp) {
          out.push(`• רגש: ${emo || "—"}`);
          if (beh) out.push(`    ביטוי: ${beh}`);
          if (imp) out.push(`    ${lastCol}: ${imp}`);
        }
      }
      return out;
    };
    section("פרק 5 — רגשות שליליים", renderEmotionTable("ch5_t1", "השלכות"));
    section("פרק 5 — רגשות חיוביים", renderEmotionTable("ch5_t2", "משמעויות"));

    // Chapter 6: Mirror Law — 5 personas × 5 traits
    const personas = [
      { c: 0, label: "אדם יקר" },
      { c: 1, label: "מכר ניטרלי" },
      { c: 2, label: "דמות מפורסמת" },
      { c: 3, label: "פוליטיקאי" },
      { c: 4, label: "אדם מאתגר" },
    ];
    const ch6Props: string[] = [];
    personas.forEach((p) => {
      const name = get(`ch6_t1_name_c${p.c}`);
      const traits: string[] = [];
      for (let t = 1; t <= 5; t++) {
        const v = get(`ch6_t1_trait${t}_c${p.c}`);
        if (v) traits.push(v);
      }
      if (name || traits.length) {
        ch6Props.push(`${p.label} — ${name || "(ללא שם)"}`);
        traits.forEach((t) => ch6Props.push(`  • ${t}`));
      }
    });
    section("פרק 6 — חוק המראות: 5 דמויות ותכונותיהן", ch6Props);

    // Chapter 6: rulers — auto-derived from the colour tags on the 25 traits.
    // One ruler per colour the reader used; count = how many traits carry it.
    const ch6Rulers: string[] = [];
    const ch6Tally = ch6TallyColors(fields);
    ch6Tally.forEach(({ idx, count }) => {
      const pos = get(`ch6_rcolor${idx}_pos`);
      const neg = get(`ch6_rcolor${idx}_neg`);
      ch6Rulers.push(
        `סרגל (${CH6_COLOR_NAMES[idx]}): ${pos || "—"}  ↔  ${neg || "—"}  (עוצמה: ${count})`
      );
    });
    section("פרק 6 — חוק המראות: סרגלי תכונות", ch6Rulers);

    // Chapter 7: ארבעת הערוצים (6 cols × 6 rows)
    const channelCols = [
      "המרכיב שבחרתי",
      "מידת מעורבות ביום-יום",
      "חיובי או שלילי",
      "להעצים או לצמצם",
      "זקוק לסיוע?",
      "מי יכול לסייע",
    ];
    const ch7Channels: string[] = [];
    for (let r = 1; r <= 6; r++) {
      const cells = channelCols.map((_, i) => get(`ch7_channels_r${r}_c${i + 1}`));
      if (cells.some(Boolean)) {
        ch7Channels.push(`• ${cells[0] || "—"}`);
        for (let i = 1; i < cells.length; i++) {
          if (cells[i]) ch7Channels.push(`    ${channelCols[i]}: ${cells[i]}`);
        }
      }
    }
    section("פרק 7 — ארבעת הערוצים", ch7Channels);

    // Chapter 7: values + plans (2 rows × 3 cols)
    const ch7Values: string[] = [];
    const valuesLabels = ["ערכים", "תוכניות"];
    for (let r = 1; r <= 2; r++) {
      const cells = [1, 2, 3].map((c) => get(`ch7_table_r${r}_c${c}`));
      if (cells.some(Boolean)) {
        ch7Values.push(`${valuesLabels[r - 1]}:`);
        cells.forEach((v) => { if (v) ch7Values.push(`  • ${v}`); });
      }
    }
    section("פרק 7 — ערכים ותוכניות", ch7Values);

    // Chapter 8: strengths (t9) and weaknesses (t10) intelligences
    const renderIntelTable = (prefix: string, lastCol: string) => {
      const out: string[] = [];
      for (let r = 2; r <= 5; r++) {
        const intel = get(`${prefix}_r${r}_c1`);
        const expression = get(`${prefix}_r${r}_c2`);
        const detail = get(`${prefix}_r${r}_c3`);
        if (intel || expression || detail) {
          out.push(`• אינטליגנציה: ${intel || "—"}`);
          if (expression) out.push(`    איך באה לידי ביטוי: ${expression}`);
          if (detail) out.push(`    ${lastCol}: ${detail}`);
        }
      }
      return out;
    };
    section("פרק 8 — חוזקות (אינטליגנציות)", renderIntelTable("ch8_t9", "תרומה ייחודית"));
    section("פרק 8 — חולשות (אינטליגנציות)", renderIntelTable("ch8_t10", "פגיעה במסוגלות"));

    // Chapter 8: self-rejection (t11) & other-rejection (t12) share shape
    const rejectionCols = ["אירוע", "טריגר", "נפגע / פגע", "הימנעות בעתיד?"];
    const renderRejection = (prefix: string) => {
      const out: string[] = [];
      for (let r = 1; r <= 4; r++) {
        const cells = [0, 1, 2, 3].map((c) => get(`${prefix}_r${r}_c${c}`));
        if (cells.some(Boolean)) {
          out.push(`• ${cells[0] || "—"}`);
          for (let i = 1; i < cells.length; i++) {
            if (cells[i]) out.push(`    ${rejectionCols[i]}: ${cells[i]}`);
          }
        }
      }
      return out;
    };
    section("פרק 8 — אי קבלה עצמית", renderRejection("ch8_t11"));
    section("פרק 8 — אי קבלה של אחר", renderRejection("ch8_t12"));

    // Chapter 8: need vs want (t13)
    const ch8Needs: string[] = [];
    const needCols = ["מעשה שצריך", "למה זה צריך?", "מה אומר לעצמי"];
    for (let r = 1; r <= 4; r++) {
      const cells = [0, 1, 2].map((c) => get(`ch8_t13_r${r}_c${c}`));
      if (cells.some(Boolean)) {
        ch8Needs.push(`• ${cells[0] || "—"}`);
        for (let i = 1; i < cells.length; i++) {
          if (cells[i]) ch8Needs.push(`    ${needCols[i]}: ${cells[i]}`);
        }
      }
    }
    section("פרק 8 — צריך מול רוצה", ch8Needs);

    // Tail marker so the reader can tell the file isn't truncated
    lines.push("");
    lines.push("══════════════════ סוף ══════════════════");

    const filename = `אינטליגנציה_רגשית_סיכום_${today.replace(/\//g, "-")}.txt`;
    const blob = new Blob([lines.join("\n")], { type: "text/plain;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };

  const steps = [
    "<strong>זהה את הרגש השלילי</strong> - כל רגש: פחד, כעס, אכזבה, תסכול... יש מעל 300 כאלה, ולא משנה אם חזק או חלש",
    "<strong>עצור. קח נשימה עמוקה.</strong> התמקד רק בנשימה הזו לשנייה אחת.",
    "שאל את עצמך: <strong>האם יש כאן באמת סכנת מוות מיידית?</strong>",
    "<strong>שחרר.</strong> אם הרגש נעלם - הצלחת! אם ממשיך - אולי זו באמת סכנה. פעל בהתאם.",
  ];

  const CHAPTERS = [
    { n: 1, title: "יש כאן אריה, באמת?" },
    { n: 2, title: "מערכת ההישרדות" },
    { n: 3, title: "אינטליגנציה רגשית" },
    { n: 4, title: "התהליך הרגשי" },
    { n: 5, title: "מודל הרמזור" },
    { n: 6, title: "חוק המראות" },
    { n: 7, title: "הורמונים, ארבעת הערוצים, ערכים" },
    { n: 8, title: "מקורות יניקה, אינטליגנציות, ועצב הוואגוס" },
    { n: 9, title: "כלים, אי הבנה, הקשבה ורגשות חולפים" },
    { n: 10, title: "אגו, סוסים, הימנעות ודחיינות" },
    { n: 11, title: "אהבה, פילוסופיה, בודהיזם וספרים" },
    { n: 12, title: "סיום: שלבי ההישרדות, סימולציות והמסע ממשיך" },
  ];

  const currentChapterTitle =
    CHAPTERS.find((c) => c.n === chapter)?.title || "";

  // Full table of contents — maps app chapters → original book chapters
  // and lists every sub-section that the reader can jump straight to.
  const TOC = [
    { n: 1, title: "יש כאן אריה, באמת?", icon: "✿", bookCh: "פרק 1", subs: [
      // idx = position of the .sc block in src/chapters/chapter-1.html
      // (see the header comment there for the section order).
      { idx: 0, title: "פתיחה: בקול שלך, והסוואנה" },
      { idx: 1, title: "הניסוי הראשון שלך" },
      { idx: 2, title: "מה קורה לאנשים שעוברים את הסדנה" },
      { idx: 3, title: "אינטליגנציה רגשית - מה זה בעצם?" },
      { idx: 4, title: "פחד יש רק אחד - פחד מוות" },
      { idx: 5, title: "\"אבל אני כבר ניסיתי הכל\"" },
      { idx: 6, title: "מבט ראשון פנימה" },
      { idx: 7, title: "יומן תרגול אישי" },
      { idx: 8, title: "דוגמאות חיות - המנגנון בפעולה" },
      { idx: 9, title: "לפני שנמשיך - מילה ממני אלייך" },
    ]},
    { n: 2, title: "מערכת ההישרדות", icon: "🛡", bookCh: "פרק 2", subs: [
      { idx: 0, title: "מערכת ההישרדות – המערכת הלא מודעת" },
      { idx: 1, title: "המבנה הפיזיולוגי של מערכת ההישרדות" },
      { idx: 2, title: "פחד המוות – מנוע החיים" },
      { idx: 3, title: "השאלה העמוקה – מהו המוות?" },
      { idx: 4, title: "תהליך עיבוד המידע במוח" },
      { idx: 5, title: "שתי המערכות: סימפתטית ופאראסימפתטית" },
      { idx: 6, title: "סיפור אישי: עיקול קטן, סערה גדולה" },
      { idx: 7, title: "חמש תובנות מהאירוע הפשוט" },
      { idx: 8, title: "מה בעצם קורה כשאנחנו מרגישים משהו?" },
      { idx: 9, title: "נקודת ההתפקחות" },
    ]},
    { n: 3, title: "אינטליגנציה רגשית", icon: "✦", bookCh: "פרק 3", subs: [
      { idx: 0, title: "אינטליגנציה רגשית – ארגז הכלים הנדיר" },
      { idx: 1, title: "התועלות בפיתוח האינטליגנציה הרגשית" },
      { idx: 2, title: "עוצמת הרגשות – איך עוצמת הרגש נקבעת" },
      { idx: 3, title: "פחד כזב (מדומה)" },
      { idx: 4, title: "פרשנויות שגויות – כיצד נזהה ונמנע" },
      { idx: 5, title: "מודעות לרגשות – כיצד" },
    ]},
    { n: 4, title: "התהליך הרגשי", icon: "❀", bookCh: "פרקים 4–7", subs: [
      { idx: 0, title: "התהליך הרגשי" },
      { idx: 1, title: "איך נוצר רגש - הגרסה הקצרה" },
      { idx: 2, title: "איך נוצר הרגש - הגרסה המורחבת" },
      { idx: 3, title: "התהליך הרגשי - מבט מסכם" },
      { idx: 4, title: "פרשנות - לב העניין" },
      { idx: 5, title: "שלב הטרום-רגש - נקודת ההתפקחות" },
      { idx: 6, title: "פרשנות שנייה - פרשנות מודעת" },
      { idx: 7, title: "התגובה הירוקה - או אי תגובה כלל" },
      { idx: 8, title: "סיכום - הדרך אל רגש מיטבי" },
      { idx: 9, title: "יצירת הרגלים חדשים" },
      { idx: 10, title: "האתגר הקשה - הרגלים שאינם נגישים" },
      { idx: 11, title: "לכייל - לא לאפס" },
      { idx: 12, title: "סרגל ראשון: נדיב — קמצן" },
      { idx: 13, title: "סרגל שני: ישר — שקרן" },
      { idx: 14, title: "דוגמאות לאי-הלימה בין פרשנות למציאות" },
      { idx: 15, title: "איך משנים הרגלים לא מודעים?" },
      { idx: 16, title: "מודלים לפרשנות מיטבית" },
      { idx: 17, title: "פחד או אהבה - שני עולמות רגשיים" },
      { idx: 18, title: "יוצא דופן חשוב: עצב" },
      { idx: 19, title: "המפתח לפרשנות מיטבית - מודעות" },
    ]},
    { n: 5, title: "מודל הרמזור", icon: "▣", bookCh: "פרק 9", subs: [
      { idx: 0, title: "מודל הרמזור" },
      { idx: 1, title: "המודל פשוט - אדום או ירוק" },
      { idx: 2, title: "תרגיל - זיהוי אישי של תחושת הסיכון ההישרדותי" },
      { idx: 3, title: "שלב ראשון - רגשות שליליים: ביטוי והשלכות" },
      { idx: 4, title: "שלב שני - רגשות חיוביים: ביטוי ומשמעויות" },
      { idx: 5, title: "סיכום ביניים - אדום או ירוק?" },
      { idx: 6, title: "איך נולדים הרגלים?" },
      { idx: 7, title: "דמיין תינוק בן חודשיים" },
      { idx: 8, title: "השאלה הגדולה" },
      { idx: 9, title: "דוגמה: ביקורת בפומבי" },
      { idx: 10, title: 'אנימציה: "מי נוהג עכשיו - הילד או המבוגר?"' },
    ]},
    { n: 6, title: "חוק המראות", icon: "◐", bookCh: "פרק 10", subs: [
      { idx: 0, title: "חוק המראות — מבוא ותרגיל" },
      { idx: 1, title: "דוגמה — הגניבה ברחוב" },
      { idx: 2, title: "באמצעותך אני מזהה את הצורך שלי" },
      { idx: 3, title: "מצב נפשי משנה תפיסה" },
      { idx: 4, title: "חוק שלוש האצבעות" },
      { idx: 5, title: "תרגיל — הפעלת חוק המראות על מצב ממשי" },
      { idx: 6, title: "לסיכום — הרגל חדש" },
    ]},
    { n: 7, title: "הורמונים, ארבעת הערוצים, ערכים", icon: "⚛", bookCh: "פרקים 11–13", subs: [
      { idx: 0, title: "הורמונים" },
      { idx: 1, title: "מודל ארבעת הערוצים" },
      { idx: 2, title: "המערכות המעצבות — ערכים, מוסר, הישרדות" },
    ]},
    { n: 8, title: "מקורות יניקה, אינטליגנציות ועצב הוואגוס", icon: "❤", bookCh: "פרקים 14–18", subs: [
      { idx: 0, title: "מקורות היניקה" },
      { idx: 1, title: "11 אינטליגנציות" },
      { idx: 2, title: "מודל אני הכי טוב" },
      { idx: 3, title: "רוצה צריך" },
      { idx: 4, title: "עצב הוואגוס ואינטליגנציה רגשית" },
    ]},
    { n: 9, title: "כלים, אי הבנה, הקשבה ורגשות חולפים", icon: "✎", bookCh: "פרקים 19–22", subs: [
      { idx: 0, title: "כלים להתמודדות עם אירועים רגשיים שכיחים" },
      { idx: 1, title: "אי הבנה ואי הסכמה — אתגר ופתרון" },
      { idx: 2, title: "המפתח להתמודדות — הקשבה" },
      { idx: 3, title: "רגשות זה עניין זמני, מגיעים וחולפים" },
    ]},
    { n: 10, title: "אגו, סוסים, הימנעות ודחיינות", icon: "♞", bookCh: "פרקים 23–27", subs: [
      { idx: 0, title: "האגו, הסיכון האגואיסטי ומערכת ההפעלה" },
      { idx: 1, title: "סוסים ואינטליגנציה רגשית" },
      { idx: 2, title: "הימנעות — הבנה והתמודדות" },
      { idx: 3, title: "דחיינות — בין הימנעות לוויסות רגשי" },
      { idx: 4, title: "מחשבות שליליות — מחשבות חיוביות" },
    ]},
    { n: 11, title: "אהבה, פילוסופיה, בודהיזם וספרים", icon: "♥", bookCh: "פרקים 28–35", subs: [
      { idx: 0, title: "לאהבה אין גבול" },
      { idx: 1, title: "פילוסופיה סטואית" },
      { idx: 2, title: "דופק נמוך, דופק גבוה" },
      { idx: 3, title: "תחום המאמץ המיטבי — Flow / Zone" },
      { idx: 4, title: "בודהיזם" },
      { idx: 5, title: "סופרפוזיציה / קארמה" },
      { idx: 6, title: "פסיכולוגיה חיובית" },
      { idx: 7, title: "ספרים מומלצים" },
    ]},
    { n: 12, title: "סיום: שלבי ההישרדות, סימולציות והמסע ממשיך", icon: "✺", bookCh: "פרקים 36–40", subs: [
      { idx: 0, title: "השלבים ברצף הפעלת מערכת ההישרדות" },
      { idx: 1, title: "מהסוף להתחלה" },
      { idx: 2, title: "הסופיות של החיים" },
      { idx: 3, title: "סימולציות מהחיים" },
      { idx: 4, title: "סיום — המסע ממשיך" },
    ]},
  ];

  // Slim chapter navigation: previous + next + back-to-top.
  // The full chapter list lives in the floating "תוכן עניינים" button.
  const ChapterNav = ({ bottom = false }) => {
    const curIdx = CHAPTERS.findIndex((c) => c.n === chapter);
    const prevCh = curIdx > 0 ? CHAPTERS[curIdx - 1] : null;
    const nextCh = curIdx >= 0 && curIdx < CHAPTERS.length - 1 ? CHAPTERS[curIdx + 1] : null;
    const navTo = (n) => { setChapter(n); scrollToTop(); };
    const pillBase = {
      padding: "4px 12px",
      borderRadius: 40,
      border: "none",
      cursor: "pointer",
      fontFamily: "'Heebo', sans-serif",
      fontSize: 14,
      fontWeight: 500,
      whiteSpace: "nowrap",
      transition: "all .2s",
    };
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
          padding: bottom ? "24px 0 0" : 0,
          marginBottom: bottom ? 0 : 6,
          borderTop: bottom ? "1px solid var(--sand)" : "none",
        }}
      >
        {prevCh && (
          <button
            onClick={() => navTo(prevCh.n)}
            style={{
              ...pillBase,
              background: "var(--bg2)",
              color: "var(--muted)",
              border: "1.5px solid var(--sand)",
            }}
          >
            → פרק {prevCh.n} הקודם
          </button>
        )}

        <span
          style={{
            fontFamily: "'Heebo', sans-serif",
            fontSize: 14,
            color: "var(--muted)",
            margin: "0 auto",
          }}
        >
          {bottom ? `פרק ${chapter} מתוך ${CHAPTERS.length}` : ""}
        </span>

        {bottom && (
          <button
            onClick={scrollToTop}
            style={{
              ...pillBase,
              background: "var(--bg2)",
              color: "var(--muted)",
              border: "1.5px solid var(--sand)",
            }}
          >
            ↑ חזרה להתחלה
          </button>
        )}

        {nextCh && (
          <button
            onClick={() => navTo(nextCh.n)}
            style={{
              ...pillBase,
              fontWeight: 600,
              background: "linear-gradient(135deg, var(--terra), var(--accent))",
              color: "white",
              boxShadow: "0 4px 16px rgba(160,112,74,0.3)",
            }}
          >
            פרק {nextCh.n} הבא ←
          </button>
        )}
      </div>
    );
  };

  // Drive the slim-header progress bar from either the TTS playback (listen mode)
  // or the page scroll position (read mode).
  // headerProgress drives the slim-header progress bar. In read mode it
  // reflects scroll position; in listen mode the AudioPlayer drives its
  // own progress UI, so the slim bar can fall back to scroll position as
  // well (the reader can still scroll while listening).
  const headerProgress = scrollProgress;

  // ---- Mode coupling: keep position roughly in sync when the reader flips
  // between קריאה and האזנה mid-chapter. We anchor on .sc section cards.
  // - read → listen: find the section nearest the viewport top, switch mode,
  //   then click that section's "האזן" button so audio picks up there.
  // - listen → read: locate whichever section is currently being spoken
  //   (its האזן button has the .pl class), switch mode, stop TTS, and scroll
  //   that section into view so the reader continues from the same place.
  const HEADER_OFFSET = 50; // slim-header (~32px) + small breathing room

  const findCurrentSection = () => {
    const sections = Array.from(document.querySelectorAll(".sc"));
    let best = null;
    let bestDist = Infinity;
    for (const sc of sections) {
      const rect = sc.getBoundingClientRect();
      if (rect.bottom <= HEADER_OFFSET) continue; // already scrolled past
      const dist = Math.abs(rect.top - HEADER_OFFSET);
      if (dist < bestDist) {
        bestDist = dist;
        best = sc;
      }
    }
    return best;
  };

  // Compute the 0-based index of a .sc element within its chapter wrapper.
  // Used to translate "the section closest to the viewport top" into the
  // section index that AudioPlayer + chapter-N-sections.json understand.
  const sectionIndexOf = (sc) => {
    if (!sc) return null;
    const wrapper = document.querySelector(`.ch-numbered-${chapter}`) || document;
    const all = Array.from(wrapper.querySelectorAll(".sc"));
    const i = all.indexOf(sc);
    return i >= 0 ? i : null;
  };

  // Read → Listen toggle.
  // If the reader has scrolled into a section (not at the top of the
  // chapter), translate that to "seek the audio to this section's start
  // and play". Otherwise just enter listen mode at the saved position.
  const switchToListen = () => {
    if (mode === "listen") {
      setStarted(true);
      return;
    }
    const wasScrolled = window.scrollY > 200;
    const target = wasScrolled ? findCurrentSection() : null;
    const idx = target ? sectionIndexOf(target) : null;
    if (typeof idx === "number") {
      setPendingAudioSection({ chapter, idx });
    }
    setMode("listen");
    setStarted(true);
  };

  // Listen → Read toggle. The AudioPlayer pauses on unmount; we don't have
  // a separate audio-stop call to make here. If we knew the section the
  // audio was currently playing we'd scroll to it — for now we leave the
  // page where the reader was scrolled.
  const switchToRead = () => {
    if (mode === "read") {
      setStarted(true);
      return;
    }
    setMode("read");
    setStarted(true);
  };

  // Per-section listen-button handler passed into ChapterContent. Only
  // wired up in read mode and for chapters that have numbered TOC subs
  // (chapters 6-12). Suppressing the prop on chapters 1-5 keeps their
  // prose unchanged — those chapters don't have the section→audio
  // alignment data ChapterContent expects.
  // Per-section 🎧 listen button handler. Every chapter (1-12) has a
  // chapter-N-sections.json with timestamps, so the button can land
  // the audio precisely at any section start across the whole book.
  // Only undefined in listen mode (the player is the control surface
  // there) or off-book (chapter outside 1-12 — shouldn't happen).
  const sectionListenHandler =
    mode === "read" && chapter >= 1 && chapter <= 12 ? listenFromSection : undefined;

  // Unknown path — show a friendly 404 instead of silently rendering the
  // home page (which made every typo URL indistinguishable from the
  // landing screen).
  if (isUnknownPath) {
    return <NotFoundPage />;
  }

  // Admin route: standalone UI, gated inside AdminPage by useAdmin.
  if (isAdminPath) {
    return <AdminPage />;
  }

  // Payment success route — Lemon Squeezy redirects buyers here. The page
  // polls the access RPC until the webhook completes the user's license.
  if (chatToken) {
    return <ChatWorkshopPage token={chatToken} />;
  }

  if (typeof window !== "undefined" && DAILY_PATH.test(window.location.pathname)) {
    return <DailyLionPage />;
  }

  if (isPaymentSuccessPath) {
    return <PaymentSuccess />;
  }

  // Contact page — standalone support landing with email + WhatsApp.
  if (isContactPath) {
    return <ContactPage />;
  }

  // Welcome / onboarding page — shown before users open the book proper.
  // Currently only accessible by direct visit to /welcome (not auto-shown
  // yet — Shlomo wants to review the content before we wire it as the
  // first-time-user flow with a localStorage dismiss flag).
  if (isWelcomePath) {
    return <WelcomePage />;
  }

  // Variant B landing (/b): quotes -> promise -> offer. The A/B split
  // happens at the ad level; this page reuses the live PaywallGate card.
  if (isDecisionPath) {
    return <DecisionPage />;
  }

  // Sales page v2 (/b2): author, headline, the "why" film, then the door.
  if (SALES2_PATH.test(window.location.pathname)) {
    return <SalesPage />;
  }

  // Gift landing (/gift): the same offer card, opened in gift mode.
  if (isGiftPath) {
    return <DecisionPage gift />;
  }

  // Chapter pick (/pick): the ToC as the sales engine (8.10).
  if (isPickPath) {
    return <PickPage />;
  }

  if (isAboutPath) {
    return <AboutPage />;
  }

  // Reviewer invitation route: when a visitor lands on /r/<token> and is
  // not authenticated, show the personalised invitation page instead of
  // the book. (After they log in we claim the invitation and fall through
  // to the normal app.)
  if (reviewerToken && !user && !authLoading) {
    return <ReviewerInvitePage token={reviewerToken} />;
  }

  // Per-buyer watermark: only on paid chapters (2+), only when the user
  // has an active license, never on the free chapter 1. Deters casual
  // screenshot-share and fingerprints leaks back to the source account.
  const watermarkVisible =
    chapter > 1 && (hasAccess || chapterGranted) && !accessLoading && !authLoading;

  return (
    <div className="bc">
      <style dangerouslySetInnerHTML={{ __html: styles }} />
      <Watermark email={user?.email ?? null} visible={watermarkVisible} />
      {locator && (
        <div className="locator-pop" role="dialog" aria-label="המיקום שלך בפרק">
          <div className="locator-ref">
            אתם כאן: פרק {locator.ch}
            {locator.section ? <> · {locator.section}</> : null}
          </div>
          <button type="button" className="locator-btn primary" onClick={copyLocatorLink}>
            {locator.copied ? "✓ הקישור הועתק" : "📋 העתקת קישור למקום הזה"}
          </button>
          {/* Save this spot to "המקומות שלי" (requires login). */}
          {user ? (
            saveState === "done" ? (
              <div className="locator-saved">✓ נשמר ל"המקומות שלי" — תמצאו אותו בתפריט ☰</div>
            ) : saveState === "prompt" ? (
              <div className="locator-savebox">
                <input
                  type="text"
                  className="locator-input"
                  placeholder="שם לסימנייה (אפשר להשאיר)"
                  value={saveTitle}
                  onChange={(e) => setSaveTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") saveCurrentSpot();
                  }}
                  maxLength={120}
                  autoFocus
                />
                <button type="button" className="locator-btn primary" onClick={saveCurrentSpot}>
                  שמירה
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="locator-btn"
                onClick={() => {
                  setSaveTitle(locator.snippet || locator.section || "");
                  setSaveState("prompt");
                }}
              >
                ➕ שמירה לרשימת המקומות שלי
              </button>
            )
          ) : (
            <div className="locator-hint">
              התחברו (כפתור "התחברות" למעלה) כדי לשמור מקומות לרשימה שלכם.
            </div>
          )}
          <div className="locator-sep">חזרה למקום שמרתם</div>
          <div className="locator-goto">
            <input
              type="text"
              className="locator-input"
              placeholder="הדביקו כאן קישור שהעתקתם…"
              value={locPaste}
              onChange={(e) => setLocPaste(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") goToPasted();
              }}
              dir="ltr"
            />
            <button type="button" className="locator-btn primary" onClick={goToPasted}>
              עבור
            </button>
          </div>
          <button type="button" className="locator-btn locator-close" onClick={() => setLocator(null)}>
            סגירה
          </button>
        </div>
      )}
      {lockedReturn && (
        <div className="locator-pop" role="dialog" aria-label="פרק נעול">
          <div className="locator-ref">פרק {lockedReturn.chapter} עדיין נעול</div>
          <div className="locator-hint" style={{ marginBottom: 10 }}>
            המקום ששמרתם נמצא בפרק שדורש רכישה. אפשר לפתוח את עמוד הרכישה של הפרק,
            או לחזור לתוכן העניינים.
          </div>
          <button
            type="button"
            className="locator-btn primary"
            onClick={() => {
              const ch = lockedReturn.chapter;
              setLockedReturn(null);
              closeTOC();
              setStarted(true);
              setChapter(ch);
            }}
          >
            לפתוח את עמוד הרכישה של פרק {lockedReturn.chapter}
          </button>
          <button
            type="button"
            className="locator-btn locator-close"
            onClick={() => setLockedReturn(null)}
          >
            חזרה לתוכן העניינים
          </button>
        </div>
      )}
      {showDrivingNotice && (
        <DrivingNotice onDismiss={() => setShowDrivingNotice(false)} />
      )}
      {activeExercise && activeExercise.chapter === chapter && (
        <div className="exercise-overlay" role="dialog" aria-modal="true">
          <div className="exercise-overlay-card">
            <div className="exercise-overlay-icon" aria-hidden="true">🎯</div>
            <div className="exercise-overlay-title">
              {activeExercise.label}
            </div>
            <div className="exercise-overlay-body">
              כאן יש תרגיל למילוי.
              <br />
              מלא עכשיו, או המשך האזנה.
            </div>
            <div className="exercise-overlay-actions">
              <button
                type="button"
                className="exercise-overlay-btn primary"
                onClick={() => {
                  setActiveExercise(null);
                  sendAudioCommand("play");
                }}
                autoFocus
              >
                ▶ המשך האזנה
              </button>
              <button
                type="button"
                className="exercise-overlay-btn secondary"
                onClick={() => {
                  // Just dismiss the overlay — audio stays paused so the
                  // reader can work through the exercise. They press the
                  // player's own play button when ready.
                  setActiveExercise(null);
                }}
              >
                סגירה — אני ממלא
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Slim, always-on header: chapter label + thin progress bar.
          TOC trigger lives INSIDE the header on the right (RTL start side) —
          formerly a floating FAB that overlapped chapter content on mobile. */}
      <div className="slim-header">
        <div className="slim-header-auth">
          <AuthBadge />
        </div>
        <a
          className="slim-header-help"
          href="/welcome"
          aria-label="פתיחת עמוד הברוכים-הבאים"
          title="ברוכים הבאים — איך לקרוא את הספר"
        >
          ?
        </a>
        <a
          className="slim-header-wa"
          href={`https://wa.me/972544562084?text=${encodeURIComponent(
            "שלום שלמה, יש לי שאלה על «שאלת האריה»"
          )}`}
          target="_blank"
          rel="noreferrer"
          aria-label="שאלה? וואטסאפ 054-4562084"
          title="שאלה? וואטסאפ 054-4562084"
        >
          💬
        </a>
        {typeof chapter === "number" && chapter >= 1 && chapter <= 12 && (
          <button
            type="button"
            className="slim-header-locate"
            onClick={() => (locator ? setLocator(null) : openLocator())}
            aria-label="המיקום שלי בפרק — העתקת קישור"
            title="המיקום שלי בפרק"
          >
            📍
          </button>
        )}
        <button
          className="slim-header-toc"
          onClick={tocOpen ? closeTOC : openTOC}
          aria-label={tocOpen ? "סגירת תוכן העניינים" : "פתיחת תוכן העניינים"}
          title={tocOpen ? "סגור" : "תוכן עניינים"}
        >
          {tocOpen ? "✕" : "☰"}
        </button>
        <div className="slim-header-row">
          {tocOpen
            ? "תוכן עניינים"
            : showPaywall
              ? "«שאלת האריה» — הסדנה המלאה"
              : `פרק ${chapter} — ${currentChapterTitle}`}
        </div>
        <div className="slim-progress">
          <div
            className="slim-progress-bar"
            style={{
              width: `${Math.round(
                (tocOpen ? 0 : headerProgress) * 100
              )}%`,
            }}
          />
        </div>
      </div>

      {showOfferStrip && !tocOpen && (
        <button
          type="button"
          className="offer-strip"
          onClick={() => {
            try {
              (window as any).fbq?.("trackCustom", "OfferStripClick");
            } catch {}
            setChapter(2);
          }}
          aria-label="לפרטי הסדנה המלאה"
        >
          <span>
            פחות התפרצויות, יותר שקט · <b>12 מפגשים</b> ←
          </span>
          <span
            role="button"
            tabIndex={0}
            className="offer-strip-x"
            aria-label="הסתרת הרצועה"
            onClick={(e) => {
              e.stopPropagation();
              dismissOfferStrip();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.stopPropagation();
                dismissOfferStrip();
              }
            }}
          >
            ✕
          </span>
        </button>
      )}

      {tocOpen && (
        <div className="toc-page">
          <button className="toc-back" onClick={closeTOC}>
            ← חזרה {prevChapter !== null ? `לפרק ${prevChapter}` : ""}
          </button>
          <div className="toc-h">תוכן עניינים</div>
          <div className="toc-sub">
            12 פרקים ב־App · 40 פרקי הספר המקורי · לחיצה על שם → מעבר ישיר
          </div>

          {/* ⭐ המקומות שלי — reader-saved spots (login required). */}
          {user && (
            <div className="bm-list">
              <div className="bm-list-h">⭐ המקומות שלי</div>
              {bookmarks.length === 0 ? (
                <div className="bm-empty">
                  עדיין לא שמרתם מקומות. בזמן הקריאה לחצו על 📍 בכותרת, ואז
                  "שמירה לרשימת המקומות שלי" — והמקום יופיע כאן.
                </div>
              ) : (
                bookmarks.map((b) => (
                  <div key={b.id} className="bm-row">
                    <div
                      className="bm-main"
                      onClick={() => returnToBookmark(b)}
                      role="button"
                      tabIndex={0}
                    >
                      <div className="bm-title">{b.title}</div>
                      <div className="bm-meta">
                        פרק {b.chapter}
                        {b.section_label ? ` · ${b.section_label}` : ""}
                      </div>
                    </div>
                    <div className="bm-actions">
                      <button
                        type="button"
                        className="bm-act"
                        title="חזרה למקום"
                        aria-label="חזרה למקום"
                        onClick={() => returnToBookmark(b)}
                      >
                        ↩︎
                      </button>
                      <button
                        type="button"
                        className="bm-act"
                        title="העתקת קישור לשיתוף"
                        aria-label="העתקת קישור לשיתוף"
                        onClick={() => copyBookmarkLink(b)}
                      >
                        {copiedBookmarkId === b.id ? "✓" : "🔗"}
                      </button>
                      <button
                        type="button"
                        className="bm-act bm-del"
                        title="מחיקה"
                        aria-label="מחיקת הסימנייה"
                        onClick={() => removeBookmark(b.id)}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {TOC.map((c) => (
            <div key={c.n} className="toc-card">
              <div
                className="toc-chh"
                onClick={() => goToChapter(c.n)}
                role="button"
                tabIndex={0}
              >
                <div className="toc-cicon">{c.icon}</div>
                <div className="toc-cmeta">
                  <div className="toc-cnum">פרק {c.n}</div>
                  <div className="toc-ctit">{c.title}</div>
                  <div className="toc-cbook">{c.bookCh}</div>
                </div>
                <div className="toc-arrow">←</div>
              </div>
              {c.subs && c.subs.length > 0 && (
                <div className="toc-subs">
                  {c.subs.map((s) => (
                    <div
                      key={s.idx}
                      className="toc-sub-link"
                      onClick={() => goToChapter(c.n, s.idx)}
                      role="button"
                      tabIndex={0}
                    >
                      <span className="toc-sub-num">{c.n}.{s.idx + 1}</span>
                      <span className="toc-sub-tit">{s.title}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {!tocOpen && (
      <>

      {/* On a locked chapter the offer IS the page (Shlomo, 6.8: the hero +
          bars pushed the offer below the fold) — hide the chapter chrome. */}
      {!started && !showPaywall && (
      <div className={"bh" + (chapter === 1 ? "" : " bh-slim")}>
        {/* Holiday gift ribbon (Shlomo, 10.9: "להניח את המתנה ממש בראש
            הדף"). One line above the headline so the ad visitor still meets
            the bell sentence first; the ribbon leads to /gift. */}
        {chapter === 1 && (
          <a className="gift-ribbon" href="/gift?s=ribbon" onClick={() => logSiteEvent("ribbon_click")}>
            <b>🎁 פיתוח אינטליגנציה רגשית זו מתנה לחיים</b>
            <span>89 ₪ · לרכישה כמתנה ←</span>
          </a>
        )}
        {/* The taste film (Shlomo, 13.9: "תעלה") - meeting 1 in five minutes,
            measured against purchases via video_events. */}
        {chapter === 1 && <TasteVideo />}
        {/* Landing hierarchy (Shlomo, 7.8): a cold visitor must meet THE
            OFFERING first - "סדנה באינטליגנציה רגשית" as the headline - and
            only then the meeting name. Chapters 2-12 keep chapter-first. */}
        {chapter === 1 ? (
          <>
            {/* "The page IS the ad" (Shlomo, 20.8): a visitor arriving from
                the bell post must meet the exact sentence that made them
                click, then three "what will happen to you" promise lines —
                not "what's in the book". The old offering headline moved
                aside; meeting name demoted to the sub line. */}
            <div className="bt">«שאלת האריה» · סדנה בספר מאת שלמה אלפנבוים</div>
            <div className="ct">
              מערכת הישרדות היפראקטיבית היא לא גזר דין.
              <br />
              היא מנגנון - ומנגנון אפשר לעדן.
            </div>
            <div className="bh-promise">
              <div>✓ תזהי את הרגע שבו הגוף נדלק - <em>לפני</em> שהמילים יוצאות</div>
              <div>✓ תלמדי שאלה אחת, בת שלוש מילים - עצם המחשבה עליה כבר עושה את העבודה</div>
              <div>✓ 12 מפגשים - פחות התפרצויות, שינה עמוקה יותר, בית שקט יותר</div>
            </div>
            <div className="bh-open">
              המפגש הראשון פתוח - התחילי עכשיו, בלי הרשמה
            </div>
            <div className="ct-sub bh-sub">מפגש 1: יש כאן אריה, באמת? - ביני לבין עצמי</div>
          </>
        ) : (
          <>
            <div className="bt">שאלת האריה - סדנה בספר</div>
            <div className="ct">
              פרק {chapter}: {currentChapterTitle}
            </div>
          </>
        )}
        {/* The host-welcome block that lived here (orgs, 20 years, price,
            "pay after you taste") moved to the end-of-chapter CTA card on
            16.8 (Shlomo): the first 3 seconds now belong to the reader's
            own voice — chapter-1.html opens with her sentences. */}
        {/* "פרק N מתוך 12" lives only in the bottom nav (Shlomo, 12.10). */}
      </div>
      )}

      {/* Testimonials strip (Shlomo, 20.8) — the trust layer the funnel
          research flagged as missing. First real quotes; more to come. */}
      {!started && !showPaywall && chapter === 1 && (
        <div className="testi-strip">
          <div className="testi">
            <div className="testi-q">"זה כל כך פשוט וכל כך עובד - שזה מטורף."</div>
            <div className="testi-n">- ניב, משתתף סדנה</div>
          </div>
          <div className="testi">
            <div className="testi-q">"שמעתי, לא האמנתי - עד שניסיתי. ומאז זה כבר באוטומט."</div>
            <div className="testi-n">- אלונה, משתתפת סדנה</div>
          </div>
          <div className="testi">
            <div className="testi-q">"שינוי כזה בדרך הפעולה צפוי היה להיות ארוך ומורכב. אבל לא כאן - זה אשכרה פטנט רגשי."</div>
            <div className="testi-n">- ירון, משתתף סדנה</div>
          </div>
        </div>
      )}

      {/* Mode bar. The explanatory sentence appears ONLY in chapter 1 —
          new readers land there and need it once. From chapter 2 on the
          pinned bar is buttons-only (Shlomo, 30.7: the full text steals
          too much phone-screen height on every chapter). */}
      {!showPaywall && (
      <div className={"mb" + (chapter === 1 ? "" : " mb-compact")}>
        {chapter === 1 && (
          <span className="ml">
            איך תרצי לצרוך את הפרק? אולי גם תקשיבי וגם תקראי - כך תחווי
            סדנה הכי קרובה למציאות.
          </span>
        )}
        <button
          className={"mbtn " + (!modeChosen ? "neutral" : mode === "read" ? "active" : "")}
          onClick={() => { setModeChosen(true); switchToRead(); }}
        >
          קריאה
        </button>
        <button
          className={"mbtn " + (!modeChosen ? "neutral" : mode === "listen" ? "active" : "")}
          onClick={() => { setModeChosen(true); switchToListen(); }}
          aria-label="האזנה לפרק"
        >
          <span aria-hidden="true" style={{ marginInlineEnd: 6 }}>🎧</span>
          האזנה
        </button>
      </div>
      )}

      {/* Audio: each chapter has a /chapter-N-audio.mp3 in /public/, narrated
          in Shlomo's cloned voice (see scripts/generate_chapter_audio.py).
          Per-section start times come from /chapter-N-sections.json
          (see scripts/align_chapter_sections.py). */}
      {mode === "listen" && chapter >= 1 && chapter <= 12 && !showPaywall ? (
        <AudioPlayer
          src={`/chapter-${chapter}-audio.mp3`}
          title={`פרק ${chapter}`}
          storageKey={`ei_audio_ch${chapter}`}
          // Spotify-style cross-device resume. cloudPosition arrives from
          // useCloudProgress after the initial fetch; the player takes
          // max(local, cloud) so neither side loses progress.
          cloudPosition={frozenCloudPos}
          onCloudPositionChange={handleCloudPositionChange}
          // When the reader picked a TOC sub for THIS chapter, hand the
          // section index over so AudioPlayer can fetch the alignment
          // JSON and seek directly. Reset to null on consumption so a
          // later "rewind to start" or "switch chapter" works as usual.
          chapter={chapter}
          seekToSectionIdx={
            pendingAudioSection && pendingAudioSection.chapter === chapter
              ? pendingAudioSection.idx
              : null
          }
          onSectionSeekConsumed={handleSectionSeekConsumed}
          onTimeUpdate={setCurrentAudioTime}
          command={audioCommand}
          titleSlot={audioTitleSlot}
        />
      ) : null}

      {/* Dubbing-QA 🚩: admin-only floating flag button in listen mode.
          A tap records chapter + current second to audio_flags; the
          offline tooling maps the second to the exact spoken words. */}
      {mode === "listen" && isAdmin && user ? (
        <div
          dir="rtl"
          style={{
            position: "fixed",
            bottom: 18,
            insetInlineStart: 14,
            zIndex: 80,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 8,
          }}
        >
          {lastAudioFlag ? (
            <div
              style={{
                background: "var(--card, #fff)",
                border: "1px solid rgba(0,0,0,.15)",
                borderRadius: 12,
                boxShadow: "0 4px 14px rgba(0,0,0,.18)",
                padding: "10px 12px",
                maxWidth: 260,
                fontSize: 14,
              }}
            >
              <div style={{ marginBottom: 6 }}>
                🚩 נרשם ב-
                {Math.floor(lastAudioFlag.sec / 60)}:
                {String(Math.floor(lastAudioFlag.sec % 60)).padStart(2, "0")} ✓
              </div>
              <input
                value={audioFlagNote}
                onChange={(e) => setAudioFlagNote(e.target.value)}
                placeholder="הערה (רשות): נשמע כמו..."
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  padding: "6px 8px",
                  borderRadius: 8,
                  border: "1px solid rgba(0,0,0,.2)",
                  fontSize: 14,
                }}
              />
              <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                <button
                  onClick={saveAudioFlagNote}
                  style={{
                    padding: "5px 12px",
                    borderRadius: 8,
                    border: "none",
                    background: "#2e7d32",
                    color: "#fff",
                    cursor: "pointer",
                  }}
                >
                  שמור
                </button>
                <button
                  onClick={() => setLastAudioFlag(null)}
                  style={{
                    padding: "5px 12px",
                    borderRadius: 8,
                    border: "1px solid rgba(0,0,0,.2)",
                    background: "transparent",
                    cursor: "pointer",
                  }}
                >
                  סגור
                </button>
              </div>
            </div>
          ) : null}
          {audioFlagError ? (
            <div
              style={{
                background: "#c62828",
                color: "#fff",
                borderRadius: 10,
                padding: "6px 10px",
                fontSize: 13,
              }}
            >
              {audioFlagError}
            </div>
          ) : null}
          <button
            onClick={fileAudioFlag}
            disabled={audioFlagBusy}
            aria-label="סמן טעות בדיבוב בנקודה הנוכחית"
            title="סמן טעות בדיבוב בנקודה הנוכחית"
            style={{
              width: 54,
              height: 54,
              borderRadius: "50%",
              border: "none",
              background: audioFlagBusy ? "#9e9e9e" : "#d84315",
              color: "#fff",
              fontSize: 24,
              boxShadow: "0 4px 12px rgba(0,0,0,.3)",
              cursor: audioFlagBusy ? "wait" : "pointer",
            }}
          >
            🚩
          </button>
        </div>
      ) : null}

      {/* CHAPTER NAVIGATION - TOP (hidden on locked chapters: the offer
          screen has its own back-to-chapter-1 link, and every row above
          the offer pushes it further below the fold) */}
      {!showPaywall && <ChapterNav />}

      {chapter === 1 && (
        <>
          {/* Chapter 1 prose lives in src/chapters/chapter-1.html and is
              synced to the book_content table via the sync-book-content
              workflow on every push to main. No fallback — the prose
              lives only in Supabase, never in the JS bundle. */}
          <ChapterContent
            chapter={1}
            onSectionListen={sectionListenHandler}
            widgets={{
              // Workshop frames (29.10.2026): ten steps on one moment,
              // mounted at data-widget-anchor="ch1_fN" (src/data/ch1Frames.ts).
              // Steps 2 and 7 are short additions under the existing
              // positive_actions / reflection_form widgets below.
              ...Object.fromEntries(
                CH1_FRAMES.map((f) => [
                  f.id,
                  <FrameBox key={f.id} f={f} fields={fields} setFields={setFields} save={<InlineSave />} />,
                ]),
              ),
              // The audio_s1..s5 anchors used to host SpeechSynthesis
              // "play this section" buttons. Now that chapter 1 is
              // narrated via the chapter MP3 (see /chapter-1-audio.mp3)
              // and the alignment JSON drives per-section seeks, these
              // inline section buttons are obsolete. Left as null so
              // existing HTML keeps rendering cleanly; future per-section
              // controls go through the listen-from-section flow.
              audio_s1: null,
              audio_s2: null,
              audio_s3: null,
              audio_s4: null,
              audio_s5: null,
              reflection_form: (
                <>
                  <div className="rg">
                    {FIELDS_DATA.map((f) => (
                      <div key={f.id}>
                        <label className="fl">{f.label}</label>
                        <textarea
                          className="ri"
                          placeholder="כתבי כאן..."
                          rows={2}
                          value={fields[f.id] || ""}
                          onChange={(e) =>
                            setFields((r) => ({ ...r, [f.id]: e.target.value }))
                          }
                        />
                      </div>
                    ))}
                    <div>
                      <label className="fl">
                        התכונות שלי, מה חשוב לי, מהו האופי שלי:
                      </label>
                      <span className="fs">
                        אין נכון ולא נכון, אין טוב או רע. יש רק מה שרץ לך בראש
                        ובמילים שלך.
                      </span>
                      <textarea
                        className="ri"
                        placeholder="כתבי כאן בחופשיות מוחלטת..."
                        rows={6}
                        value={freeText}
                        onChange={(e) => setFreeText(e.target.value)}
                      />
                      <div className="cc">{freeText.length} תווים</div>
                    </div>
                  </div>
                  <InlineSave />
                </>
              ),
              // Decision moment (23.8 audit): the purchase ask used to sit 23
              // screens down on mobile, after a 38-second average visit. This
              // soft card appears right after the promise — one door to the
              // offer screen, one "keep reading for free". Unpaid readers only.
              // Save-for-later hook (29.8): the median ad visitor spends
              // ~26s and leaves with no small ask on the way. Right after
              // the tool card, anonymous visitors only.
              save_for_later: !user ? <LeadCaptureCard variant="save" /> : null,
              decision_moment: !hasAccess && !accessLoading ? (
                <div className="decide">
                  <div className="decide-t">רוצה את כל 12 המפגשים?</div>
                  <div className="decide-b">
                    המשך הסדנה - בקולי, בקצב שלך, לתמיד. 89 ₪, והתשלום רק אחרי שטעמת.
                  </div>
                  <div className="decide-row">
                    <button className="decide-btn" onClick={() => setChapter(2)}>
                      להמשך הסדנה ←
                    </button>
                    <span className="decide-alt">או פשוט להמשיך לקרוא בחינם ↓</span>
                  </div>
                </div>
              ) : null,
              positive_actions: (
                <>
                  <label className="fl">
                    הפעולות שמזמנות בי רגשות חיוביים:
                  </label>
                  <textarea
                    className="ri"
                    rows={5}
                    placeholder="למשל: צחוק עם חברה, מילה טובה, מוזיקה, חיבוק, טיול בטבע, רגע של הודיה... (אפשר להיעזר ב-AI)"
                    value={fields["ch1_pos_actions"] || ""}
                    onChange={(e) =>
                      setFields((r) => ({ ...r, ch1_pos_actions: e.target.value }))
                    }
                  />
                  <div className="cc">
                    {(fields["ch1_pos_actions"] || "").length} תווים
                  </div>
                  <InlineSave />
                  {/* Lead capture (funnel leak #2): right after the first
                      exercise — the moment a connected visitor is most
                      likely to leave an email. Anonymous visitors only. */}
                  {!user && <LeadCaptureCard />}
                </>
              ),
              practice_log: (
                <>
                  <textarea
                    className="ri"
                    rows={5}
                    placeholder="תאר את המצב, הרגש שעלה, ומה קרה אחרי שניסית את התרגיל..."
                    value={pracLog}
                    onChange={(e) => setPracLog(e.target.value)}
                  />
                  <div className="cc">{pracLog.length} תווים</div>
                  <InlineSave />
                </>
              ),
            }}
            fallback={null}
          />

          {/* End-of-free-chapter bridge (Shlomo, 6.8): the first meeting
              must END with an invitation to continue, not a bare
              next-chapter button. Anonymous / unpaid readers only. */}
          {!hasAccess && !accessLoading && !authLoading && (
            <div className="ch1-cta">
              {/* Reader-voice rewrite (16.8): the closing prose ("מילה ממני
                  אלייך") now lives in chapter-1.html; this card carries only
                  the price + button, so paying readers never see a price. */}
              <div className="ch1-cta-title">
                הסדנה כולה - 12 מפגשים, בקצב שלך, בבית, בקולי - 89 ₪
              </div>
              <div className="ch1-cta-body">
                סדנה כזו הועברה עד היום כמעט רק בתוך ארגונים - אל על, אגד,
                צה״ל ומשרדי ממשלה. מהיום היא פתוחה לכל אחת ואחד: שיחות
                עומק בינך לבין עצמך, בהנחיית מנחה עם ניסיון של 20 שנה,
                תרגילים ממש כמו בסדנה. כל מה שתכתבי יישמר לך - לקובץ שילווה
                אותך בשנים הבאות - ותוכלי לחזור למפגשים שוב ושוב.
                <br />
                פחות ממה שעולה ערב אחד בחוץ - והתשלום רק אחרי שטעמת
                והחלטת שזה בשבילך. אני מבטיח לך: תופתעי מההתחלה ועד הסוף.
                <span style={{ display: "block", marginTop: 6, fontWeight: 500 }}>
                  שלך, שלמה אלפנבוים
                </span>
              </div>
              {/* Strongest participant quote (20.8) — social proof at the
                  moment of decision. */}
              <div className="ch1-cta-quote">
                "שינוי כזה בדרך הפעולה צפוי היה להיות ארוך ומורכב. אבל לא
                כאן - זה אשכרה פטנט רגשי." <span>- ירון, משתתף סדנה</span>
              </div>
              <button
                className="ch1-cta-btn"
                onClick={() => setChapter(2)}
                aria-label="להמשך הסדנה"
              >
                אני רוצה להמשיך בסדנה - 89 ₪ ←
              </button>
              <div className="ch1-cta-fine">
                הרשמה חינם · תשלום רק אחרי שהחלטתם · אפשר גם בביט ובכרטיס
                ישראלי · או בוואטסאפ: 054-4562084
              </div>
            </div>
          )}

          <div style={{ margin: "32px 0 8px" }}>
            <ChapterNav bottom={true} />
          </div>
        </>
      )}

      {showPaywall && (
        <PaywallGate
          chapter={chapter}
          chapterTitle={currentChapterTitle}
          onBackToChapter1={() => setChapter(1)}
        />
      )}

      {chapter === 2 && !showPaywall && (
        <>
          {/* ===== CHAPTER 2 ===== */}
          {/* Chapter 2 prose lives in src/chapters/chapter-2.html and is
              synced to the book_content table via the sync-book-content
              workflow on every push to main. Since 26.10.2026 every section
              ends with an image + exercise box (static HTML) and a writing
              frame mounted here at data-widget-anchor="ch2_fN" (config in
              src/data/ch2Frames.ts). */}
          <ChapterContent
            chapter={2}
            onSectionListen={sectionListenHandler}
            widgets={Object.fromEntries(
              CH2_FRAMES.map((f) => [
                f.id,
                <FrameBox key={f.id} f={f} fields={fields} setFields={setFields} save={<InlineSave />} />,
              ]),
            )}
            fallback={null}
          />

          <div style={{ margin: "32px 0 8px" }}>
            <ChapterNav bottom={true} />
          </div>
        </>
      )}

      {chapter === 3 && !showPaywall && (
        <>
          {/* ===== CHAPTER 3 ===== */}
          {/* Chapter 3 prose lives in src/chapters/chapter-3.html and is
              synced to the book_content table via the sync-book-content
              workflow on every push to main. Since 29.10.2026 every section
              ends with an image + exercise box (static HTML) and a writing
              frame mounted here at data-widget-anchor="ch3_fN" (config in
              src/data/ch3Frames.ts). */}
          <ChapterContent
            chapter={3}
            onSectionListen={sectionListenHandler}
            widgets={Object.fromEntries(
              CH3_FRAMES.map((f) => [
                f.id,
                <FrameBox key={f.id} f={f} fields={fields} setFields={setFields} save={<InlineSave />} />,
              ]),
            )}
            fallback={null}
          />

          <div style={{ margin: "32px 0 8px" }}>
            <ChapterNav bottom={true} />
          </div>
        </>
      )}

      {chapter === 4 && !showPaywall && (
        <>
          {/* ===== CHAPTER 4 ===== */}
          {/* Chapter 4 prose lives in src/chapters/chapter-4.html and is
              synced to the book_content table via the sync-book-content
              workflow on every push to main. One widget anchor:
              past_present_table — the 4-row textarea grid (cells persist
              to fields[`ch4_t1_rN_cM`] + InlineSave button below).
              Since 30.10.2026 twelve workshop frames are mounted at
              data-widget-anchor="ch4_fN" (src/data/ch4Frames.ts). Since
              2.11.2026 the eight old lecture slides are one tappable deck
              (LiveSlides, src/data/ch4Slides.ts) at ch4_slides, and the
              ten-step map alone at ch4_map. */}
          <ChapterContent
            chapter={4}
            onSectionListen={sectionListenHandler}
            widgets={{
              ...Object.fromEntries(
                CH4_FRAMES.map((f) => [
                  f.id,
                  <FrameBox key={f.id} f={f} fields={fields} setFields={setFields} save={<InlineSave />} />,
                ]),
              ),
              ch4_slides: <LiveSlides slides={CH4_SLIDES} fields={fields} setFields={setFields} save={<InlineSave />} />,
              ch4_map: <LiveSlides slides={[CH4_MAP_SLIDE]} fields={fields} setFields={setFields} compact />,
              past_present_table: (
                <>
                  <div style={{ overflowX: "auto", margin: "16px 0" }}>
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        fontSize: 13,
                        border: "1px solid var(--sand)",
                        borderRadius: 8,
                        overflow: "hidden",
                      }}
                    >
                      <thead>
                        <tr style={{ background: "var(--bg2)" }}>
                          <th style={{ padding: 10, textAlign: "right", borderBottom: "2px solid var(--warm)", fontWeight: 600 }}>מה קרה?</th>
                          <th style={{ padding: 10, textAlign: "right", borderBottom: "2px solid var(--warm)", fontWeight: 600 }}>פרשנות ותגובה בעבר</th>
                          <th style={{ padding: 10, textAlign: "right", borderBottom: "2px solid var(--warm)", fontWeight: 600 }}>פרשנות ותגובה היום</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[1, 2, 3, 4].map((i) => (
                          <tr key={i}>
                            <td style={{ padding: 6, border: "1px solid var(--sand)", verticalAlign: "top" }}>
                              <textarea
                                value={fields[`ch4_t1_r${i}_c1`] || ""}
                                onChange={(e) => setFields({ ...fields, [`ch4_t1_r${i}_c1`]: e.target.value })}
                                className="ta"
                                rows={3}
                                placeholder="מה קרה..."
                                style={{ width: "100%", border: "none", background: "transparent", fontFamily: "inherit", fontSize: 13, direction: "rtl", resize: "vertical" }}
                              />
                            </td>
                            <td style={{ padding: 6, border: "1px solid var(--sand)", verticalAlign: "top" }}>
                              <textarea
                                value={fields[`ch4_t1_r${i}_c2`] || ""}
                                onChange={(e) => setFields({ ...fields, [`ch4_t1_r${i}_c2`]: e.target.value })}
                                className="ta"
                                rows={3}
                                placeholder="פרשנות ותגובה בעבר..."
                                style={{ width: "100%", border: "none", background: "transparent", fontFamily: "inherit", fontSize: 13, direction: "rtl", resize: "vertical" }}
                              />
                            </td>
                            <td style={{ padding: 6, border: "1px solid var(--sand)", verticalAlign: "top" }}>
                              <textarea
                                value={fields[`ch4_t1_r${i}_c3`] || ""}
                                onChange={(e) => setFields({ ...fields, [`ch4_t1_r${i}_c3`]: e.target.value })}
                                className="ta"
                                rows={3}
                                placeholder="פרשנות ותגובה היום..."
                                style={{ width: "100%", border: "none", background: "transparent", fontFamily: "inherit", fontSize: 13, direction: "rtl", resize: "vertical" }}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ marginTop: 14 }}>
                    <label className="fl">
                      ורגע שמח לסיום: מתי אירוע נראה "שלילי" לשנייה, אבל
                      לא נתת לו לצלול פנימה - וכמה מהר הרגש התהפך לטובה?
                    </label>
                    <textarea
                      className="ri"
                      rows={3}
                      placeholder="למשל: נבהלתי מהערה, שאלתי אם יש כאן סכנה, ותוך דקה כבר חייכתי..."
                      value={fields["ch4_positive_flip"] || ""}
                      onChange={(e) =>
                        setFields({ ...fields, ch4_positive_flip: e.target.value })
                      }
                    />
                  </div>
                  <InlineSave />
                </>
              ),
            }}
            fallback={null}
          />

          <div style={{ margin: "32px 0 8px" }}>
            <ChapterNav bottom={true} />
          </div>
        </>
      )}

      {chapter === 5 && !showPaywall && (
        <>
          {/* ===== CHAPTER 5: מודל הרמזור ===== */}
          {/* Chapter 5 prose lives in src/chapters/chapter-5.html and is
              synced to the book_content table via the sync-book-content
              workflow on every push to main. Two widget anchors:
              negative_emotions_table + positive_emotions_table — each is
              a 5-row table of text inputs persisted via fields[] + a
              SaveAndJump button that scrolls to the other table. The
              wrapper divs carry the ch5-negative-emotions /
              ch5-positive-emotions ids so SaveAndJump's scrollIntoView
              still finds them. Since 30.10.2026 eleven workshop frames are
              mounted at data-widget-anchor="ch5_fN" (src/data/ch5Frames.ts). */}
          <ChapterContent
            chapter={5}
            onSectionListen={sectionListenHandler}
            widgets={{
              ...Object.fromEntries(
                CH5_FRAMES.map((f) => [
                  f.id,
                  <FrameBox key={f.id} f={f} fields={fields} setFields={setFields} save={<InlineSave />} />,
                ]),
              ),
              reflection_questions: (
                <>
                  <div className="rg">
                    {[
                      "1. מתי לאחרונה הרגשתי כעס / פחד / אכזבה / דחק?",
                      "2. מה היה האירוע?",
                      "3. מה חשבתי באותו רגע?",
                      "4. אילו תחושות הופיעו בגוף שלי?",
                      "5. איזו פעולה עשיתי מיד אחרי?",
                      "6. בדיעבד - האם באמת היה שם סיכון?",
                      "7. אילו תוצאות היו אילו הייתי שואלת את עצמי: \"האם יש כאן סכנת מוות?\"",
                    ].map((q, i) => (
                      <div key={i}>
                        <label className="fl">{q}</label>
                        <textarea
                          className="ri"
                          placeholder="כתבי כאן..."
                          rows={2}
                          value={fields[`ch5_q${i + 1}`] || ""}
                          onChange={(e) => setFields({ ...fields, [`ch5_q${i + 1}`]: e.target.value })}
                        />
                      </div>
                    ))}
                  </div>
                  <InlineSave />
                </>
              ),
              negative_emotions_table: (
                <div id="ch5-negative-emotions" style={{ overflowX: "auto", margin: "8px 0 16px" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: 13,
                      border: "1px solid var(--sand)",
                      borderRadius: 8,
                      overflow: "hidden",
                    }}
                  >
                    <thead>
                      <tr style={{ background: "#FFEBEE" }}>
                        <th style={{ padding: 10, textAlign: "right", borderBottom: "2px solid #EF9A9A", fontWeight: 600, color: "#C62828" }}>רגש שלילי</th>
                        <th style={{ padding: 10, textAlign: "right", borderBottom: "2px solid #EF9A9A", fontWeight: 600, color: "#C62828" }}>ביטוי התנהגותי</th>
                        <th style={{ padding: 10, textAlign: "right", borderBottom: "2px solid #EF9A9A", fontWeight: 600, color: "#C62828" }}>השלכות</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr style={{ background: "#FFFAFA" }}>
                        <td style={{ padding: 10, border: "1px solid var(--sand)", fontStyle: "italic", color: "var(--muted)" }}>דוגמה: עצבנית</td>
                        <td style={{ padding: 10, border: "1px solid var(--sand)", fontStyle: "italic", color: "var(--muted)" }}>מרימה קול או מסתגרת לשעה / יומיים</td>
                        <td style={{ padding: 10, border: "1px solid var(--sand)", fontStyle: "italic", color: "var(--muted)" }}>אנשים מתרחקים, העסק נפגע, נהיית חולה</td>
                      </tr>
                      {[1, 2, 3, 4, 5].map((i) => (
                        <tr key={i}>
                          <td style={{ padding: 6, border: "1px solid var(--sand)" }}>
                            <input
                              type="text"
                              value={fields[`ch5_t1_r${i}_c1`] || ""}
                              onChange={(e) => setFields({ ...fields, [`ch5_t1_r${i}_c1`]: e.target.value })}
                              style={{ width: "100%", border: "none", outline: "none", background: "transparent", fontFamily: "'Heebo', sans-serif", fontSize: 13, padding: 4 }}
                            />
                          </td>
                          <td style={{ padding: 6, border: "1px solid var(--sand)" }}>
                            <input
                              type="text"
                              value={fields[`ch5_t1_r${i}_c2`] || ""}
                              onChange={(e) => setFields({ ...fields, [`ch5_t1_r${i}_c2`]: e.target.value })}
                              style={{ width: "100%", border: "none", outline: "none", background: "transparent", fontFamily: "'Heebo', sans-serif", fontSize: 13, padding: 4 }}
                            />
                          </td>
                          <td style={{ padding: 6, border: "1px solid var(--sand)" }}>
                            <input
                              type="text"
                              value={fields[`ch5_t1_r${i}_c3`] || ""}
                              onChange={(e) => setFields({ ...fields, [`ch5_t1_r${i}_c3`]: e.target.value })}
                              style={{ width: "100%", border: "none", outline: "none", background: "transparent", fontFamily: "'Heebo', sans-serif", fontSize: 13, padding: 4 }}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <SaveAndJump jumpTo="ch5-positive-emotions" jumpLabel="↓ קפוץ לטבלת הרגשות החיוביים" />
                </div>
              ),
              positive_emotions_table: (
                <div id="ch5-positive-emotions" style={{ overflowX: "auto", margin: "8px 0 16px" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: 13,
                      border: "1px solid var(--sand)",
                      borderRadius: 8,
                      overflow: "hidden",
                    }}
                  >
                    <thead>
                      <tr style={{ background: "#E8F5E9" }}>
                        <th style={{ padding: 10, textAlign: "right", borderBottom: "2px solid #A5D6A7", fontWeight: 600, color: "#2E7D32" }}>רגש חיובי</th>
                        <th style={{ padding: 10, textAlign: "right", borderBottom: "2px solid #A5D6A7", fontWeight: 600, color: "#2E7D32" }}>ביטוי התנהגותי</th>
                        <th style={{ padding: 10, textAlign: "right", borderBottom: "2px solid #A5D6A7", fontWeight: 600, color: "#2E7D32" }}>משמעויות</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr style={{ background: "#FAFFFA" }}>
                        <td style={{ padding: 10, border: "1px solid var(--sand)", fontStyle: "italic", color: "var(--muted)" }}>דוגמה: שמחה</td>
                        <td style={{ padding: 10, border: "1px solid var(--sand)", fontStyle: "italic", color: "var(--muted)" }}>מחייכת, מחבקת, נותנת מחמאות</td>
                        <td style={{ padding: 10, border: "1px solid var(--sand)", fontStyle: "italic", color: "var(--muted)" }}>ביטחון עצמי עולה, יצירתיות, אנשים מתקרבים</td>
                      </tr>
                      {[1, 2, 3, 4, 5].map((i) => (
                        <tr key={i}>
                          <td style={{ padding: 6, border: "1px solid var(--sand)" }}>
                            <input
                              type="text"
                              value={fields[`ch5_t2_r${i}_c1`] || ""}
                              onChange={(e) => setFields({ ...fields, [`ch5_t2_r${i}_c1`]: e.target.value })}
                              style={{ width: "100%", border: "none", outline: "none", background: "transparent", fontFamily: "'Heebo', sans-serif", fontSize: 13, padding: 4 }}
                            />
                          </td>
                          <td style={{ padding: 6, border: "1px solid var(--sand)" }}>
                            <input
                              type="text"
                              value={fields[`ch5_t2_r${i}_c2`] || ""}
                              onChange={(e) => setFields({ ...fields, [`ch5_t2_r${i}_c2`]: e.target.value })}
                              style={{ width: "100%", border: "none", outline: "none", background: "transparent", fontFamily: "'Heebo', sans-serif", fontSize: 13, padding: 4 }}
                            />
                          </td>
                          <td style={{ padding: 6, border: "1px solid var(--sand)" }}>
                            <input
                              type="text"
                              value={fields[`ch5_t2_r${i}_c3`] || ""}
                              onChange={(e) => setFields({ ...fields, [`ch5_t2_r${i}_c3`]: e.target.value })}
                              style={{ width: "100%", border: "none", outline: "none", background: "transparent", fontFamily: "'Heebo', sans-serif", fontSize: 13, padding: 4 }}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <SaveAndJump jumpTo="ch5-negative-emotions" jumpLabel="↑ חזרה לטבלת הרגשות השליליים" />
                </div>
              ),
            }}
            fallback={null}
          />

          <div style={{ margin: "32px 0 8px" }}>
            <ChapterNav bottom={true} />
          </div>
        </>
      )}

      {chapter === 6 && !showPaywall && (
        <div className="ch-numbered ch-numbered-6">
          {/* ===== CHAPTER 6 = book ch 10: חוק המראות ===== */}
          {/* Chapter 6 prose lives in src/chapters/chapter-6.html and is
              synced to the book_content table via the sync-book-content
              workflow on every push to main. Two widget anchors:
              properties_table (5×6 input grid + mobile cards + jump-to-
              rulers + save) and rulers_table (10 ruler rows + jump-back-
              to-properties + save). Outer ch-numbered-6 wrapper stays
              here so chapter-6 CSS scoping continues to work. Fallback
              kept until verified live; dropped in a follow-up PR.
              Since 30.10.2026 eleven workshop frames are mounted at
              data-widget-anchor="ch6_fN" (src/data/ch6Frames.ts). */}
          <ChapterContent
            chapter={6}
            onSectionListen={sectionListenHandler}
            widgets={{
              ...Object.fromEntries(
                CH6_FRAMES.map((f) => [
                  f.id,
                  <FrameBox key={f.id} f={f} fields={fields} setFields={setFields} save={<InlineSave />} />,
                ]),
              ),
              names_table: (
                <>
                  <div id="ch6-names-table" className="props-table-wrap" style={{ marginTop: 8, marginBottom: 16, overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, tableLayout: "fixed" }}>
                      <thead><tr>
                        {CH6_PEOPLE.map((pp, c) => (
                          <th key={c} style={{ padding: 10, background: pp.bg, border: "1px solid var(--sand)", textAlign: "right", verticalAlign: "top", width: "20%" }}>
                            <div style={{ fontWeight: 700, color: pp.color, fontSize: 14 }}>{pp.label}</div>
                            <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>{pp.sub}</div>
                          </th>
                        ))}
                      </tr></thead>
                      <tbody><tr>
                        {[0, 1, 2, 3, 4].map((c) => {
                          const fk = `ch6_t1_name_c${c}`;
                          return (
                            <td key={c} style={{ padding: 4, border: "1px solid var(--sand)" }}>
                              <input type="text" value={fields[fk] || ""} onChange={(e) => setFields({ ...fields, [fk]: e.target.value })} placeholder="שם..." style={{ width: "100%", padding: "10px 12px", border: "1.5px solid var(--sand)", borderRadius: 8, background: "var(--bg2)", fontSize: 13, fontFamily: "inherit", textAlign: "right", direction: "rtl" }} />
                            </td>
                          );
                        })}
                      </tr></tbody>
                    </table>
                  </div>
                  {CH6_PEOPLE.map((person, c) => {
                    const fk = `ch6_t1_name_c${c}`;
                    return (
                      <div key={c} className="props-person-card" style={{ background: person.bg }}>
                        <div className="props-person-head" style={{ color: person.color }}>
                          <span>{person.label}</span>
                          <span className="props-person-sub">{person.sub}</span>
                        </div>
                        <div className="props-trait-row">
                          <label className="props-trait-label">שם</label>
                          <input className="props-trait-input" type="text" value={fields[fk] || ""} onChange={(e) => setFields({ ...fields, [fk]: e.target.value })} placeholder="שם..." />
                        </div>
                      </div>
                    );
                  })}
                </>
              ),
              traits_table: (
                <>
                  <div id="ch6-traits-table" className="props-table-wrap" style={{ marginTop: 8, marginBottom: 16, overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, tableLayout: "fixed" }}>
                      <thead>
                        <tr>
                          {CH6_PEOPLE.map((pp, c) => (
                            <th key={c} style={{ padding: "8px 10px", background: pp.bg, border: "1px solid var(--sand)", textAlign: "center", width: "20%" }}>
                              <div style={{ fontWeight: 700, color: pp.color, fontSize: 13 }}>{pp.label}</div>
                            </th>
                          ))}
                        </tr>
                        <tr>
                          {[0, 1, 2, 3, 4].map((c) => (
                            <th key={c} style={{ padding: "5px 8px", background: "var(--bg2)", border: "1px solid var(--sand)", textAlign: "center", fontStyle: "italic", fontWeight: 400, color: "var(--muted)", fontSize: 12 }}>
                              {fields[`ch6_t1_name_c${c}`] || "—"}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {["trait1", "trait2", "trait3", "trait4", "trait5"].map((rowKey, rowIdx) => (
                          <tr key={rowKey}>
                            {[0, 1, 2, 3, 4].map((c) => {
                              const fk = `ch6_t1_${rowKey}_c${c}`;
                              return (
                                <td key={c} style={{ padding: 4, border: "1px solid var(--sand)" }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 10px", border: "1.5px solid var(--sand)", borderRadius: 8, background: "var(--bg2)" }}>
                                    <input type="text" value={fields[fk] || ""} onChange={(e) => setFields({ ...fields, [fk]: e.target.value })} placeholder={`תכונה ${rowIdx + 1}`} style={{ flex: 1, minWidth: 0, padding: "6px 0", border: "none", background: "transparent", fontSize: 13, fontFamily: "inherit", textAlign: "right", direction: "rtl", outline: "none" }} />
                                    <TraitColorDot fk={`${fk}_color`} fields={fields} setFields={setFields} size={20} />
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {CH6_PEOPLE.map((person, c) => (
                    <div key={c} className="props-person-card" style={{ background: person.bg }}>
                      <div className="props-person-head" style={{ color: person.color }}>
                        <span>{person.label}</span>
                        <span className="props-person-sub">{fields[`ch6_t1_name_c${c}`] || "—"}</span>
                      </div>
                      {["trait1", "trait2", "trait3", "trait4", "trait5"].map((key, i) => {
                        const fk = `ch6_t1_${key}_c${c}`;
                        return (
                          <div className="props-trait-row" key={key}>
                            <label className="props-trait-label">{`תכונה ${i + 1}`}</label>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, padding: "2px 10px", border: "1px solid var(--sand)", borderRadius: 6, background: "var(--bg2)" }}>
                              <input className="props-trait-input" type="text" value={fields[fk] || ""} onChange={(e) => setFields({ ...fields, [fk]: e.target.value })} placeholder={`תכונה ${i + 1}`} style={{ flex: 1, minWidth: 0, padding: "6px 0", border: "none", background: "transparent", outline: "none" }} />
                              <TraitColorDot fk={`${fk}_color`} fields={fields} setFields={setFields} size={22} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, margin: "10px 0 6px" }}>
                    <button type="button" onClick={() => document.getElementById("ch6-rulers-table")?.scrollIntoView({ behavior: "smooth", block: "start" })} style={{ padding: "8px 14px", background: "var(--bg2)", color: "var(--deep)", border: "1.5px solid var(--sand)", borderRadius: 10, fontFamily: "'Heebo', sans-serif", fontSize: 13, fontWeight: 500, cursor: "pointer" }}>
                      ↓ קפוץ לטבלת הסרגלים
                    </button>
                    <button type="button" onClick={save} style={{ padding: "8px 16px", background: "linear-gradient(135deg, var(--terra), var(--accent))", color: "white", border: "none", borderRadius: 10, fontFamily: "'Heebo', sans-serif", fontSize: 13, fontWeight: 600, cursor: "pointer", boxShadow: "0 2px 8px rgba(160,112,74,0.25)" }}>
                      💾 שמור התקדמות
                    </button>
                  </div>
                </>
              ),
              rulers_table: (() => {
                // Auto-generated rulers. One ruler per colour the reader
                // assigned in the properties table above; tick marks + count
                // are computed live from those colour tags. The reader only
                // names each ruler (positive / opposite). Replaces the old
                // manual 10-row table where the reader counted by hand.
                const tally = ch6TallyColors(fields);
                const { filled, colored } = ch6Stats(fields);
                const numRulers = tally.length;

                // Adaptive guidance: one message based on where the reader is.
                let guide: { tone: "warn" | "tip" | "ok"; text: string } | null = null;
                if (filled < 25) {
                  guide = { tone: "warn", text: "השלימי את כל 25 התכונות בטבלה למעלה כדי לקבל תמונה מלאה." };
                } else if (colored < filled) {
                  guide = { tone: "warn", text: "סמני צבע לכל תכונה - כל תכונה צריכה להשתייך לסרגל." };
                } else if (numRulers > 7) {
                  guide = { tone: "tip", text: `יש לך ${numRulers} סרגלים. לרוב לאדם יש 2-7 סרגלים מרכזיים - נסי לאחד תכונות דומות תחת צבע אחד, גם אם הן נראות מעט רחוקות זו מזו. שם בדיוק מתחילה ההבנה.` };
                } else if (numRulers >= 1) {
                  guide = { tone: "ok", text: `מצוין! זיקקת את עצמך ל-${numRulers} סרגלים. זו תמונת המראות שלך.` };
                }
                const toneStyles: Record<string, { bg: string; border: string; color: string }> = {
                  warn: { bg: "#FFF8E1", border: "#FFB300", color: "#E65100" },
                  tip: { bg: "#E8F4FB", border: "#0277BD", color: "#01579B" },
                  ok: { bg: "#F0F7F3", border: "#4CAF50", color: "#2E7D32" },
                };

                return (
                <>
                  <div id="ch6-rulers-table" style={{ marginTop: 16, marginBottom: 16, padding: 14, background: "var(--bg2)", borderRadius: 8, border: "1px solid var(--sand)" }}>
                    <div style={{ fontWeight: 600, color: "var(--deep)", marginBottom: 12, fontSize: 14 }}>📏 הסרגלים שלך - נבנים מהצבעים שסימנת</div>

                    {/* Adaptive status / guidance box */}
                    <div style={{ marginBottom: 14, fontSize: 13 }}>
                      <div style={{ color: "var(--muted)", marginBottom: guide ? 8 : 0 }}>
                        מילאת <strong style={{ color: "var(--deep)" }}>{filled}</strong> מתוך 25 תכונות · צבעת <strong style={{ color: "var(--deep)" }}>{colored}</strong>
                      </div>
                      {guide && (
                        <div style={{ background: toneStyles[guide.tone].bg, borderRight: `3px solid ${toneStyles[guide.tone].border}`, color: toneStyles[guide.tone].color, borderRadius: "0 8px 8px 0", padding: "10px 14px", lineHeight: 1.6 }}>
                          {guide.text}
                        </div>
                      )}
                    </div>

                    {/* Auto-generated rulers, one per used colour */}
                    {numRulers === 0 ? (
                      <div style={{ color: "var(--muted)", fontSize: 13, padding: "16px 4px", textAlign: "center" }}>
                        עדיין לא סימנת צבעים. חזרי לטבלה למעלה, לחצי על העיגול שליד כל תכונה ובחרי צבע - הסרגלים יופיעו כאן אוטומטית.
                      </div>
                    ) : (
                      tally.map(({ idx, count }) => {
                        const color = CH6_TRAIT_COLORS[idx];
                        // No flexWrap on the row: on narrow phones a high
                        // tick count must never push the opposite-name input
                        // onto its own line (it reads as a separate, nameless
                        // ruler). The tick strip wraps instead.
                        return (
                          <div key={idx} style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 6px", borderBottom: "1px solid var(--sand)" }}>
                            {/* positive name — right side in RTL */}
                            <input
                              type="text"
                              value={fields[`ch6_rcolor${idx}_pos`] || ""}
                              onChange={(e) => setFields({ ...fields, [`ch6_rcolor${idx}_pos`]: e.target.value })}
                              placeholder="תכונה חיובית"
                              style={{ flex: 1, minWidth: 90, padding: "8px 10px", border: "1.5px solid var(--sand)", borderRadius: 8, background: "var(--white)", fontSize: 13, fontFamily: "inherit", textAlign: "right", direction: "rtl" }}
                            />
                            {/* centre — colour swatch + tick marks + scribbled count */}
                            <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                              <div style={{ width: 16, height: 16, borderRadius: "50%", background: color, border: "1.5px solid rgba(0,0,0,0.18)", flexShrink: 0 }} title={CH6_COLOR_NAMES[idx]} />
                              <RulerTicks n={count} color={color} />
                              <ScribbledCount n={count} color={color} />
                            </div>
                            {/* opposite name — left side. direction:ltr +
                                textAlign:left makes the placeholder, cursor,
                                and text BLOCK start at the LEFT edge of the
                                input, mirroring the positive name on the right.
                                Hebrew characters inside still render with the
                                correct right-to-left bidi order — direction:ltr
                                only controls the base direction of the line,
                                not the per-character glyph order. */}
                            <input
                              type="text"
                              value={fields[`ch6_rcolor${idx}_neg`] || ""}
                              onChange={(e) => setFields({ ...fields, [`ch6_rcolor${idx}_neg`]: e.target.value })}
                              placeholder="תכונה הופכית"
                              style={{ flex: 1, minWidth: 90, padding: "8px 10px", border: "1.5px solid var(--sand)", borderRadius: 8, background: "var(--white)", fontSize: 13, fontFamily: "inherit", textAlign: "left", direction: "ltr" }}
                            />
                          </div>
                        );
                      })
                    )}
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, margin: "10px 0 6px" }}>
                    <button
                      type="button"
                      onClick={() => document.getElementById("ch6-traits-table")?.scrollIntoView({ behavior: "smooth", block: "start" })}
                      style={{ padding: "8px 14px", background: "var(--bg2)", color: "var(--deep)", border: "1.5px solid var(--sand)", borderRadius: 10, fontFamily: "'Heebo', sans-serif", fontSize: 13, fontWeight: 500, cursor: "pointer" }}
                    >
                      ↑ חזרה לטבלת התכונות
                    </button>
                    <button
                      type="button"
                      onClick={save}
                      style={{ padding: "8px 16px", background: "linear-gradient(135deg, var(--terra), var(--accent))", color: "white", border: "none", borderRadius: 10, fontFamily: "'Heebo', sans-serif", fontSize: 13, fontWeight: 600, cursor: "pointer", boxShadow: "0 2px 8px rgba(160,112,74,0.25)" }}
                    >
                      💾 שמור התקדמות
                    </button>
                  </div>
                  <Ch6WorkedExample />
                </>
                );
              })(),
            }}
            fallback={null}
          />

          <div style={{ margin: "32px 0 8px" }}>
            <ChapterNav bottom={true} />
          </div>
        </div>
      )}

      {chapter === 7 && !showPaywall && (
        <div className="ch-numbered ch-numbered-7">
          {/* ===== CHAPTER 7: book chapters 11+12+13 ===== */}
          {/* Chapter 7 prose lives in src/chapters/chapter-7.html and is
              synced to the book_content table via the sync-book-content
              workflow on every push to main. Nine widget anchors: six
              standalone textareas (ex1-ex6) interleaved with channel-
              description paragraphs, the 6×6 channels_table, the 2×3
              values_plan_table, and the final ex7 reflection textarea.
              The 104-values chooser grid stays as static HTML (it's a
              read-only chooser, not bound to state). Outer
              ch-numbered-7 wrapper stays here so chapter-7 CSS scoping
              (.values-wrap, .values-grid-pos, .values-cell, etc.)
              continues to work.
              Since 1.11.2026 eleven workshop frames are mounted at
              data-widget-anchor="ch7_fN" (src/data/ch7Frames.ts). */}
          <ChapterContent
            chapter={7}
            onSectionListen={sectionListenHandler}
            widgets={{
              ...Object.fromEntries(
                CH7_FRAMES.map((f) => [
                  f.id,
                  <FrameBox key={f.id} f={f} fields={fields} setFields={setFields} save={<InlineSave />} />,
                ]),
              ),
              ex1: (
                <div style={{ marginTop: 8, marginBottom: 8 }}>
                  <textarea
                    value={fields.ch7_ex1 || ""}
                    onChange={(e) => setFields({ ...fields, ch7_ex1: e.target.value })}
                    placeholder="כתבי כאן..."
                    className="ta"
                    rows={3}
                  />
                </div>
              ),
              ex2: (
                <div style={{ marginTop: 8, marginBottom: 8 }}>
                  <textarea
                    value={fields.ch7_ex2 || ""}
                    onChange={(e) => setFields({ ...fields, ch7_ex2: e.target.value })}
                    placeholder="כתבי כאן..."
                    className="ta"
                    rows={3}
                  />
                </div>
              ),
              ex3: (
                <div style={{ marginTop: 8, marginBottom: 8 }}>
                  <textarea
                    value={fields.ch7_ex3 || ""}
                    onChange={(e) => setFields({ ...fields, ch7_ex3: e.target.value })}
                    placeholder="כתבי כאן..."
                    className="ta"
                    rows={3}
                  />
                </div>
              ),
              ex4: (
                <div style={{ marginTop: 8, marginBottom: 8 }}>
                  <textarea
                    value={fields.ch7_ex4 || ""}
                    onChange={(e) => setFields({ ...fields, ch7_ex4: e.target.value })}
                    placeholder="כתבי כאן..."
                    className="ta"
                    rows={3}
                  />
                </div>
              ),
              ex5: (
                <div style={{ marginTop: 8, marginBottom: 8 }}>
                  <textarea
                    value={fields.ch7_ex5 || ""}
                    onChange={(e) => setFields({ ...fields, ch7_ex5: e.target.value })}
                    placeholder="כתבי כאן..."
                    className="ta"
                    rows={3}
                  />
                </div>
              ),
              ex6: (
                <>
                  <div style={{ marginTop: 8, marginBottom: 8 }}>
                    <textarea
                      value={fields.ch7_ex6 || ""}
                      onChange={(e) => setFields({ ...fields, ch7_ex6: e.target.value })}
                      placeholder="כתבי כאן..."
                      className="ta"
                      rows={3}
                    />
                  </div>
                  <InlineSave />
                </>
              ),
              channels_table: (
                <>
                  {/* Card per component, on ALL screens, in a responsive grid:
                      2+ per row on desktop, 1 on mobile (auto-fit + minmax, no
                      media query). Every column is a labelled field so the
                      guidance always sits beside its input — the old 6-column
                      table was too cramped in the narrow reading column. Same
                      ch7_channels_r{i}_c{c} keys → saved answers preserved. */}
                  <div id="ch7-channels-table" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 14, margin: "8px 0 4px", alignItems: "start" }}>
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                      <div key={i} style={{ background: "var(--white)", border: "1px solid var(--sand)", borderRadius: 12, padding: 14 }}>
                        <div style={{ fontWeight: 700, fontSize: 15, color: "var(--terra)", borderBottom: "1px solid var(--sand)", paddingBottom: 8, marginBottom: 12 }}>מרכיב {i}</div>
                        {CH7_CHANNEL_COLS.map((label, ci) => {
                          const c = ci + 1;
                          const fk = `ch7_channels_r${i}_c${c}`;
                          return (
                            <div key={c} style={{ marginBottom: ci === CH7_CHANNEL_COLS.length - 1 ? 0 : 12 }}>
                              <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--deep)", marginBottom: 5, lineHeight: 1.35 }}>{label}</div>
                              <textarea
                                value={fields[fk] || ""}
                                onChange={(e) => setFields({ ...fields, [fk]: e.target.value })}
                                className="ta"
                                rows={2}
                                style={{ width: "100%", padding: "8px 10px", border: "1px solid var(--sand)", borderRadius: 6, background: "var(--bg2)", fontFamily: "inherit", fontSize: 13, direction: "rtl", resize: "vertical" }}
                                placeholder="כתבי כאן..."
                              />
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, margin: "10px 0 6px" }}>
                    <button
                      type="button"
                      onClick={() => document.getElementById("ch7-channels-list")?.scrollIntoView({ behavior: "smooth", block: "start" })}
                      style={{ padding: "8px 14px", background: "var(--bg2)", color: "var(--deep)", border: "1.5px solid var(--sand)", borderRadius: 10, fontFamily: "'Heebo', sans-serif", fontSize: 13, fontWeight: 500, cursor: "pointer" }}
                    >
                      ↑ לרשימת המרכיבים
                    </button>
                    <InlineSave />
                  </div>
                </>
              ),
              values_plan_table: (
                <>
                  {/* Was a 4-column 13px table that scrolled sideways on mobile.
                      Now one card per value-category (green/amber/red), each with
                      the current-state + plans fields — matches the channels card
                      grid. The two long row-instructions move to the intro box
                      once (verbatim) instead of repeating in every cell. Same
                      ch7_table_r{1,2}_c{1,2,3} keys → saved answers preserved. */}
                  <div style={{ background: "var(--bg2)", border: "1px solid var(--sand)", borderRadius: 10, padding: "12px 14px", margin: "8px 0 14px", fontSize: 13, lineHeight: 1.6, color: "var(--deep)" }}>
                    <div style={{ marginBottom: 6 }}><strong>מצב קיים:</strong> ציני ערכים ואמונות בהתאם לכותרת בהתייחס לתפיסת עולמך הנוכחית.</div>
                    <div><strong>תוכניות לשימור ושינוי:</strong> בחרי ערך אחד או שניים לכל היותר והתווי לעצמך תוכניות לשינוי, אחרי שהצלחת עם ערך אחד ולאורך זמן תמשיכי לערכים נוספים.</div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 14, margin: "0 0 4px", alignItems: "start" }}>
                    {CH7_VALUE_COLS.map((col, ci) => {
                      const c = ci + 1;
                      return (
                        <div key={c} style={{ background: "var(--white)", border: "1px solid var(--sand)", borderRadius: 12, overflow: "hidden" }}>
                          <div style={{ background: col.bg, borderBottom: `1px solid ${col.border}`, padding: "10px 12px", fontSize: 13, fontWeight: 600, color: "var(--deep)", lineHeight: 1.4, minHeight: 62 }}>{col.label}</div>
                          <div style={{ padding: 12 }}>
                            <div style={{ marginBottom: 12 }}>
                              <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--terra)", marginBottom: 5 }}>מצב קיים</div>
                              <textarea value={fields[`ch7_table_r1_c${c}`] || ""} onChange={(e) => setFields({ ...fields, [`ch7_table_r1_c${c}`]: e.target.value })} className="ta" rows={3} style={{ width: "100%", padding: "8px 10px", border: "1px solid var(--sand)", borderRadius: 6, background: "var(--bg2)", fontFamily: "inherit", fontSize: 13, direction: "rtl", resize: "vertical" }} placeholder="ערכים..." />
                            </div>
                            <div>
                              <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--terra)", marginBottom: 5 }}>תוכניות לשימור ושינוי</div>
                              <textarea value={fields[`ch7_table_r2_c${c}`] || ""} onChange={(e) => setFields({ ...fields, [`ch7_table_r2_c${c}`]: e.target.value })} className="ta" rows={3} style={{ width: "100%", padding: "8px 10px", border: "1px solid var(--sand)", borderRadius: 6, background: "var(--bg2)", fontFamily: "inherit", fontSize: 13, direction: "rtl", resize: "vertical" }} placeholder="תוכניות..." />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <InlineSave />
                </>
              ),
              ex7: (
                <>
                  <div style={{ marginTop: 8, marginBottom: 8 }}>
                    <textarea
                      value={fields.ch7_ex7 || ""}
                      onChange={(e) => setFields({ ...fields, ch7_ex7: e.target.value })}
                      placeholder="כתבי כאן..."
                      className="ta"
                      rows={3}
                    />
                  </div>
                  <InlineSave />
                </>
              ),
            }}
            fallback={null}
          />

          <div style={{ margin: "32px 0 8px" }}>
            <ChapterNav bottom={true} />
          </div>
        </div>
      )}

      {chapter === 8 && !showPaywall && (
        <div className="ch-numbered ch-numbered-8">
          {/* ===== CHAPTER 8 = book ch 14, 15, 16, 17, 18 ===== */}
          {/* Chapter 8 prose lives in src/chapters/chapter-8.html and is
              synced to the book_content table via the sync-book-content
              workflow on every push to main. Five widget anchors:
              strengths/weaknesses intelligences tables (with jump-between),
              self/other rejection tables (with jump-between), and the
              want_need table. All textareas persist via the shared
              fields[] state. Outer ch-numbered-8 wrapper stays here so
              chapter-8 CSS scoping continues to work.
              Since 1.11.2026 eleven workshop frames are mounted at
              data-widget-anchor="ch8_fN" (src/data/ch8Frames.ts). */}
          <ChapterContent
            chapter={8}
            onSectionListen={sectionListenHandler}
            widgets={{
              ...Object.fromEntries(
                CH8_FRAMES.map((f) => [
                  f.id,
                  <FrameBox key={f.id} f={f} fields={fields} setFields={setFields} save={<InlineSave />} />,
                ]),
              ),
              strengths_intelligences_table: renderCh8Grid({
                id: "ch8-strengths-intelligences",
                headers: ["האינטליגנציה", "איך היא באה לידי ביטוי", "תרומה ייחודית לחיי"],
                prefix: "ch8_t9",
                rowKeys: [2, 3, 4, 5],
                colKeys: [1, 2, 3],
                rowLabel: "בחירה",
                example: ["א. רגשית תוך אישית", "מנתחת את מצבי הרגשי מספר פעמים ביום, בוחנת ומשנה פרשנויות לאירועים למיטבי", "נוטה להתרגז מעט, לעיתים מציגה כעס כחלק מאסטרטגיה להוביל לשינוי"],
                footer: <SaveAndJump jumpTo="ch8-weaknesses-intelligences" jumpLabel="↓ קפוץ לטבלת החולשות" />,
              }),
              weaknesses_intelligences_table: renderCh8Grid({
                id: "ch8-weaknesses-intelligences",
                headers: ["האינטליגנציה", "איך היא באה לידי ביטוי", "פגיעה במסוגלות המסוימת"],
                prefix: "ch8_t10",
                rowKeys: [2, 3, 4, 5],
                colKeys: [1, 2, 3],
                rowLabel: "בחירה",
                example: ["א. רגשית בין-אישית", "לא מצליחה לזהות אצל האחר את מצבו הרגשי (אדום או ירוק)", "מופתעת, לא מגיבה טוב, לא משיגה מטרות שהצבתי לעצמי"],
                footer: <SaveAndJump jumpTo="ch8-strengths-intelligences" jumpLabel="↑ חזרה לטבלת החוזקות" />,
              }),
              self_rejection_table: renderCh8Grid({
                id: "ch8-self-rejection",
                headers: ["תאר אירוע של אי קבלה עצמית", "מה היה הטריגר", "האם נפגעת, אולי פגעתי באחר (הטריגר היה הרגל)", "האם הימנעות בעתיד אפשרית להערכתך?"],
                prefix: "ch8_t11",
                rowKeys: [1, 2, 3, 4],
                colKeys: [0, 1, 2, 3],
                rowLabel: "אירוע",
                footer: <SaveAndJump jumpTo="ch8-other-rejection" jumpLabel="↓ קפוץ לטבלת אי קבלה של אחר" />,
              }),
              other_rejection_table: renderCh8Grid({
                id: "ch8-other-rejection",
                headers: ["אירוע אי קבלה של אחר", "מה היה הטריגר", "האם נפגעת ואו פגעת באחר (הטריגר היה הרגל)", "האם הימנעות בעתיד להערכתך?"],
                prefix: "ch8_t12",
                rowKeys: [1, 2, 3, 4],
                colKeys: [0, 1, 2, 3],
                rowLabel: "אירוע",
                footer: <SaveAndJump jumpTo="ch8-self-rejection" jumpLabel="↑ חזרה לטבלת אי קבלה עצמית" />,
              }),
              want_need_table: renderCh8Grid({
                headers: ["מעשים שצריך", "למה זה צריך?", "מה אומר לעצמי על מנת שיהיה לי קל לקבל את זה שאני רוצה?"],
                prefix: "ch8_t13",
                rowKeys: [1, 2, 3, 4],
                colKeys: [0, 1, 2],
                rowLabel: "פריט",
                footer: <InlineSave />,
              }),
            }}
            fallback={null}
          />

          <div style={{ margin: "32px 0 8px" }}>
            <ChapterNav bottom={true} />
          </div>
        </div>
      )}

      {chapter === 9 && !showPaywall && (
        <div className="ch-numbered ch-numbered-9">
          {/* ===== CHAPTER 9 = book ch 19, 20, 21, 22 ===== */}
          {/* Chapter 9 prose lives in src/chapters/chapter-9.html and is
              synced to the book_content table via the sync-book-content
              workflow on every push to main. No widgets — pure prose
              with one image (/engri_smaili.png). Outer ch-numbered-9
              wrapper stays here so chapter-9 CSS scoping continues to
              work. Since 1.11.2026 eleven workshop frames are mounted at
              data-widget-anchor="ch9_fN" (src/data/ch9Frames.ts). */}
          <ChapterContent
            chapter={9}
            onSectionListen={sectionListenHandler}
            widgets={Object.fromEntries(
              CH9_FRAMES.map((f) => [
                f.id,
                <FrameBox key={f.id} f={f} fields={fields} setFields={setFields} save={<InlineSave />} />,
              ]),
            )}
            fallback={null}
          />

          <div style={{ margin: "32px 0 8px" }}>
            <ChapterNav bottom={true} />
          </div>
        </div>
      )}

      {chapter === 10 && !showPaywall && (
        <div className="ch-numbered ch-numbered-10">
          {/* ===== CHAPTER 10 = book ch 23, 24, 25, 26, 27 ===== */}
          {/* Chapter 10 prose lives in src/chapters/chapter-10.html and
              is synced to the book_content table via the
              sync-book-content workflow on every push to main. No
              widgets — pure prose with one image (/horse_ch10.svg) and
              one external YouTube link. Outer ch-numbered-10 wrapper
              stays here so chapter-10 CSS scoping continues to work.
              Since 1.11.2026 eleven workshop frames are mounted at
              data-widget-anchor="ch10_fN" (src/data/ch10Frames.ts). */}
          <ChapterContent
            chapter={10}
            onSectionListen={sectionListenHandler}
            widgets={Object.fromEntries(
              CH10_FRAMES.map((f) => [
                f.id,
                <FrameBox key={f.id} f={f} fields={fields} setFields={setFields} save={<InlineSave />} />,
              ]),
            )}
            fallback={null}
          />

          <div style={{ margin: "32px 0 8px" }}>
            <ChapterNav bottom={true} />
          </div>
        </div>
      )}

      {chapter === 11 && !showPaywall && (
        <div className="ch-numbered ch-numbered-11">
          {/* ===== CHAPTER 11 = book ch 28-35 ===== */}
          {/* Chapter 11 prose lives in src/chapters/chapter-11.html and
              is synced to the book_content table via the
              sync-book-content workflow on every push to main. No
              widgets — pure prose with 8 book sections, 6 images
              (some filenames contain spaces — browser auto-encodes
              when fetching), and a handful of external links. Outer
              ch-numbered-11 wrapper stays here so chapter-11 CSS
              scoping continues to work.
              Since 1.11.2026 ten workshop frames are mounted at
              data-widget-anchor="ch11_fN" (src/data/ch11Frames.ts). */}
          <ChapterContent
            chapter={11}
            onSectionListen={sectionListenHandler}
            widgets={Object.fromEntries(
              CH11_FRAMES.map((f) => [
                f.id,
                <FrameBox key={f.id} f={f} fields={fields} setFields={setFields} save={<InlineSave />} />,
              ]),
            )}
            fallback={null}
          />

          <div style={{ margin: "32px 0 8px" }}>
            <ChapterNav bottom={true} />
          </div>
        </div>
      )}

      {chapter === 12 && !showPaywall && (
        <div className="ch-numbered ch-numbered-12">
          {/* ===== CHAPTER 12 = book ch 36-40 (FINAL chapter) ===== */}
          {/* Chapter 12 prose lives in src/chapters/chapter-12.html and
              is synced to the book_content table via the
              sync-book-content workflow on every push to main. No
              widgets — pure prose with one image (/journey_ch12.svg)
              and a closing "סוף הספר" gradient banner card. Outer
              ch-numbered-12 wrapper stays here so chapter-12 CSS
              scoping continues to work.
              Since 1.11.2026 nine workshop frames are mounted at
              data-widget-anchor="ch12_fN" (src/data/ch12Frames.ts). */}
          <ChapterContent
            chapter={12}
            onSectionListen={sectionListenHandler}
            widgets={Object.fromEntries(
              CH12_FRAMES.map((f) => [
                f.id,
                <FrameBox key={f.id} f={f} fields={fields} setFields={setFields} save={<InlineSave />} />,
              ]),
            )}
            fallback={null}
          />

          <div style={{ margin: "32px 0 8px" }}>
            <ChapterNav bottom={true} />
          </div>
        </div>
      )}

      <div className="acts">
        <button className="bpr" onClick={save}>
          שמור התקדמות
        </button>
        <button className="bsc" onClick={downloadSummary}>
          הורד סיכום
        </button>
      </div>

      <div
        style={{
          textAlign: "center",
          marginTop: 24,
          paddingTop: 16,
          borderTop: "1px solid #E8DCC8",
          fontSize: 12,
          color: "#8B7355",
        }}
      >
        <a
          href="/contact"
          style={{ color: "#A0704A", textDecoration: "none", fontWeight: 500 }}
        >
          צור קשר
        </a>
        {" · "}
        <a href="/legal/terms.html" style={{ color: "#A0704A", textDecoration: "none" }}>
          תקנון
        </a>
        {" · "}
        <a href="/legal/refund.html" style={{ color: "#A0704A", textDecoration: "none" }}>
          ביטולים והחזרים
        </a>
        {" · "}
        <a href="/legal/accessibility.html" style={{ color: "#A0704A", textDecoration: "none" }}>
          נגישות
        </a>
        {" · "}
        <a href="/legal/privacy.html" style={{ color: "#A0704A", textDecoration: "none" }}>
          פרטיות
        </a>
        {/* Business-identity line — the "legitimate Israeli business
            fingerprint" from the 11.8 trust research: skeptical 55+
            visitors scroll to the footer to check who is behind a site. */}
        <div style={{ marginTop: 10, lineHeight: 1.7 }}>
          «שאלת האריה - סדנה בספר» מאת שלמה אלפנבוים
          <br />
          יחד יעוץ חזון דינמיקה בע״מ · ח.פ 513185116 · וואטסאפ:{" "}
          <a href="https://wa.me/972544562084" target="_blank" rel="noreferrer" style={{ color: "#A0704A", textDecoration: "none" }}>
            054-4562084
          </a>{" "}
          · <span style={{ direction: "ltr", unicodeBidi: "embed" }}>shlomo@eiforall.com</span>
        </div>
      </div>

      <div className={"svb " + (saveBanner ? "vis" : "")}>
        ההתקדמות שלך נשמרה!
      </div>

      {justPurchased && (
        <div
          role="status"
          style={{
            position: "fixed",
            insetInlineStart: 16,
            insetInlineEnd: 16,
            top: 16,
            zIndex: 9000,
            background: "linear-gradient(135deg, #2E7D32, #4CAF50)",
            color: "white",
            padding: "14px 20px",
            borderRadius: 14,
            boxShadow: "0 8px 28px rgba(46,125,50,0.4)",
            display: "flex",
            alignItems: "center",
            gap: 12,
            fontFamily: "Heebo, sans-serif",
            fontSize: 15,
            fontWeight: 600,
            direction: "rtl",
            maxWidth: 540,
            margin: "0 auto",
          }}
        >
          <span style={{ fontSize: 24 }}>🎉</span>
          <div style={{ flex: 1 }}>
            ברוכים הבאים — הגישה שלכם פתוחה. תיהנו מהסדנא.
          </div>
          <button
            onClick={() => setJustPurchased(false)}
            aria-label="סגירה"
            style={{
              background: "rgba(255,255,255,0.2)",
              border: "none",
              color: "white",
              width: 28,
              height: 28,
              borderRadius: 6,
              cursor: "pointer",
              fontSize: 16,
            }}
          >
            ✕
          </button>
        </div>
      )}
      </>
      )}
    </div>
  );
}
